import { transformError as sdkTransformError } from '@wix/sdk-runtime/transform-error';
import { queryBuilder } from '@wix/sdk-runtime/query-builder';
import {
  renameKeysFromSDKRequestToRESTRequest,
  renameKeysFromRESTResponseToSDKResponse,
} from '@wix/sdk-runtime/rename-all-nested-keys';
import {
  HttpClient,
  HttpResponse,
  Search as SearchSdkType,
  NonNullablePaths,
  QuerySpec,
  Query as QuerySdkType,
  SearchSpec,
} from '@wix/sdk-types';
import * as ambassadorWixBookingsServicesV2Service from './bookings-services-v2-service-services.http.js';
// @ts-ignore
import { transformSDKAddressToRESTAddress } from '@wix/sdk-runtime/transformations/address';
import { transformRESTAddressToSDKAddress } from '@wix/sdk-runtime/transformations/address';
import { transformSDKImageToRESTImage } from '@wix/sdk-runtime/transformations/image';
import { transformRESTImageToSDKImage } from '@wix/sdk-runtime/transformations/image';
import { transformSDKPageURLV2ToRESTPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformRESTPageURLV2ToSDKPageURLV2 } from '@wix/sdk-runtime/transformations/page-url-v2';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { createQueryUtils } from '@wix/sdk-runtime/query-builder-utils';
import { createSearchUtils } from '@wix/sdk-runtime/search-builder-utils';

/** The `service` object represents an offering that a business provides to its customers. */
export interface Service {
  /**
   * Service ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * ID of the app associated with the service. You can't update `appId`.
   * Services are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.
   * Default: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)
   * For services from Wix apps, the following values apply:
   * - Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * - Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`
   * - Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`
   * [Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).
   * <!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->
   * @format GUID
   * @immutable
   */
  appId?: string | null;
  /**
   * ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.
   * This read-only field is automatically populated during service creation by checking these sources in order:
   * 1. The caller's App ID from the request identity context.
   * 2. The service's `appId` field.
   * 3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.
   * <!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->
   * @format GUID
   * @readonly
   */
  createdByAppId?: string | null;
  /**
   * Service type.
   * Learn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).
   */
  type?: ServiceTypeWithLiterals;
  /** Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object). */
  sortOrder?: number | null;
  /**
   * Service name.
   * @maxLength 400
   * @minLength 1
   */
  name?: string | null;
  /**
   * Service description. For example, `High-class hair styling, cuts, straightening and color`.
   * @maxLength 7000
   */
  description?: string | null;
  /**
   * Short service description, such as `Hair styling`.
   * @maxLength 6000
   */
  tagLine?: string | null;
  /**
   * Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.
   * @min 1
   * @max 1000
   */
  defaultCapacity?: number | null;
  /** Media associated with the service. */
  media?: Media;
  /** Whether the service is hidden from Wix Bookings pages and widgets. */
  hidden?: boolean | null;
  /**
   * [Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)
   * the service is associated with. Services aren't automatically assigned to a category.
   * Without an associated category, the service isn't visible on the live site.
   */
  category?: V2Category;
  /** Form the customer filled out when booking the service. */
  form?: Form;
  /**
   * Payment options for booking the service.
   * Learn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).
   */
  payment?: Payment;
  /** Online booking settings. */
  onlineBooking?: OnlineBooking;
  /** Conferencing options for the service. */
  conferencing?: Conferencing;
  /**
   * The locations this service is offered at. Read more about [service locations](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-locations).
   * @immutable
   * @maxSize 500
   */
  locations?: V2Location[];
  /**
   * [Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)
   * determining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.
   */
  bookingPolicy?: BookingPolicy;
  /**
   * The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),
   * which can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).
   */
  schedule?: Schedule;
  /**
   * IDs of the [resources](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction) associated with the [staff members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/introduction) providing the service.
   * Note that these are the resource IDs, not the staff member IDs.
   *
   * For appointment-based services, set this field when creating or updating the service.
   * For classes and courses, this field is read-only and is automatically derived from staff assigned to the service's recurring scheduled sessions.
   * Staff assigned only to single, non-recurring events are not included.
   * Once all of a staff member's upcoming recurring sessions have ended, their ID is removed from this field.
   * To retrieve the full list of staff for classes or courses, query the service's calendar events instead.
   * Learn more about [retrieving staff for classes and courses](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/sample-flows#retrieve-staff-members-for-a-class-or-course).
   * @maxSize 220
   * @format GUID
   */
  staffMemberIds?: string[];
  /**
   * Information about which resources must be available so customers can book the service.
   * For example, a meeting room or equipment.
   * @maxSize 3
   */
  serviceResources?: ServiceResource[];
  /**
   * A slug is the last part of the URL address that serves as a unique identifier of the service.
   * The list of supported slugs includes past service names for backwards compatibility, and a custom slug if one was set by the business owner.
   * @readonly
   * @maxSize 100
   */
  supportedSlugs?: Slug[];
  /**
   * Active slug for the service.
   * Learn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).
   * @readonly
   */
  mainSlug?: Slug;
  /**
   * URLs to various service-related pages, such as the calendar page and the booking page.
   * @readonly
   */
  urls?: URLs;
  /** Extensions enabling users to save custom data related to the service. */
  extendedFields?: ExtendedFields;
  /** Custom SEO data for the service. */
  seoData?: SeoSchema;
  /**
   * Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Revision number, which increments by 1 each time the service is updated. To
   * prevent conflicting changes, the existing revision must be used when updating
   * a service.
   * @readonly
   */
  revision?: string | null;
  /**
   * Information about the [add-on groups](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-add-on-groups) associated with the service.
   * @maxSize 3
   */
  addOnGroups?: AddOnGroup[];
  /**
   * Details about all [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) customers can choose when booking the service.
   * @readonly
   * @maxSize 50
   */
  addOnDetails?: AddOnDetails[];
  /** Taxable address used to calculate tax */
  taxableAddress?: TaxableAddress;
}

export enum ServiceType {
  /** Appointment-based service. */
  APPOINTMENT = 'APPOINTMENT',
  /** Class service. */
  CLASS = 'CLASS',
  /** Course service. */
  COURSE = 'COURSE',
}

/** @enumType */
export type ServiceTypeWithLiterals =
  | ServiceType
  | 'APPOINTMENT'
  | 'CLASS'
  | 'COURSE';

export interface Media {
  /**
   * Media items associated with the service.
   * @maxSize 100
   */
  items?: MediaItem[];
  /** Primary media associated with the service. */
  mainMedia?: MediaItem;
  /** Cover media associated with the service. */
  coverMedia?: MediaItem;
}

export interface MediaItem extends MediaItemItemOneOf {
  /** Details of the image associated with the service, such as URL and size. */
  image?: string;
}

/** @oneof */
export interface MediaItemItemOneOf {
  /** Details of the image associated with the service, such as URL and size. */
  image?: string;
}

export interface V2Category {
  /**
   * Category ID.
   * @format GUID
   */
  _id?: string;
  /**
   * Category name.
   * @maxLength 500
   * @readonly
   */
  name?: string | null;
  /**
   * Order of a category within a category list.
   * @readonly
   */
  sortOrder?: number | null;
}

export interface Form {
  /**
   * ID of the form associated with the service.
   * The form information that you submit when booking includes contact details, participants, and other form fields set up for the service.
   * You can manage the service booking form fields using the Bookings Forms API.
   * @format GUID
   */
  _id?: string;
}

export interface FormSettings {
  /** Whether the service booking form should be hidden from the site. */
  hidden?: boolean | null;
}

export interface Payment extends PaymentRateOneOf {
  /**
   * The details for the fixed price of the service.
   *
   * Required when: `rateType` is `FIXED`
   */
  fixed?: FixedPayment;
  /**
   * The details for the custom price of the service.
   *
   * Required when: `rateType` is `CUSTOM`
   */
  custom?: CustomPayment;
  /**
   * The details for the varied pricing of the service.
   * Read more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).
   *
   * Required when: `rateType` is `VARIED`
   */
  varied?: VariedPayment;
  /**
   * The details for the subscription pricing of the service.
   *
   * Required when: `rateType` is `SUBSCRIPTION`
   * @internal
   */
  subscription?: SubscriptionPayment;
  /** The rate the customer is expected to pay for the service. */
  rateType?: RateTypeWithLiterals;
  /** The payment options a customer can use to pay for the service. */
  options?: PaymentOptions;
  /**
   * IDs of pricing plans that can be used as payment for the service.
   * Read more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).
   * @readonly
   * @maxSize 75
   * @format GUID
   */
  pricingPlanIds?: string[];
  /**
   * How customers can pay for add-ons when paying for the related booking with a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction).
   * If customers pay for the booking using any method other than a pricing plan, the value of this field is ignored.
   */
  addOnOption?: AddOnPaymentOptionsWithLiterals;
  /**
   * Estimated discount information for the service based on active [eCommerce discounts](https://dev.wix.com/docs/rest/business-solutions/e-commerce/extensions/discounts/introduction).
   * The final discount is determined during eCommerce checkout and may differ from the estimate,
   * for example when discounts depend on cart totals.
   *
   * A discount is considered active when its start time has passed and its end time hasn't.
   * If multiple active discounts apply, the most recently created one is returned.
   *
   * Returned only when `DISCOUNT_INFO_DETAILS` is requested.
   * @internal
   * @readonly
   */
  discountInfo?: DiscountInfo;
}

/** @oneof */
export interface PaymentRateOneOf {
  /**
   * The details for the fixed price of the service.
   *
   * Required when: `rateType` is `FIXED`
   */
  fixed?: FixedPayment;
  /**
   * The details for the custom price of the service.
   *
   * Required when: `rateType` is `CUSTOM`
   */
  custom?: CustomPayment;
  /**
   * The details for the varied pricing of the service.
   * Read more about [varied price options](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online#offering-varied-price-options).
   *
   * Required when: `rateType` is `VARIED`
   */
  varied?: VariedPayment;
  /**
   * The details for the subscription pricing of the service.
   *
   * Required when: `rateType` is `SUBSCRIPTION`
   * @internal
   */
  subscription?: SubscriptionPayment;
}

export enum RateType {
  /** The service has a fixed price. */
  FIXED = 'FIXED',
  /** The service has a custom price, expressed as a price description. */
  CUSTOM = 'CUSTOM',
  /** This service is offered with a set of different prices based on different terms. */
  VARIED = 'VARIED',
  /** This service is offered free of charge. */
  NO_FEE = 'NO_FEE',
}

/** @enumType */
export type RateTypeWithLiterals =
  | RateType
  | 'FIXED'
  | 'CUSTOM'
  | 'VARIED'
  | 'NO_FEE';

export interface FixedPayment {
  /**
   * The fixed price required to book the service.
   *
   * Required when: `rateType` is `FIXED`
   */
  price?: Money;
  /**
   * The deposit price required to book the service.
   *
   * Required when: `rateType` is `FIXED` and `paymentOptions.deposit` is `true`
   */
  deposit?: Money;
  /**
   * Whether customers can choose to pay the full service price upfront instead of only the deposit.
   *
   * Used only when a `deposit` amount is set.
   *
   * Default: `false`.
   */
  fullUpfrontPaymentAllowed?: boolean | null;
}

/**
 * Money.
 * Default format to use. Sufficiently compliant with majority of standards: w3c, ISO 4217, ISO 20022, ISO 8583:2003.
 */
export interface Money {
  /**
   * Monetary amount. Decimal string with a period as a decimal separator. For example `25.05`.
   * @format DECIMAL_VALUE
   * @decimalValue options { gt:0, maxScale:2 }
   */
  value?: string;
  /**
   * Currency code in [ISO 4217 format](https://en.wikipedia.org/wiki/ISO_4217). For example, `USD`.
   * @format CURRENCY
   * @readonly
   */
  currency?: string;
  /**
   * Monetary amount. Decimal string in local format. For example, `1 000,30`.
   * @maxLength 50
   */
  formattedValue?: string | null;
}

export interface CustomPayment {
  /**
   * A custom description explaining to the customer how to pay for the service.
   * @maxLength 50
   */
  description?: string | null;
}

export interface VariedPayment {
  /** The default price for the service without any variants. It will also be used as the default price for any new variant. */
  defaultPrice?: Money;
  /**
   * The deposit price required to book the service.
   *
   * Required when: `rateType` is `VARIED` and `paymentOptions.deposit` is `true`
   */
  deposit?: Money;
  /**
   * The minimal price a customer may pay for this service, based on its variants.
   * @readonly
   */
  minPrice?: Money;
  /**
   * The maximum price a customer may pay for this service, based on its variants.
   * @readonly
   */
  maxPrice?: Money;
  /**
   * Whether customers can choose to pay the full service price upfront instead of only the deposit.
   *
   * Used only when a `deposit` amount is set.
   *
   * Default: `false`.
   */
  fullUpfrontPaymentAllowed?: boolean | null;
}

export interface SubscriptionPayment {
  /** Amount charged each billing cycle. */
  amountPerBillingCycle?: Money;
  /** Billing frequency used for recurring subscription charges. */
  frequency?: FrequencyTypeWithLiterals;
  /**
   * Total number of recurring payments in the subscription.
   * Value must be greater than `1`.
   * Required when: `rateType` is `SUBSCRIPTION`
   * @min 2
   */
  totalPayments?: number | null;
  /** When the first recurring payment is charged. */
  firstChargeDate?: FirstChargeDateTypeWithLiterals;
  /**
   * Date and time when recurring payments start in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * When `firstChargeDate` is `SCHEDULED`, the first charge occurs on this date.
   * When `firstChargeDate` is `CHECKOUT`, the first charge occurs at checkout and recurring billing begins on this date.
   * Required when: `rateType` is `SUBSCRIPTION`. Sets the future billing cycle dates.
   */
  recurringStartDate?: Date | null;
  /** Optional one-time enrollment fee. */
  enrollmentFeeAmount?: Money;
  /** Optional full payment alternative to recurring subscription billing. */
  fullUpfrontPayment?: FullUpfrontPayment;
}

export enum FrequencyType {
  /** Payment is charged once every month. */
  MONTHLY = 'MONTHLY',
}

/** @enumType */
export type FrequencyTypeWithLiterals = FrequencyType | 'MONTHLY';

export enum FirstChargeDateType {
  /** Charge the first billing cycle at checkout. */
  CHECKOUT = 'CHECKOUT',
  /** Charge the first billing cycle on the `recurringStartDate`. */
  SCHEDULED = 'SCHEDULED',
}

/** @enumType */
export type FirstChargeDateTypeWithLiterals =
  | FirstChargeDateType
  | 'CHECKOUT'
  | 'SCHEDULED';

export interface FullUpfrontPayment {
  /** Whether customers can choose a one-time full payment instead of recurring subscription payments. */
  supported?: boolean | null;
  /**
   * Percentage discount applied when a customer chooses the full payment option.
   * Min: `0.01` percent
   * Max: `100` percent
   * @format DECIMAL_VALUE
   * @decimalValue options { gte:0.01, lte:100, maxScale:2 }
   */
  discountPercent?: string | null;
}

export interface PaymentOptions {
  /**
   * Customers can pay for the service online.
   * When `true`:
   * + `rateType` must be `FIXED` or `VARIED`.
   * + `fixed.price` or `varied.defaultPrice` must be specified respectively.
   * Read more about [getting paid online](https://support.wix.com/en/article/wix-bookings-about-getting-paid-online).
   */
  online?: boolean | null;
  /** Customers can pay for the service in person. */
  inPerson?: boolean | null;
  /**
   * This service requires a deposit to be made online in order to book it.
   * When `true`:
   * + `rateType` must be `VARIED` or `FIXED`.
   * + A `deposit` must be specified.
   */
  deposit?: boolean | null;
  /**
   * Whether customers can pay for the service using a pricing plan.
   * Read more about [service payment options](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).
   */
  pricingPlan?: boolean | null;
}

export enum AddOnPaymentOptions {
  /** Customers are required to pay online for add-ons when the booking is paid for with a pricing plan. */
  ONLINE = 'ONLINE',
  /** Customers are required to pay in person for add-ons when the booking is paid for with a pricing plan. */
  IN_PERSON = 'IN_PERSON',
}

/** @enumType */
export type AddOnPaymentOptionsWithLiterals =
  | AddOnPaymentOptions
  | 'ONLINE'
  | 'IN_PERSON';

/** Estimated discount information for a service based on active eCommerce discounts. */
export interface DiscountInfo {
  /**
   * Name of the discount. For example, `Summer Sale - 20% Off`.
   * @minLength 1
   * @maxLength 50
   */
  discountName?: string;
  /**
   * Estimated price after applying the discount. The final price is determined at checkout and may differ when additional discounts are applied or rules are re-evaluated with complete booking information.
   *
   * Not returned when the discount depends on booking or cart context, for example a discount that applies to a service only when booked together with another service, or when the discount requires information only available at checkout.
   */
  priceAfterDiscount?: Money;
}

export interface OnlineBooking {
  /**
   * Whether the service can be booked online.
   * When set to `true`, customers can book the service online. Configure the payment options via the `service.payment` property.
   * When set to `false`, customers cannot book the service online, and the service can only be paid for in person.
   */
  enabled?: boolean | null;
  /** Booking the service requires approval by the Wix user. */
  requireManualApproval?: boolean | null;
  /** Multiple customers can request to book the same time slot. This is relevant when `requireManualApproval` is `true`. */
  allowMultipleRequests?: boolean | null;
}

export interface Conferencing {
  /** Whether a conference link is generated for the service's sessions. */
  enabled?: boolean | null;
}

export interface V2Location extends V2LocationOptionsOneOf {
  /** Information about business locations. */
  business?: BusinessLocationOptions;
  /** Information about custom locations. */
  custom?: CustomLocationOptions;
  /**
   * Location ID.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /**
   * Location type.
   *
   * Default: `CUSTOM`
   */
  type?: LocationTypeEnumLocationTypeWithLiterals;
  /**
   * Location address. Empty for `{"type": "CUSTOMER"}`.
   * @readonly
   */
  calculatedAddress?: CommonAddress;
}

/** @oneof */
export interface V2LocationOptionsOneOf {
  /** Information about business locations. */
  business?: BusinessLocationOptions;
  /** Information about custom locations. */
  custom?: CustomLocationOptions;
}

export enum LocationTypeEnumLocationType {
  /** Location set by the business that is not a standard business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction). */
  CUSTOM = 'CUSTOM',
  /** Business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction). */
  BUSINESS = 'BUSINESS',
  /**
   * The customer specifies any address when booking. Available only for
   * appointment-based services.
   */
  CUSTOMER = 'CUSTOMER',
}

/** @enumType */
export type LocationTypeEnumLocationTypeWithLiterals =
  | LocationTypeEnumLocationType
  | 'CUSTOM'
  | 'BUSINESS'
  | 'CUSTOMER';

export interface CommonAddress extends CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: CommonStreetAddress;
  /** @maxLength 255 */
  addressLine1?: string | null;
  /**
   * 2-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format.
   * @format COUNTRY
   */
  country?: string | null;
  /**
   * Code for a subdivision (such as state, prefecture, or province) in [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2) format.
   * @maxLength 255
   */
  subdivision?: string | null;
  /**
   * City name.
   * @maxLength 255
   */
  city?: string | null;
  /**
   * Postal or zip code.
   * @maxLength 255
   */
  postalCode?: string | null;
  /**
   * Full address of the location.
   * @maxLength 512
   */
  formatted?: string | null;
}

/** @oneof */
export interface CommonAddressStreetOneOf {
  /** Street name and number. */
  streetAddress?: CommonStreetAddress;
  /** @maxLength 255 */
  addressLine?: string | null;
}

/** Street address. Includes street name, number, and apartment number in separate fields. */
export interface CommonStreetAddress {
  /**
   * Street number.
   * @maxLength 255
   */
  number?: string;
  /**
   * Street name.
   * @maxLength 255
   */
  name?: string;
  /**
   * Apartment number.
   * @maxLength 255
   */
  apt?: string;
}

export interface CommonAddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface BusinessLocationOptions {
  /**
   * ID of the business [location](https://dev.wix.com/docs/api-reference/business-management/locations/introduction).
   * When setting a business location, specify only the location ID. Other location details are overwritten.
   * @format GUID
   */
  _id?: string;
  /**
   * Business location name.
   * @readonly
   * @maxLength 150
   */
  name?: string;
  /**
   * Whether this is the default location. There can only be a single default location per site.
   * @readonly
   */
  default?: boolean | null;
  /**
   * Business location address.
   * @readonly
   */
  address?: CommonAddress;
  /**
   * Business location email.
   * @format EMAIL
   * @readonly
   */
  email?: string | null;
  /**
   * Business location phone.
   * @format PHONE
   * @readonly
   */
  phone?: string | null;
}

export interface CustomLocationOptions {
  /**
   * ID of the custom location.
   * @format GUID
   * @readonly
   */
  _id?: string;
  /** Address of the custom location. */
  address?: CommonAddress;
}

/**
 * `BookingPolicy` is the main entity of `BookingPolicyService` and specifies a set of rules for booking a service
 * by visitors and members.
 *
 * Each `BookingPolicy` consists of a number of sub-policies. When the Bookings App is provisioned to a meta site then a
 * default `BookingPolicy` will be created with defaults for each of these sub-policies. This also applies when a request
 * is received to create a new `BookingPolicy` and one or more of these sub-policies are not provided.
 *
 * Sub-policies are defined in separate objects as specified below.
 *
 * - The `LimitEarlyBookingPolicy` object defines the policy for limiting early bookings.
 * - The `LimitLateBookingPolicy` object defines the policy for limiting late bookings.
 * - The `BookAfterStartPolicy` object defines the policy for booking after the start of the schedule.
 * - The `CancellationPolicy` object defines the policy for canceling a booked entity.
 * - The `ReschedulePolicy` object defines the policy for rescheduling booked entity.
 * - The `WaitlistPolicy` object defines the policy for a waitlist.
 * - The `ParticipantsPolicy` object defines the policy regarding the participants per booking.
 * - The `ResourcesPolicy` object defines the policy regarding the resources per booking.
 * - The `CancellationFeePolicy` object defines the policy regarding cancellation fees.
 * - The `SaveCreditCardPolicy` object defines the policy for saving credit card details.
 *
 * By default each sub-policy is disabled. A more detailed specification of the default settings of each sub-policy
 * can be found in the description of the corresponding object.
 *
 * Partial updates are supported on the main entity level, however in order to update a sub-policy the client needs to provide the whole sub-policy object.
 */
export interface BookingPolicy {
  /**
   * The ID to the policy for the booking.
   * @format GUID
   */
  _id?: string;
  /**
   * Date and time the policy was created.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the policy was updated.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Name of the policy.
   * @maxLength 400
   * @readonly
   */
  name?: string | null;
  /**
   * Custom description for the policy. This policy is displayed to the participant.
   * @readonly
   */
  customPolicyDescription?: PolicyDescription;
  /**
   * Whether the policy is the default for the meta site.
   * @readonly
   */
  default?: boolean | null;
  /**
   * Policy for limiting early bookings.
   * @readonly
   */
  limitEarlyBookingPolicy?: LimitEarlyBookingPolicy;
  /**
   * Policy for limiting late bookings.
   * @readonly
   */
  limitLateBookingPolicy?: LimitLateBookingPolicy;
  /**
   * Policy on booking an entity after the start of the schedule.
   * @readonly
   */
  bookAfterStartPolicy?: BookAfterStartPolicy;
  /**
   * Policy for canceling a booked entity.
   * @readonly
   */
  cancellationPolicy?: CancellationPolicy;
  /**
   * Policy for rescheduling a booked entity.
   * @readonly
   */
  reschedulePolicy?: ReschedulePolicy;
  /**
   * Waitlist policy for the service.
   * @readonly
   */
  waitlistPolicy?: WaitlistPolicy;
  /**
   * Policy regarding the participants per booking.
   * @readonly
   */
  participantsPolicy?: ParticipantsPolicy;
  /**
   * Policy for allocating resources.
   * @readonly
   */
  resourcesPolicy?: ResourcesPolicy;
  /**
   * Rules for cancellation fees.
   * @readonly
   */
  cancellationFeePolicy?: CancellationFeePolicy;
  /**
   * Rule for saving credit card.
   * @readonly
   */
  saveCreditCardPolicy?: SaveCreditCardPolicy;
}

/** A description of the policy to display to participants. */
export interface PolicyDescription {
  /**
   * Whether the description should be displayed. If `true`, the description is displayed.
   *
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * The description to display.
   *
   * Default: Empty
   * Max length: 2500 characters
   * @maxLength 2500
   */
  description?: string;
}

/** The policy for limiting early bookings. */
export interface LimitEarlyBookingPolicy {
  /**
   * Whether there is a limit on how early a customer
   * can book. When `false`, there is no limit on the earliest
   * booking time and customers can book in advance, as early as they like.
   *
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * Maximum number of minutes before the start of the session that a booking can be made. This value must be greater
   * than `latest_booking_in_minutes` in the `LimitLateBookingPolicy` policy.
   *
   * Default: 10080 minutes (7 days)
   * Min: 1 minute
   * @min 1
   */
  earliestBookingInMinutes?: number;
}

/**
 * The policy for limiting late bookings.
 *
 * This policy and the `BookAfterStartPolicy` policy cannot be enabled at the same time. So if this policy
 * is enabled, `BookAfterStartPolicy` must be disabled.
 */
export interface LimitLateBookingPolicy {
  /**
   * Whether there is a limit on how late a customer
   * can book. When `false`, there is no limit on the latest
   * booking time and customers can book up to the last minute.
   *
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * Minimum number of minutes before the start of the session that a booking can be made.
   * For a schedule, this is relative to the start time of the next booked session, excluding past-booked sessions.
   * This value must be less than `earliest_booking_in_minutes` in the `LimitEarlyBookingPolicy` policy.
   *
   * Default: 1440 minutes (1 day)
   * Min: 1 minute
   * @min 1
   */
  latestBookingInMinutes?: number;
}

/**
 * The policy for whether a session can be booked after the start of the schedule.
 * This policy and `LimitLateBookingPolicy` cannot be enabled at the same time. So if this policy
 * is enabled, the `LimitLateBookingPolicy` policy must be disabled.
 */
export interface BookAfterStartPolicy {
  /**
   * Whether booking is allowed after the start of the schedule. When `true`,
   * customers can book after the start of the schedule.
   *
   * Default: `false`
   */
  enabled?: boolean;
}

/** The policy for canceling a booked session. */
export interface CancellationPolicy {
  /**
   * Whether canceling a booking is allowed. When `true`, customers
   * can cancel the booking.
   *
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * Whether there is a limit on the latest cancellation time. When `true`,
   * a time limit is enforced.
   *
   * Default: `false`
   */
  limitLatestCancellation?: boolean;
  /**
   * Minimum number of minutes before the start of the booked session that the booking can be canceled.
   *
   * Default: 1440 minutes (1 day)
   * Min: 1 minute
   * @min 1
   */
  latestCancellationInMinutes?: number;
  /**
   * Whether this cancellation policy allows anonymous cancellations.
   *
   * **Important**: This flag only applies when `enabled` is `true`. If the cancellation
   * policy itself is disabled (`enabled` = `false`), anonymous users cannot cancel
   * regardless of this flag's value.
   *
   * When not set (null), defaults to disabled.
   *
   * Default: `null` (treated as disabled)
   */
  allowAnonymous?: boolean | null;
}

/** The policy for rescheduling a booked session. */
export interface ReschedulePolicy {
  /**
   * Whether rescheduling a booking is allowed. When `true`, customers
   * can reschedule the booking.
   *
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * Whether there is a limit on the latest reschedule time. When `true`,
   * a time limit is enforced.
   *
   * Default: `false`
   */
  limitLatestReschedule?: boolean;
  /**
   * Minimum number of minutes before the start of the booked session that the booking can be rescheduled.
   *
   * Default: 1440 minutes (1 day)
   * Min: 1 minute
   * @min 1
   */
  latestRescheduleInMinutes?: number;
  /**
   * Whether this reschedule policy allows anonymous rescheduling.
   *
   * **Important**: This flag only applies when `enabled` is `true`. If the reschedule
   * policy itself is disabled (`enabled` = `false`), anonymous users cannot reschedule
   * regardless of this flag's value.
   *
   * When not set (null), defaults to disabled.
   *
   * Default: `null` (treated as disabled)
   */
  allowAnonymous?: boolean | null;
}

/** The policy for the waitlist. */
export interface WaitlistPolicy {
  /**
   * Whether the session has a waitlist. If `true`, there is a waitlist.
   *
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * Number of spots available in the waitlist.
   *
   * Default: 10 spots
   * Min: 1 spot
   * @min 1
   */
  capacity?: number;
  /**
   * Amount of time a participant is given to book, once notified that a spot is available.
   *
   * Default: 10 minutes
   * Min: 1 spot
   * @min 1
   */
  reservationTimeInMinutes?: number;
}

/** The policy for the maximum number of participants per booking. */
export interface ParticipantsPolicy {
  /**
   * Maximum number of participants allowed.
   *
   * Default: 1 participant
   * Min: 1 participant
   * @min 1
   */
  maxParticipantsPerBooking?: number;
}

/** The policy regarding the allocation of resources (e.g. staff members). */
export interface ResourcesPolicy {
  /**
   * `true` if this policy is enabled, `false` otherwise.
   * When `false` then the client must always select a resource when booking an appointment.
   */
  enabled?: boolean;
  /**
   * `true`, if it is allowed to automatically assign a resource when booking an appointment,
   * `false`, if the client must always select a resource.
   *
   * Default: `false`
   */
  autoAssignAllowed?: boolean;
}

export interface CancellationFeePolicy {
  /**
   * Whether canceling a booking will result in a cancellation fee
   *
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * Cancellation windows describing the time of cancellation and the fee to charge.
   * @maxSize 2
   */
  cancellationWindows?: CancellationWindow[];
  /**
   * Whether the cancellation fee should not be automatically collected when customer cancels the booking.
   *
   * Default: `true`
   */
  autoCollectFeeEnabled?: boolean | null;
}

export interface CancellationWindow extends CancellationWindowFeeOneOf {
  /** Amount to be charged as a cancellation fee. */
  amount?: Money;
  /**
   * Percentage of the original price to be charged as a cancellation fee.
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentage?: string;
  /**
   * The fee will be applied if the booked session starts within this start time in minutes.
   * @min 1
   */
  startInMinutes?: number | null;
}

/** @oneof */
export interface CancellationWindowFeeOneOf {
  /** Amount to be charged as a cancellation fee. */
  amount?: Money;
  /**
   * Percentage of the original price to be charged as a cancellation fee.
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentage?: string;
}

export interface SaveCreditCardPolicy {
  /** Default: `false` */
  enabled?: boolean;
}

/**
 * Policy for determining how staff members are sorted and selected during the booking process.
 * This affects which staff member is chosen when multiple staff members are available for a service.
 */
export interface StaffSortingPolicy extends StaffSortingPolicyOptionsOneOf {
  rankingOptions?: RankingOptions;
  customOptions?: CustomOptions;
  /**
   * Method used for sorting and selecting staff members.
   *
   * Default: `RANDOM`
   */
  sortingMethodType?: SortingMethodTypeWithLiterals;
}

/** @oneof */
export interface StaffSortingPolicyOptionsOneOf {
  rankingOptions?: RankingOptions;
  customOptions?: CustomOptions;
}

/** Order for ranking-based staff selection. */
export enum RankingOrder {
  /** Staff members with lower priority values are selected first. */
  LOWEST_TO_HIGHEST = 'LOWEST_TO_HIGHEST',
  /** Staff members with higher priority values are selected first. */
  HIGHEST_TO_LOWEST = 'HIGHEST_TO_LOWEST',
}

/** @enumType */
export type RankingOrderWithLiterals =
  | RankingOrder
  | 'LOWEST_TO_HIGHEST'
  | 'HIGHEST_TO_LOWEST';

/** Method used to sort and select staff members. */
export enum SortingMethodType {
  /** Staff members are selected randomly from available options. */
  RANDOM = 'RANDOM',
  /** Staff members are selected based on their priority ranking. */
  RANKING = 'RANKING',
  /**
   * Staff members are selected using a custom implementation provided by SortStaffSPI.
   * This allows third-party apps to implement custom staff sorting logic.
   */
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type SortingMethodTypeWithLiterals =
  | SortingMethodType
  | 'RANDOM'
  | 'RANKING'
  | 'CUSTOM';

/**
 * Configuration options for ranking-based staff selection.
 * Used when `sorting_method_type` is set to `RANKING`.
 */
export interface RankingOptions {
  /**
   * Order in which staff members are sorted by their priority ranking.
   *
   * Default: `LOWEST_TO_HIGHEST`
   */
  order?: RankingOrderWithLiterals;
}

/**
 * Configuration options for custom staff selection methods.
 * Used when `sorting_method_type` is set to `CUSTOM`.
 */
export interface CustomOptions {
  /**
   * ID of the custom sorting method implemented in SortStaffSPI.
   * This identifies which custom sorting algorithm to use.
   * @format GUID
   */
  implementationId?: string;
  /**
   * ID of the app that provides the custom sorting method.
   * @format GUID
   */
  appId?: string;
}

/** Policy for integrating with Intake form. Stores which form to use and when to present it. */
export interface IntakeFormPolicy {
  /**
   * Whether intake form integration is enabled for the service.
   * Default: `false`
   */
  enabled?: boolean;
  /**
   * ID of the intake form to integrate with the service.
   * @format GUID
   */
  formId?: string | null;
  /** When to present the intake form to the customer. */
  timing?: TimingWithLiterals;
  completionRequirement?: CompletionRequirementWithLiterals;
}

export enum Timing {
  /** Send form after booking. */
  AFTER_BOOKING = 'AFTER_BOOKING',
  /** Show form during booking flow. */
  BEFORE_BOOKING = 'BEFORE_BOOKING',
}

/** @enumType */
export type TimingWithLiterals = Timing | 'AFTER_BOOKING' | 'BEFORE_BOOKING';

/** Requirement for completing the intake form. */
export enum CompletionRequirement {
  /** Form completion is optional and can be skipped entirely. */
  OPTIONAL = 'OPTIONAL',
  /**
   * Form must be completed before the booking can be finalized.
   * can used only if timing is BEFORE_BOOKING.
   */
  REQUIRED_BEFORE_BOOKING = 'REQUIRED_BEFORE_BOOKING',
}

/** @enumType */
export type CompletionRequirementWithLiterals =
  | CompletionRequirement
  | 'OPTIONAL'
  | 'REQUIRED_BEFORE_BOOKING';

export interface Schedule {
  /**
   * ID of the [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)
   * to which the service's events belong.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Start time of the first session in the schedule. For courses only.
   * @readonly
   */
  firstSessionStart?: Date | null;
  /**
   * End time of the last session in the schedule. For courses only.
   * @readonly
   */
  lastSessionEnd?: Date | null;
  /** Limitations affecting the service availability. */
  availabilityConstraints?: AvailabilityConstraints;
}

export interface AvailabilityConstraints {
  /**
   * Calculated list of all supported session durations for the service. For
   * appointment-based services without varied pricing based on session length, it
   * matches the single value in the `sessionDurations` array. For appointment-based
   * services with varied pricing based on session length, it includes session
   * durations for all [variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction),
   * while ignoring `sessionDurations`.
   * For courses and classes, it includes durations for all future
   * recurring sessions but excludes durations for one-off or past recurring sessions.
   * @readonly
   * @maxSize 50
   */
  durations?: V2Duration[];
  /**
   * List of supported session durations in minutes.
   *
   * - For appointment-based services, specify `sessionDurations` when creating a service.
   * - For appointment-based services with varied pricing by session length, you must still specify `sessionDurations`, but the values are ignored. Actual durations are taken from the [service variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction).
   * - For classes or courses, don't specify `sessionDurations` when creating a service.
   *
   * Min: `1` minute
   * Max: `44639` minutes (30 days, 23 hours, and 59 minutes)
   * @min 1
   * @max 44639
   * @maxSize 50
   */
  sessionDurations?: number[];
  /**
   * The number of minutes between the end of a session and the start of the next.
   *
   *
   * Min: `0` minutes
   * Max: `720` minutes
   * @max 720
   */
  timeBetweenSessions?: number;
}

export interface V2Duration {
  /**
   * The duration of the service in minutes.
   *
   * Min: `1` minute
   * Max: `44639` minutes (30 days, 23 hours, and 59 minutes)
   * @min 1
   * @max 44639
   */
  minutes?: number;
}

export interface StaffMember {
  /**
   * ID of the [resource](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction) associated with the staff member providing the service.
   * Despite the field name, this is the resource ID, not the staff member ID.
   * This value matches the staff member's `resourceId` from the [Staff Members API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/introduction)
   * and corresponds to the IDs in the service's `staffMemberIds` field.
   * @format GUID
   * @readonly
   */
  staffMemberId?: string;
  /**
   * Name of the staff member
   * @maxLength 40
   * @readonly
   */
  name?: string | null;
  /**
   * Main media associated with the service.
   * @readonly
   */
  mainMedia?: StaffMediaItem;
}

export interface StaffMediaItem extends StaffMediaItemItemOneOf {
  /** Details of the image associated with the staff, such as URL and size. */
  image?: string;
}

/** @oneof */
export interface StaffMediaItemItemOneOf {
  /** Details of the image associated with the staff, such as URL and size. */
  image?: string;
}

export interface StaffMemberDetails {
  /**
   * Staff members providing the service. For appointments only.
   * @maxSize 220
   */
  staffMembers?: StaffMember[];
}

export interface ResourceGroup {
  /**
   * An optional resource group ID. If specified, it references a resource group in the resource groups API.
   * TODO - referenced_entity annotation
   * @format GUID
   */
  resourceGroupId?: string | null;
  /**
   * Resource IDs. Each ID references a resource in the resources API and may be a subset of resources within a resource group.
   * TODO - referenced_entity annotation
   */
  resourceIds?: ResourceIds;
  /**
   * Specifies how many resources in the group / resource IDs are required to book the service.
   * Defaults to 1.
   * @min 1
   */
  requiredResourcesNumber?: number | null;
  /**
   * If set to `true`, the customer can select the specific resources while booking the service.
   * If set to `false`, the resources required to book the service will be auto-selected at the time of the booking.
   * Defaults to false.
   * @readonly
   */
  selectableResource?: boolean | null;
}

export interface ResourceIds {
  /**
   * Values of the resource IDs.
   * @maxSize 100
   * @format GUID
   */
  values?: string[];
}

export interface ServiceResource extends ServiceResourceSelectionOneOf {
  /** Details about the required [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction). */
  resourceType?: ResourceType;
}

/** @oneof */
export interface ServiceResourceSelectionOneOf {}

export interface ResourceType {
  /**
   * The type of the resource.
   * @format GUID
   */
  _id?: string | null;
  /**
   * The name of the resource type.
   * @readonly
   * @maxLength 40
   * @minLength 1
   */
  name?: string | null;
}

export interface Slug {
  /**
   * The unique part of service's URL that identifies the service's information page. For example, `service-1` in `https:/example.com/services/service-1`.
   * @maxLength 500
   */
  name?: string;
  /**
   * Whether the slug was generated or customized. If `true`, the slug was customized manually by the business owner. Otherwise, the slug was automatically generated from the service name.
   * @readonly
   */
  custom?: boolean | null;
  /**
   * Date and time the slug was created. This is a system field.
   * @readonly
   */
  _createdDate?: Date | null;
}

export interface URLs {
  /**
   * The URL for the service page.
   * @readonly
   */
  servicePage?: string;
  /**
   * The URL for the booking entry point. It can be either to the calendar or to the service page.
   * @readonly
   */
  bookingPage?: string;
  /**
   * The URL for the calendar. Can be empty if no calendar exists.
   * @readonly
   */
  calendarPage?: string;
}

export interface ExtendedFields {
  /**
   * Extended field data. Each key corresponds to the namespace of the app that created the extended fields.
   * The value of each key is structured according to the schema defined when the extended fields were configured.
   *
   * You can only access fields for which you have the appropriate permissions.
   *
   * Learn more about [extended fields](https://dev.wix.com/docs/rest/articles/getting-started/extended-fields).
   */
  namespaces?: Record<string, Record<string, any>>;
}

/**
 * The SEO schema object contains data about different types of meta tags. It makes sure that the information about your page is presented properly to search engines.
 * The search engines use this information for ranking purposes, or to display snippets in the search results.
 * This data will override other sources of tags (for example patterns) and will be included in the <head> section of the HTML document, while not being displayed on the page itself.
 */
export interface SeoSchema {
  /** SEO tag information. */
  tags?: Tag[];
  /** SEO general settings. */
  settings?: Settings;
}

export interface Keyword {
  /** Keyword value. */
  term?: string;
  /** Whether the keyword is the main focus keyword. */
  isMain?: boolean;
  /**
   * The source that added the keyword terms to the SEO settings.
   * @maxLength 1000
   */
  origin?: string | null;
}

export interface Tag {
  /**
   * SEO tag type.
   *
   *
   * Supported values: `title`, `meta`, `script`, `link`.
   */
  type?: string;
  /**
   * A `{"key": "value"}` pair object where each SEO tag property (`"name"`, `"content"`, `"rel"`, `"href"`) contains a value.
   * For example: `{"name": "description", "content": "the description itself"}`.
   */
  props?: Record<string, any> | null;
  /** SEO tag metadata. For example, `{"height": 300, "width": 240}`. */
  meta?: Record<string, any> | null;
  /** SEO tag inner content. For example, `<title> inner content </title>`. */
  children?: string;
  /** Whether the tag is a [custom tag](https://support.wix.com/en/article/adding-additional-meta-tags-to-your-sites-pages). */
  custom?: boolean;
  /** Whether the tag is disabled. If the tag is disabled, people can't find your page when searching for this phrase in search engines. */
  disabled?: boolean;
}

export interface Settings {
  /**
   * Whether the [automatical redirect visits](https://support.wix.com/en/article/customizing-your-pages-seo-settings-in-the-seo-panel) from the old URL to the new one is enabled.
   *
   *
   * Default: `false` (automatical redirect is enabled).
   */
  preventAutoRedirect?: boolean;
  /**
   * User-selected keyword terms for a specific page.
   * @maxSize 5
   */
  keywords?: Keyword[];
}

export interface AddOnGroup {
  /**
   * ID of the add-on group.
   * Wix Bookings automatically populates this field when creating or updating an add-on group.
   * @readonly
   * @format GUID
   */
  _id?: string | null;
  /**
   * Name of the add-on group.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Maximum number of different [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) from the group customers can add when booking the service.
   * When empty, there's no upper limit.
   */
  maxNumberOfAddOns?: number | null;
  /**
   * List of IDs of all [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) included in the group.
   * @format GUID
   * @maxSize 7
   */
  addOnIds?: string[] | null;
  /**
   * Description or instructional prompt of the add-on group that's displayed to customers when booking the service.
   * @maxLength 200
   */
  prompt?: string | null;
}

export interface AddOnDetails {
  /**
   * ID of the add-on.
   * @format GUID
   */
  addOnId?: string | null;
  /**
   * Duration in minutes for add-ons that extend service time.
   * Empty for [quantity-based add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction#terminology).
   */
  durationInMinutes?: number | null;
}

/** `TaxableAddress` defines the taxable address used for the service. */
export interface TaxableAddress {
  /** Taxable address type. */
  taxableAddressType?: TaxableAddressTypeWithLiterals;
}

export enum TaxableAddressType {
  BUSINESS = 'BUSINESS',
  BILLING = 'BILLING',
}

/** @enumType */
export type TaxableAddressTypeWithLiterals =
  | TaxableAddressType
  | 'BUSINESS'
  | 'BILLING';

export interface V2PhoneCall {
  /** Whether the service is delivered via phone call. */
  enabled?: boolean | null;
}

export interface SetCustomSlugEvent {
  /** The main slug for the service after the update */
  mainSlug?: Slug;
}

export interface ServicesUrlsChanged {}

export interface DummyRequest {}

export interface DummyResponse {
  /** Service. */
  service?: Service;
  /** Booking. */
  booking?: Booking;
}

/** An entity representing a scheduled appointment, class session, or course. */
export interface Booking extends BookingParticipantsInfoOneOf {
  /**
   * Total number of participants.
   * When creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).
   * @min 1
   */
  totalParticipants?: number;
  /**
   * Information about the booked service choices and participant count for each choice.
   * When creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).
   *
   * For example, use this for a spa package booking that includes different service levels:
   * - 2 participants chose "Standard Package".
   * - 1 participant chose "VIP Package".
   */
  participantsChoices?: ParticipantChoices;
  /**
   * Booking ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * An object describing the bookable entity - either a specific time slot or a recurring schedule.
   *
   * The structure depends on the type of service being booked:
   *
   * __For appointment services__: Use `slot` to book a specific time slot with a
   * service provider. Appointments are typically one-time sessions at a specific date and time.
   *
   * __For class services__: Use `slot` to book a specific class session. Classes
   * are individual sessions that can have multiple participants.
   *
   * __For course services__: Use `schedule` to book an entire course consisting of
   * multiple sessions over time. Courses are recurring, multi-session offerings.
   *
   * Choose the appropriate field based on your service type and booking requirements.
   */
  bookedEntity?: BookedEntity;
  /**
   * Contact details of the site visitor or [member](https://dev.wix.com/docs/api-reference/crm/members-contacts/members/members/introduction)
   * making the booking.
   */
  contactDetails?: ContactDetails;
  /**
   * Additional custom fields submitted with the booking form.
   * Deprecated due to Bookings integrating with Wix Forms.
   * You can call Wix Forms' [Get Submission](https://dev.wix.com/docs/api-reference/crm/forms/form-submissions/get-submission) specifying `formSubmissionId` as the `id` to get the submitted booking form data.
   * Read more about the [Bookings Forms integration](https://dev.wix.com/docs/api-reference/business-solutions/bookings/wix-forms-integration).
   * @deprecated Additional custom fields submitted with the booking form.
   * Deprecated due to Bookings integrating with Wix Forms.
   * You can call Wix Forms' [Get Submission](https://dev.wix.com/docs/api-reference/crm/forms/form-submissions/get-submission) specifying `formSubmissionId` as the `id` to get the submitted booking form data.
   * Read more about the [Bookings Forms integration](https://dev.wix.com/docs/api-reference/business-solutions/bookings/wix-forms-integration).
   * @replacedBy form_submission_id
   * @targetRemovalDate 2026-03-31
   */
  additionalFields?: CustomFormField[];
  /**
   * Booking status. A booking is automatically confirmed if the service allows it
   * and an eCommerce order is created. It is automatically declined if there is a
   * double booking and the customer hasn't paid or is eligible for an automatic
   * refund. Currently, only payments with pricing plans are automatically refundable.
   */
  status?: BookingStatusWithLiterals;
  /**
   * The payment status of the booking. This field automatically syncs with the
   * `paymentStatus` of the corresponding [eCommerce order](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/orders/introduction)
   * when customers use Wix eCommerce checkout.
   *
   * ### Integration patterns
   *
   * __When using Wix eCommerce checkout__: Wix Bookings automatically syncs the payment status based on the eCommerce order's payment status.
   * Do not manually update this field.
   *
   * __When using custom payment flows__: You can manually update the payment status with [Confirm Booking or Decline Booking](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings/bookings-writer-v2/confirm-or-decline-booking) to reflect the customer's payment state.
   *
   * __For membership/pricing plan payments__: Wix Bookings automatically manages the payment status when customers pay with an active [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/introduction) subscription.
   *
   * All payment statuses are supported for every booking `status`.
   */
  paymentStatus?: PaymentStatusWithLiterals;
  /**
   * Payment option selected by the customer. If the customer hasn't completed their checkout, they may still change the payment method. Must be one of the payment options offered by the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction), unless `skipSelectedPaymentOptionValidation` is `true`.
   *
   * When the customer pays with a [Wix eCommerce checkout](https://dev.wix.com/docs/api-reference/business-solutions/e-commerce/checkout/introduction), this field is required.
   * Otherwise, the Create Booking call fails.
   * For custom checkouts, you don't have to specify this field.
   */
  selectedPaymentOption?: SelectedPaymentOptionWithLiterals;
  /**
   * Date and time the booking was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /** External user ID that you can provide. */
  externalUserId?: string | null;
  /**
   * Revision number to be used when updating, rescheduling, or cancelling the booking.
   * Increments by 1 each time the booking is updated, rescheduled, or canceled. To prevent conflicting changes, the current revision must be specified when updating the booking.
   */
  revision?: string | null;
  /**
   * ID of the creator of the booking.
   * If `appId` and another ID are present, the other ID takes precedence.
   * @readonly
   */
  createdBy?: CommonIdentificationData;
  /**
   * The start date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * For a slot, this is the start date of the slot. For a schedule, this is the start date of the first session.
   * @readonly
   */
  startDate?: Date | null;
  /**
   * The end date of the booking in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * For a slot, this is the end date of the slot. For a schedule, this is the end date of the last session.
   * @readonly
   */
  endDate?: Date | null;
  /**
   * Date and time the booking was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Custom field data for this object.
   * Extended fields must be configured in the app dashboard before they can be accessed with API calls.
   */
  extendedFields?: ExtendedFields;
  /**
   * Whether this booking overlaps with another confirmed booking. Returned
   * only if set to `true`.
   * @readonly
   */
  doubleBooked?: boolean | null;
  /**
   * ID of the [form submission](https://dev.wix.com/docs/rest/crm/forms/form-submissions/introduction)
   * associated with this booking.
   * @format GUID
   */
  formSubmissionId?: string | null;
  /**
   * ID of the [form](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object)
   * associated with this booking. The value depends on how the
   * booking was created:
   * - For bookings created with Create Booking or Bulk Create Booking, `formId`
   * is identical to ID of the booking form that's associated with the relevant
   * service.
   * - For bookings created via Create Multi Service Booking, `formId` is set to
   * `00000000-0000-0000-0000-000000000000` (the default booking form ID).
   * @format GUID
   * @readonly
   */
  formId?: string | null;
  /**
   * List of [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) associated with the booking.
   * @maxSize 21
   */
  bookedAddOns?: BookedAddOn[];
  /**
   * ID of the app that's associated with the booking.
   * Inherited from the `appId` of the service associated with the booking.
   * Bookings are displayed in Wix Bookings only if they are associated with the Wix Bookings app ID or have no associated app ID.
   * For bookings from Wix apps, the following values apply:
   * - Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * - Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`
   * - Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`
   * [Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).
   * <!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->
   * @format GUID
   * @readonly
   */
  appId?: string | null;
  /**
   * ID of the app that created the booking. This field is used for analytics, auditing, and tracking creation sources.
   * This read-only field is automatically populated during booking creation by checking these sources in order:
   * 1. The caller's App ID from the request identity context (external app or server identity).
   * 2. The booking's `appId` field (inherited from the service's `appId`).
   * 3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.
   * <!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->
   * @format GUID
   * @readonly
   */
  createdByAppId?: string | null;
  /**
   * Whether the customer chooses to pay only the deposit amount upfront.
   *
   * - `true`: The customer pays only the deposit amount upfront.
   * - `false`: The customer pays the full price upfront.
   *
   * Used only when `selectedPaymentOption` is `ONLINE` and the [service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction) has a deposit amount set.
   *
   * When the service supports deposit payments and `fullUpfrontPaymentAllowed` is `false`, this field must be `true`.
   * When the service supports deposit payments and `fullUpfrontPaymentAllowed` is `true`, this field can be `true` or `false`.
   */
  depositSelected?: boolean | null;
}

/** @oneof */
export interface BookingParticipantsInfoOneOf {
  /**
   * Total number of participants.
   * When creating a booking, use this field only if the relevant service has fixed pricing and doesn't offer [variants and options](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).
   * @min 1
   */
  totalParticipants?: number;
  /**
   * Information about the booked service choices and participant count for each choice.
   * When creating a booking, use this field only if the booking includes multiple [service variants](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).
   *
   * For example, use this for a spa package booking that includes different service levels:
   * - 2 participants chose "Standard Package".
   * - 1 participant chose "VIP Package".
   */
  participantsChoices?: ParticipantChoices;
}

/**
 * Type of multi-service booking.
 * Defines how individual bookings within a multi-service package are scheduled relative to each other.
 */
export enum MultiServiceBookingType {
  /**
   * Bookings must be scheduled back-to-back.
   * Each booking starts when the previous one ends, with no gaps between them.
   */
  SEQUENTIAL_BOOKINGS = 'SEQUENTIAL_BOOKINGS',
  /**
   * Bookings can be scheduled separately.
   * Each booking can be at different times, not necessarily consecutive.
   * Not currently supported.
   */
  SEPARATE_BOOKINGS = 'SEPARATE_BOOKINGS',
  /**
   * Bookings occur at the same time.
   * All bookings in the package are scheduled for the same time slot.
   * Not currently supported.
   */
  PARALLEL_BOOKINGS = 'PARALLEL_BOOKINGS',
}

/** @enumType */
export type MultiServiceBookingTypeWithLiterals =
  | MultiServiceBookingType
  | 'SEQUENTIAL_BOOKINGS'
  | 'SEPARATE_BOOKINGS'
  | 'PARALLEL_BOOKINGS';

export interface BookedEntity extends BookedEntityItemOneOf {
  /**
   * [Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).
   *
   * Specify `slot` when creating bookings for:
   * - __Appointment-based services__: Individual sessions with service providers (consultations, treatments).
   * Wix Bookings creates a new session when the booking is confirmed.
   * - __Class services__: Group sessions at specific times (fitness classes, workshops).
   * Wix Bookings links the booking to an existing scheduled session.
   *
   * For course services, specify `schedule` instead of `slot`.
   */
  slot?: BookedSlot;
  /**
   * [Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).
   *
   * Specify `schedule` when creating bookings for:
   * - __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).
   * Wix Bookings enrolls participants in all sessions defined by the course schedule.
   */
  schedule?: BookedSchedule;
  /**
   * Session title at the time of booking. If there is no pre-existing session,
   * for example for appointment-based services, Wix Bookings sets `title` to the service name.
   * @readonly
   * @maxLength 6000
   */
  title?: string | null;
  /**
   * List of tags for the booking.
   *
   * - "INDIVIDUAL": For bookings of appointment-based services. Including when the appointment is for a group of participants.
   * - "GROUP": For bookings of individual class sessions.
   * - "COURSE": For course bookings.
   */
  tags?: string[] | null;
}

/** @oneof */
export interface BookedEntityItemOneOf {
  /**
   * [Booked slot](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings-and-time-slots/time-slots/availability-calendar/query-availability).
   *
   * Specify `slot` when creating bookings for:
   * - __Appointment-based services__: Individual sessions with service providers (consultations, treatments).
   * Wix Bookings creates a new session when the booking is confirmed.
   * - __Class services__: Group sessions at specific times (fitness classes, workshops).
   * Wix Bookings links the booking to an existing scheduled session.
   *
   * For course services, specify `schedule` instead of `slot`.
   */
  slot?: BookedSlot;
  /**
   * [Booked schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction).
   *
   * Specify `schedule` when creating bookings for:
   * - __Course services__: Multi-session offerings spanning weeks or months (educational courses, training programs).
   * Wix Bookings enrolls participants in all sessions defined by the course schedule.
   */
  schedule?: BookedSchedule;
}

export interface BookedSlot {
  /**
   * Session ID. For class bookings, this is the ID of the existing session.
   * For appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.
   *
   * __Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).
   * You can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.
   * @deprecated Session ID. For class bookings, this is the ID of the existing session.
   * For appointment bookings, this field is typically empty during booking creation and gets populated when the booking is confirmed.
   *
   * __Migration Guidance__: Replace usage of this field with `eventId` from the [Time Slots V2 API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/time-slots/time-slots-v2/introduction).
   * You can follow [these sample flows](https://dev.wix.com/docs/api-reference/business-solutions/bookings/end-to-end-booking-flows) for step-by-step guidance about determining availability and creating bookings.
   * @replacedBy event_id
   * @targetRemovalDate 2026-03-31
   */
  sessionId?: string | null;
  /** Service ID. */
  serviceId?: string;
  /** Schedule ID. */
  scheduleId?: string;
  /**
   * ID of the corresponding [event](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).
   * Available for both appointment and class bookings, not available for course bookings.
   * For appointment-based services, Wix Bookings automatically populates `eventId` when the booking `status` changes to `CONFIRMED`.
   * For class bookings, Wix Bookings automatically populates `eventId` upon booking creation.
   * @minLength 36
   * @maxLength 250
   */
  eventId?: string | null;
  /** The start time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`. */
  startDate?: string | null;
  /** The end time of this slot in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`. */
  endDate?: string | null;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * For example, `America/New_York` or `UTC`. This is the time zone in which the slot was shown to the customer at the time of booking. Wix Bookings ensures that the slot is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.
   */
  timezone?: string | null;
  /**
   * [Primary resource](https://dev.wix.com/docs/rest/business-solutions/bookings/resources/resources-v2/introduction) for the booking.
   * For example, the [staff member](https://dev.wix.com/docs/rest/business-solutions/bookings/staff-members/introduction) providing the service.
   */
  resource?: BookedResource;
  /** Location where the session takes place. */
  location?: Location;
  /**
   * Information about how the customer has selected resources for the booking.
   * Each resource type may have a different selection method.
   * Check `resource` for resource details.
   * @maxSize 3
   */
  resourceSelections?: ResourceSelection[];
}

export interface BookedResource {
  /**
   * ID of the booking's primary resource.
   * @format GUID
   */
  _id?: string;
  /**
   * Resource's name at the time of booking.
   * @maxLength 40
   */
  name?: string | null;
  /**
   * Resource's email at the time of booking.
   * @maxLength 500
   */
  email?: string | null;
  /**
   * ID of the schedule belonging to the booking's primary resource.
   * @format GUID
   */
  scheduleId?: string | null;
}

export enum ResourceTransparency {
  OPAQUE = 'OPAQUE',
  TRANSPARENT = 'TRANSPARENT',
}

/** @enumType */
export type ResourceTransparencyWithLiterals =
  | ResourceTransparency
  | 'OPAQUE'
  | 'TRANSPARENT';

export interface Location {
  /**
   * Business location ID. Available only for locations that are business locations,
   * meaning the `location_type` is `"OWNER_BUSINESS"`.
   * @format GUID
   */
  _id?: string | null;
  /** Location name. */
  name?: string | null;
  /** The full address of this location. */
  formattedAddress?: string | null;
  /**
   * The full translated address of this location.
   * @maxLength 512
   */
  formattedAddressTranslated?: string | null;
  /** Location type. */
  locationType?: LocationTypeWithLiterals;
}

export enum LocationType {
  /** Undefined location type. */
  UNDEFINED = 'UNDEFINED',
  /** The business address, as set in the site’s general settings. */
  OWNER_BUSINESS = 'OWNER_BUSINESS',
  /** The address as set when creating the service. */
  OWNER_CUSTOM = 'OWNER_CUSTOM',
  /** The address as set for the individual session. */
  CUSTOM = 'CUSTOM',
}

/** @enumType */
export type LocationTypeWithLiterals =
  | LocationType
  | 'UNDEFINED'
  | 'OWNER_BUSINESS'
  | 'OWNER_CUSTOM'
  | 'CUSTOM';

/** TODO: should we import it from services-2? */
export interface PhoneCall {
  /** Whether the service is delivered via phone call. */
  enabled?: boolean | null;
}

export interface ResourceSelection {
  /**
   * ID of the [resource type](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resource-types-v2/introduction).
   * @format GUID
   */
  resourceTypeId?: string | null;
  /** Information about how the customer has selected the resource for this resource type. */
  selectionMethod?: SelectionMethodWithLiterals;
}

export enum SelectionMethod {
  /** The customer explicitly chose a particular resource. */
  SPECIFIC_RESOURCE = 'SPECIFIC_RESOURCE',
  /** The customer explicitly chose "any available resource" for this resource type. */
  ANY_RESOURCE = 'ANY_RESOURCE',
  /** The customer wasn't offered a resource selection or agreement option for this resource type. */
  NO_SELECTION = 'NO_SELECTION',
}

/** @enumType */
export type SelectionMethodWithLiterals =
  | SelectionMethod
  | 'SPECIFIC_RESOURCE'
  | 'ANY_RESOURCE'
  | 'NO_SELECTION';

export interface BookedSchedule {
  /** [Schedule ID](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction). */
  scheduleId?: string;
  /**
   * Booked service ID.
   * @format GUID
   */
  serviceId?: string | null;
  /**
   * [Location](https://dev.wix.com/docs/rest/business-management/locations/introduction) where the schedule's sessions take place.
   * @readonly
   */
  location?: Location;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database).
   * For example, `America/New_York` or `UTC`. This is the time zone in which the schedule was shown to the customer at the time of booking. Wix Bookings ensures that the schedule is always displayed in this same time zone to the customer, including when they view or edit their booking in the future.
   */
  timezone?: string | null;
  /**
   * Start time of the first session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.
   * @readonly
   */
  firstSessionStart?: string | null;
  /**
   * End time of the last session related to the booking in `YYYY-MM-DDThh:mm:ss`, `YYYY-MM-DDThh:mm:ss:SSS`, or `YYYY-MM-DDThh:mm:ss:SSSZZ` [ISO-8601 format](https://en.wikipedia.org/wiki/ISO_8601). For example, `2026-01-30T13:30:00`, `2026-01-30T13:30:00:000`, or `2026-01-30T13:30:00:000-05:00`.
   * @readonly
   */
  lastSessionEnd?: string | null;
}

export interface ContactDetails {
  /**
   * Contact ID.
   * @format GUID
   */
  contactId?: string | null;
  /**
   * Contact's first name. When populated from a standard booking form, this
   * property corresponds to the `name` field.
   */
  firstName?: string | null;
  /** Contact's last name. */
  lastName?: string | null;
  /**
   * Contact's email. If no [contact](https://dev.wix.com/docs/rest/crm/members-contacts/contacts/contacts/contact-v4/contact-object)
   * with this email exist, a new contact is created.
   * Used to validate coupon usage limitations per contact. If not specified,
   * the coupon usage limitation will not be enforced. (Coupon usage limitation
   * validation is not supported yet).
   * @format EMAIL
   */
  email?: string | null;
  /** Contact's phone number. */
  phone?: string | null;
  /** Contact's full address. */
  fullAddress?: Address;
  /**
   * Contact's time zone.
   * @deprecated Contact's time zone.
   * @targetRemovalDate 2026-03-31
   */
  timeZone?: string | null;
  /**
   * Contact's country in [ISO 3166-1 alpha-2 code](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2)
   * format.
   * @format COUNTRY
   */
  countryCode?: string | null;
}

/** Physical address */
export interface Address extends AddressStreetOneOf {
  /** Street name, number and apartment number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number, as free text. */
  addressLine?: string | null;
  /**
   * Country code.
   * @format COUNTRY
   */
  country?: string | null;
  /** Subdivision. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  subdivision?: string | null;
  /** City name. */
  city?: string | null;
  /** Zip/postal code. */
  postalCode?: string | null;
  /** Free text providing more detailed address info. Usually contains Apt, Suite, and Floor. */
  addressLine2?: string | null;
  /** A string containing the full address of this location. */
  formattedAddress?: string | null;
  /** Free text to help find the address. */
  hint?: string | null;
  /** Coordinates of the physical address. */
  geocode?: AddressLocation;
  /** Country full name. */
  countryFullname?: string | null;
  /** Multi-level subdivisions from top to bottom. */
  subdivisions?: Subdivision[];
}

/** @oneof */
export interface AddressStreetOneOf {
  /** Street name, number and apartment number. */
  streetAddress?: StreetAddress;
  /** Main address line, usually street and number, as free text. */
  addressLine?: string | null;
}

export interface StreetAddress {
  /** Street number. */
  number?: string;
  /** Street name. */
  name?: string;
  /** Apartment number. */
  apt?: string;
}

export interface AddressLocation {
  /** Address latitude. */
  latitude?: number | null;
  /** Address longitude. */
  longitude?: number | null;
}

export interface Subdivision {
  /** Subdivision code. Usually state, region, prefecture or province code, according to [ISO 3166-2](https://en.wikipedia.org/wiki/ISO_3166-2). */
  code?: string;
  /** Subdivision full name. */
  name?: string;
}

export interface CustomFormField {
  /**
   * ID of the form field as defined in the form.
   * @format GUID
   */
  _id?: string;
  /** Value that was submitted for this field. */
  value?: string | null;
  /**
   * Form field's label at the time of submission.
   * @readonly
   */
  label?: string | null;
  valueType?: ValueTypeWithLiterals;
}

export enum ValueType {
  /** Short text. This is the default value type. */
  SHORT_TEXT = 'SHORT_TEXT',
  /** Long text. */
  LONG_TEXT = 'LONG_TEXT',
  /** A text that represents the check box value. If selected the value is `true`, otherwise `false`. */
  CHECK_BOX = 'CHECK_BOX',
}

/** @enumType */
export type ValueTypeWithLiterals =
  | ValueType
  | 'SHORT_TEXT'
  | 'LONG_TEXT'
  | 'CHECK_BOX';

/** Booking status. */
export enum BookingStatus {
  /** The booking was created, but the customer hasn't completed the related eCommerce order yet. */
  CREATED = 'CREATED',
  /** The merchant has confirmed the booking and it appears in the business calendar. Merchants can set up their [services](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/introduction) to automatically confirm all `PENDING` bookings. */
  CONFIRMED = 'CONFIRMED',
  /**
   * The customer has canceled the booking. Depending on the relevant service's [policy snapshot](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policy-snapshots/introduction)
   * they may have to pay a [cancellation fee](https://dev.wix.com/docs/rest/business-solutions/bookings/pricing/booking-fees/introduction).
   */
  CANCELED = 'CANCELED',
  /** The merchant must manually confirm the booking before it appears in the business calendar. */
  PENDING = 'PENDING',
  /** The merchant has declined the booking before the customer was charged. */
  DECLINED = 'DECLINED',
  /** Currently, you can't call [Register to Waitlist](https://dev.wix.com/docs/rest/business-solutions/bookings/bookings/waitlist/register-to-waitlist) for course or appointment bookings, even though this is supported in live sites. */
  WAITING_LIST = 'WAITING_LIST',
}

/** @enumType */
export type BookingStatusWithLiterals =
  | BookingStatus
  | 'CREATED'
  | 'CONFIRMED'
  | 'CANCELED'
  | 'PENDING'
  | 'DECLINED'
  | 'WAITING_LIST';

/**
 * Payment status.
 * Automatically updated when using eCommerce checkout APIs.
 */
export enum PaymentStatus {
  /** Undefined payment status. */
  UNDEFINED = 'UNDEFINED',
  /** The booking isn't paid. */
  NOT_PAID = 'NOT_PAID',
  /** The booking is fully paid. */
  PAID = 'PAID',
  /** The booking is partially paid. */
  PARTIALLY_PAID = 'PARTIALLY_PAID',
  /** The booking is refunded. */
  REFUNDED = 'REFUNDED',
  /** The booking is free of charge. */
  EXEMPT = 'EXEMPT',
}

/** @enumType */
export type PaymentStatusWithLiterals =
  | PaymentStatus
  | 'UNDEFINED'
  | 'NOT_PAID'
  | 'PAID'
  | 'PARTIALLY_PAID'
  | 'REFUNDED'
  | 'EXEMPT';

/**
 * Selected payment option.
 *
 * One of the payment options offered by the service.
 * This field is set when the user selects an option during booking.
 * If left undefined, the payment option is resolved by the service configuration on checkout.
 */
export enum SelectedPaymentOption {
  /** Undefined payment option. */
  UNDEFINED = 'UNDEFINED',
  /** Offline payment. */
  OFFLINE = 'OFFLINE',
  /** Online payment. */
  ONLINE = 'ONLINE',
  /** Payment using a Wix Pricing Plan. */
  MEMBERSHIP = 'MEMBERSHIP',
  /**
   * Customers can pay only in person with a Wix Pricing Plan, while the Wix user
   * must manually redeem the pricing plan in the dashboard.
   */
  MEMBERSHIP_OFFLINE = 'MEMBERSHIP_OFFLINE',
}

/** @enumType */
export type SelectedPaymentOptionWithLiterals =
  | SelectedPaymentOption
  | 'UNDEFINED'
  | 'OFFLINE'
  | 'ONLINE'
  | 'MEMBERSHIP'
  | 'MEMBERSHIP_OFFLINE';

export interface BookingSource {
  /** Platform from which a booking was created. */
  platform?: PlatformWithLiterals;
  /** Actor that created this booking. */
  actor?: ActorWithLiterals;
  /**
   * Wix site ID of the application that created the booking.
   * @format GUID
   * @readonly
   */
  appDefId?: string | null;
  /**
   * Name of the application that created the booking, as saved in Wix Developers Center at the time of booking.
   * @readonly
   */
  appName?: string | null;
}

export enum Platform {
  UNDEFINED_PLATFORM = 'UNDEFINED_PLATFORM',
  WEB = 'WEB',
  MOBILE_APP = 'MOBILE_APP',
}

/** @enumType */
export type PlatformWithLiterals =
  | Platform
  | 'UNDEFINED_PLATFORM'
  | 'WEB'
  | 'MOBILE_APP';

export enum Actor {
  UNDEFINED_ACTOR = 'UNDEFINED_ACTOR',
  BUSINESS = 'BUSINESS',
  CUSTOMER = 'CUSTOMER',
}

/** @enumType */
export type ActorWithLiterals =
  | Actor
  | 'UNDEFINED_ACTOR'
  | 'BUSINESS'
  | 'CUSTOMER';

export interface ParticipantNotification {
  /**
   * Whether to send a message about the changes to the customer.
   *
   * Default: `false`
   */
  notifyParticipants?: boolean;
  /** Custom message to send to the participants about the changes to the booking. */
  message?: string | null;
  /**
   * Information about the delivery channels used to send the notification.
   * For example, `{"channels": "SMS" }`, `{"channels": "EMAIL" }`, or `{"channels": "EMAIL, SMS" }`.
   */
  metadata?: Record<string, string>;
}

export interface CommonIdentificationData
  extends CommonIdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /**
   * ID of of a contact in the site's [CRM by Ascend](https://www.wix.com/ascend/crm) system.
   * @format GUID
   */
  contactId?: string | null;
}

/** @oneof */
export interface CommonIdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum IdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type IdentityTypeWithLiterals =
  | IdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

/**
 * Settings that control booking flow behavior and override default business rules.
 *
 * These settings allow administrators to bypass standard validation checks
 * and policies when creating, confirming, rescheduling, or canceling bookings.
 * Most settings require elevated permissions to use.
 *
 * Use flow control settings to handle special scenarios like:
 * - Emergency bookings outside normal business hours
 * - Admin-initiated bookings that bypass availability checks
 * - Custom payment flows that don't use standard eCommerce checkout
 * - Overriding cancellation or rescheduling policies in exceptional cases
 */
export interface FlowControlSettings {
  /** Whether availability is checked when creating or confirming the booking. */
  skipAvailabilityValidation?: boolean;
  /**
   * Whether the booking's `status` is automatically updated to `CONFIRMED` when
   * the customer completes the [eCommerce checkout](https://dev.wix.com/docs/rest/business-solutions/e-commerce/checkout/introduction),
   * regardless of whether the relevant service requires manual business confirmation.
   */
  skipBusinessConfirmation?: boolean;
  /**
   * Whether the customer is allowed to pay with a payment method that isn't
   * supported for the relevant service.
   */
  skipSelectedPaymentOptionValidation?: boolean;
  /**
   * Whether the customer receives an automatic refund if there's a double booking
   * conflict. Only available if the customer has paid with a
   * pricing plan.
   */
  withRefund?: boolean | null;
  /**
   * Whether [add-on](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) validations are skipped when creating a booking.
   * For example, customers can choose an add-on from an [add-on group](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-add-on-groups) that's not associated with the service or choose more than the maximum number of different add-ons from a group.
   * The calling [identity](https://dev.wix.com/docs/build-apps/develop-your-app/access/about-identities) must have `BOOKINGS.MANAGE_ADDONS` permissions when specifying `true`.
   *
   * Default: `false`.
   */
  skipAddOnValidation?: boolean | null;
}

export interface ParticipantChoices {
  /**
   * Information about the booked service choices. Includes the number of participants.
   * @minSize 1
   * @maxSize 20
   */
  serviceChoices?: ServiceChoices[];
}

export interface ServiceChoices {
  /**
   * Number of participants for this [variant](https://dev.wix.com/docs/rest/business-solutions/bookings/services/service-options-and-variants/introduction).
   * @min 1
   */
  numberOfParticipants?: number | null;
  /**
   * Service choices for these participants.
   * @maxSize 5
   */
  choices?: ServiceChoice[];
}

export interface ServiceChoice extends ServiceChoiceChoiceOneOf {
  /**
   * Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.
   * Choices are specific values for an option the customer can choose to book. For example,
   * the option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.
   * Each choice may have a different price.
   */
  custom?: string;
  duration?: Duration;
  /**
   * ID of the corresponding option for the choice. For example, the choice `child`
   * could correspond to the option `ageGroup`. In this case, `optionId` is the ID
   * for the `ageGroup` option.
   * @format GUID
   */
  optionId?: string;
}

/** @oneof */
export interface ServiceChoiceChoiceOneOf {
  /**
   * Value for one of the choices in the [`CustomServiceOption.choices`](https://example.com) list.
   * Choices are specific values for an option the customer can choose to book. For example,
   * the option `ageGroup` may have these choices: `child`, `student`, `adult`, and `senior`.
   * Each choice may have a different price.
   */
  custom?: string;
  duration?: Duration;
}

export interface Duration {
  /**
   * Duration of the service in minutes.
   * Min: 1 minute, Max: 30 days, 23 hours, and 59 minutes
   * @min 1
   * @max 44639
   */
  minutes?: number;
  /**
   * Name of the duration option.
   * Defaults to the formatted duration e.g. "1 hour, 30 minutes".
   * @maxLength 255
   */
  name?: string | null;
}

export interface MultiServiceBookingInfo {
  /**
   * Multi-service booking ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Type of the multi-service booking. */
  type?: MultiServiceBookingTypeWithLiterals;
}

export interface BookedAddOn {
  /**
   * The ID of the add-on.
   * @format GUID
   */
  _id?: string;
  /**
   * The ID of the add-on group.
   * @format GUID
   */
  groupId?: string;
  /**
   * The add-on duration in minutes at the time of booking.
   * Populated for duration-based add-ons.
   * @min 1
   * @max 1440
   * @readonly
   */
  durationInMinutes?: number | null;
  /**
   * The quantity of booked add-ons.
   * For duration-based add-ons, `quantity` is not applicable.
   * If `quantity` is provided as `1` for a duration-based add-on, it will be accepted but the value will be cleared.
   * If any other value is provided, an `INVALID_ARGUMENT` error will be returned with the message: "Invalid AddOn details: either duration or quantity must be set correctly".
   * @min 1
   * @max 1000
   */
  quantity?: number | null;
  /**
   * Add-on `name` at the time of booking.
   * @maxLength 100
   * @readonly
   */
  name?: string | null;
  /**
   * Add-on name translated to the language the customer used during booking.
   * @maxLength 100
   * @readonly
   */
  nameTranslated?: string | null;
}

export interface DomainEvent extends DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
}

/** @oneof */
export interface DomainEventBodyOneOf {
  createdEvent?: EntityCreatedEvent;
  updatedEvent?: EntityUpdatedEvent;
  deletedEvent?: EntityDeletedEvent;
  actionEvent?: ActionEvent;
}

export interface EntityCreatedEvent {
  entity?: string;
}

export interface RestoreInfo {
  deletedDate?: Date | null;
}

export interface EntityUpdatedEvent {
  /**
   * Since platformized APIs only expose PATCH and not PUT we can't assume that the fields sent from the client are the actual diff.
   * This means that to generate a list of changed fields (as opposed to sent fields) one needs to traverse both objects.
   * We don't want to impose this on all developers and so we leave this traversal to the notification recipients which need it.
   */
  currentEntity?: string;
}

export interface EntityDeletedEvent {
  /** Entity that was deleted. */
  deletedEntity?: string | null;
}

export interface ActionEvent {
  body?: string;
}

export interface MessageEnvelope {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Stringify payload. */
  data?: string;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface IdentificationData extends IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
  /** @readonly */
  identityType?: WebhookIdentityTypeWithLiterals;
}

/** @oneof */
export interface IdentificationDataIdOneOf {
  /**
   * ID of a site visitor that has not logged in to the site.
   * @format GUID
   */
  anonymousVisitorId?: string;
  /**
   * ID of a site visitor that has logged in to the site.
   * @format GUID
   */
  memberId?: string;
  /**
   * ID of a Wix user (site owner, contributor, etc.).
   * @format GUID
   */
  wixUserId?: string;
  /**
   * ID of an app.
   * @format GUID
   */
  appId?: string;
}

export enum WebhookIdentityType {
  UNKNOWN = 'UNKNOWN',
  ANONYMOUS_VISITOR = 'ANONYMOUS_VISITOR',
  MEMBER = 'MEMBER',
  WIX_USER = 'WIX_USER',
  APP = 'APP',
}

/** @enumType */
export type WebhookIdentityTypeWithLiterals =
  | WebhookIdentityType
  | 'UNKNOWN'
  | 'ANONYMOUS_VISITOR'
  | 'MEMBER'
  | 'WIX_USER'
  | 'APP';

export interface AccountInfo {
  /**
   * ID of the Wix account associated with the event.
   * @format GUID
   */
  accountId?: string | null;
  /**
   * ID of the parent Wix account. Only included when accountId belongs to a child account.
   * @format GUID
   */
  parentAccountId?: string | null;
  /**
   * ID of the Wix site associated with the event. Only included when the event is tied to a specific site.
   * @format GUID
   */
  siteId?: string | null;
}

export interface CreateServiceRequest {
  /** Service to create. */
  service: Service;
}

export interface CreateServiceResponse {
  /** Created service. */
  service?: Service;
}

export interface ValidateServiceRequest {
  /** Service to validate. */
  service?: Service;
}

export interface ValidateServiceResponse {
  /** Whether the service is valid. */
  valid?: boolean;
  /** Field violations. */
  fieldViolations?: FieldViolation[];
}

export interface FieldViolation {
  /**
   * Path to the invalid field.
   * @maxLength 2000
   */
  fieldName?: string;
  /**
   * Description of the error.
   * @maxLength 2000
   */
  description?: string;
  /**
   * Rule name of the violation
   * @maxLength 2000
   */
  ruleName?: string;
}

export interface BulkCreateServicesRequest {
  /**
   * Services to create.
   * @maxSize 100
   */
  services: Service[];
  /** Whether to return the created service objects. */
  returnEntity?: boolean;
}

export interface BulkCreateServicesResponse {
  /** The result of each service creation. */
  results?: BulkServiceResult[];
  /** Create statistics. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkServiceResult {
  /** Update metadata. */
  itemMetadata?: ItemMetadata;
  /** Updated service. */
  item?: Service;
}

export interface ItemMetadata {
  /**
   * Item ID. Should always be available, unless it's impossible (for example, when failing to create an item).
   * @format GUID
   */
  _id?: string | null;
  /** Index of the item within the request array. Allows for correlation between request and response items. */
  originalIndex?: number;
  /** Whether the requested action was successful for this item. When `false`, the `error` field is populated. */
  success?: boolean;
  /** Details about the error in case of failure. */
  error?: ApplicationError;
}

export interface ApplicationError {
  /** Error code. */
  code?: string;
  /** Description of the error. */
  description?: string;
  /** Data related to the error. */
  data?: Record<string, any> | null;
}

export interface BulkActionMetadata {
  /** Number of items that were successfully processed. */
  totalSuccesses?: number;
  /** Number of items that couldn't be processed. */
  totalFailures?: number;
  /** Number of failures without details because detailed failure threshold was exceeded. */
  undetailedFailures?: number;
}

export interface GetServiceRequest {
  /**
   * ID of the service to retrieve.
   * @format GUID
   */
  serviceId: string;
}

export enum V2RequestedFields {
  /** When passed, `service.staffMemberDetails` is returned. */
  STAFF_MEMBER_DETAILS = 'STAFF_MEMBER_DETAILS',
  /** When passed, `service.serviceResources.resourceType.name` is returned. */
  RESOURCE_TYPE_DETAILS = 'RESOURCE_TYPE_DETAILS',
  /** When passed, `service.payment.discountInfo` is returned. */
  DISCOUNT_INFO_DETAILS = 'DISCOUNT_INFO_DETAILS',
}

/** @enumType */
export type V2RequestedFieldsWithLiterals =
  | V2RequestedFields
  | 'STAFF_MEMBER_DETAILS'
  | 'RESOURCE_TYPE_DETAILS'
  | 'DISCOUNT_INFO_DETAILS';

export interface GetServiceResponse {
  /** Retrieved service. */
  service?: Service;
}

export interface GetServiceAvailabilityConstraintsRequest {
  /**
   * ID of the service to retrieve.
   * @format GUID
   */
  serviceId?: string;
}

export interface GetServiceAvailabilityConstraintsResponse {
  /** The retrieved availability constraints of the service. */
  constraints?: ServiceAvailabilityConstraints;
}

export interface ServiceAvailabilityConstraints {
  /**
   * The booking policy.
   * @readonly
   */
  bookingPolicy?: BookingPolicy;
  /**
   * The service schedule, including the schedule ID and availability constraints.
   * @readonly
   */
  schedule?: Schedule;
  /**
   * The locations this service is offered at.
   * Only multiple locations of type `BUSINESS` are supported. Multiple locations of type `CUSTOM` or `CUSTOMER` are not supported.
   * For courses only: Currently, only one location is supported for all location types.
   * Use the `Set Service Locations` method to change the locations this service is offered at.
   * @readonly
   * @maxSize 100
   */
  locations?: V2Location[];
  /**
   * Resource groups required to book the service. For backward compatibility only. Use `Service Resources` instead.
   * @maxSize 3
   * @readonly
   * @deprecated Resource groups required to book the service. For backward compatibility only. Use `Service Resources` instead.
   * @replacedBy service_resources
   * @targetRemovalDate 2024-08-19
   */
  resourceGroups?: ResourceGroup[];
  /**
   * Resource groups required to book the service.
   * @maxSize 3
   * @readonly
   */
  serviceResources?: ServiceResource[];
  /**
   * The time between available slots' start times.
   * For example, for 5-minute slots: 3:00, 3:05, 3:10 etc. For 1-hour slots: 3:00, 4:00, 5:00 etc.
   * Applied to all schedules of the site.
   * For appointments only.
   * @readonly
   */
  slotsSplitInterval?: SplitInterval;
  /**
   * Online booking settings.
   * @readonly
   */
  onlineBooking?: OnlineBooking;
}

/** The time between available slots' start times. For example, For 5 minute slots, 3:00, 3:05, 3:15 etc. For 1 hour slots, 3:00, 4:00, 5:00 etc. */
export interface SplitInterval {
  /**
   * Whether the slot duration is used as the split interval value.
   * If `same_as_duration` is `true`, the `value_in_minutes` is the sum of the first duration in
   * `schedule.availabilityConstraints.SlotDurations` field, and `schedule.availabilityConstraints.TimeBetweenSlots` field.
   */
  sameAsDuration?: boolean | null;
  /** Number of minutes between available slots' start times when `same_as_duration` is `false`. */
  valueInMinutes?: number | null;
}

export interface UpdateServiceRequest {
  /** Service to update. */
  service: Service;
}

export interface UpdateServiceResponse {
  /** Updated service. */
  service?: Service;
}

export interface BulkUpdateServicesRequest {
  /**
   * Services to update.
   * @maxSize 100
   */
  services?: MaskedService[];
  /** Whether to include the updated services in the response. Default: `false` */
  returnEntity?: boolean;
}

export interface MaskedService {
  /** Service to update. */
  service?: Service;
  /** Explicit list of fields to update. */
  mask?: string[];
}

export interface BulkUpdateServicesResponse {
  /** The result of each service update. */
  results?: BulkServiceResult[];
  /** Update statistics. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkUpdateServicesByFilterRequest {
  /** Filter to identify the services to update. */
  filter: Record<string, any> | null;
  /** Service to update. */
  service: Service;
}

export interface BulkUpdateServicesByFilterResponse {
  /**
   * ID of the service update job.
   *
   * Pass this ID to [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/introduction) to retrieve job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

export interface DeleteServiceRequest {
  /**
   * ID of the service to delete.
   * @format GUID
   */
  serviceId: string;
  /**
   * Whether to preserve future sessions with participants.
   *
   * Default: `false`
   */
  preserveFutureSessionsWithParticipants?: boolean;
  /**
   * Whether to notify participants about the change and an optional
   * custom message.
   */
  participantNotification?: V2ParticipantNotification;
}

export interface V2ParticipantNotification {
  /**
   * Whether to send a message about the changes to the customer.
   *
   * Default: `false`
   */
  notifyParticipants?: boolean | null;
  /**
   * Custom message to send to the participants about the changes to the booking.
   * @maxLength 2000
   */
  message?: string | null;
}

export interface DeleteServiceResponse {}

export interface BulkDeleteServicesRequest {
  /**
   * IDs of the services to delete.
   * @format GUID
   * @maxSize 100
   */
  ids: string[];
  /**
   * Whether to preserve future sessions with participants.
   *
   * Default: `false`.
   */
  preserveFutureSessionsWithParticipants?: boolean;
  /**
   * Whether to notify participants about the change and an optional
   * custom message.
   */
  participantNotification?: V2ParticipantNotification;
}

export interface BulkDeleteServicesResponse {
  /** The result of each service removal. */
  results?: BulkServiceResult[];
  /** Delete statistics. */
  bulkActionMetadata?: BulkActionMetadata;
}

export interface BulkDeleteServicesByFilterRequest {
  /** Filter to identify the services that need to be deleted. */
  filter: Record<string, any> | null;
  /**
   * Whether to preserve future sessions with participants.
   *
   * Default: `false`.
   */
  preserveFutureSessionsWithParticipants?: boolean;
  /** Whether to notify participants about the change and an optional custom message. */
  participantNotification?: V2ParticipantNotification;
}

export interface BulkDeleteServicesByFilterResponse {
  /**
   * ID of the service deletion job.
   *
   * Pass this ID to [Get Async Job](https://dev.wix.com/docs/rest/business-management/async-job/introduction) to retrieve job details and metadata.
   * @format GUID
   */
  jobId?: string;
}

export interface QueryServicesRequest {
  /** WQL expression. */
  query: QueryV2;
}

export interface QueryV2 extends QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
  /**
   * Filter object in the following format:
   *
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   *
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`
   *
   * Read more about [supported fields and operators](https://dev.wix.com/api/rest/wix-bookings/services-v2/filtering-and-sorting).
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[ {"fieldName":"sortField1","order":"ASC"},
   * {"fieldName":"sortField2","order":"DESC"} ]`
   *
   * Read more about [sorting](https://dev.wix.com/api/rest/wix-bookings/services-v2/filtering-and-sorting#wix-bookings_services-v2_filtering-and-sorting_sorting).
   * @maxSize 50
   */
  sort?: Sorting[];
}

/** @oneof */
export interface QueryV2PagingMethodOneOf {
  /** Paging options to limit and skip the number of items. */
  paging?: Paging;
}

export interface Sorting {
  /**
   * Name of the field to sort by.
   * @maxLength 512
   */
  fieldName?: string;
  /** Sort order. */
  order?: SortOrderWithLiterals;
}

export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/** @enumType */
export type SortOrderWithLiterals = SortOrder | 'ASC' | 'DESC';

export interface Paging {
  /** Number of items to load. */
  limit?: number | null;
  /** Number of items to skip in the current sort order. */
  offset?: number | null;
}

export interface CursorPaging {
  /**
   * Number of items to load.
   * @max 100
   */
  limit?: number | null;
  /**
   * Pointer to the next or previous page in the list of results.
   *
   * You can get the relevant cursor token
   * from the `pagingMetadata` object in the previous call's response.
   * Not relevant for the first request.
   * @maxLength 16000
   */
  cursor?: string | null;
}

export interface QueryServicesResponse {
  /** The retrieved services. */
  services?: Service[];
  /** Paging metadata, including offset and count. */
  pagingMetadata?: PagingMetadataV2;
}

export interface PagingMetadataV2 {
  /** Number of items returned in the response. */
  count?: number | null;
  /** Offset that was requested. */
  offset?: number | null;
  /** Total number of items that match the query. Returned if offset paging is used and the `tooManyToCount` flag is not set. */
  total?: number | null;
  /** Flag that indicates the server failed to calculate the `total` field. */
  tooManyToCount?: boolean | null;
  /** Cursors to navigate through the result pages using `next` and `prev`. Returned if cursor paging is used. */
  cursors?: Cursors;
}

export interface Cursors {
  /**
   * Cursor token for retrieving the next page of results.
   *
   * Use this token in subsequent requests to continue pagination forward.
   * Value is `null` when on the last page of results.
   * @maxLength 16000
   */
  next?: string | null;
  /**
   * Cursor token for retrieving the previous page of results.
   *
   * Use this token to navigate backwards through result pages.
   * Value is `null` when on the first page of results.
   * @maxLength 16000
   */
  prev?: string | null;
}

export interface SearchServicesRequest {
  /** Search criteria including filter, sort, aggregations, and paging options. */
  search: CursorSearch;
}

export interface CursorSearch extends CursorSearchPagingMethodOneOf {
  /**
   * Cursor-based paging for result navigation. When requesting `cursor_paging.cursor`,
   * `filter`, `sort`, or `search` can't be specified.
   */
  cursorPaging?: CursorPaging;
  /**
   * Filter object for narrowing search results. For example, to return only services with specific payment options: `"filter": {"payment.options.online": true, "payment.options.in_person": false}`.
   *
   * Learn more about the filter format in the [supported filters article](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/filtering-and-sorting).
   */
  filter?: Record<string, any> | null;
  /**
   * Array of sort objects specifying result order. For example, to sort by creation date in descending order: `"sort": [{"fieldName": "createdDate", "order": "DESC"}]`.
   *
   * Learn more about the sort format in the [supported filters article](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/filtering-and-sorting).
   * @maxSize 10
   */
  sort?: Sorting[];
  /**
   * Aggregations for grouping data into categories (facets) and providing summaries for each category.
   * For example, use aggregations to categorize search results by service type, payment options, or locations.
   * @maxSize 10
   */
  aggregations?: Aggregation[];
  /** Free text to match in searchable fields. */
  search?: SearchDetails;
  /**
   * Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database) or [ISO 8601 UTC offset format](https://en.wikipedia.org/wiki/ISO_8601#Time_offsets_from_UTC) for adjusting time fields in the specified filters and returned aggregation data. For example, `America/New_York`, `UTC`, or `+02:00`.
   *
   * Default: Time zone specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties).
   * @maxLength 50
   */
  timeZone?: string | null;
}

/** @oneof */
export interface CursorSearchPagingMethodOneOf {
  /**
   * Cursor-based paging for result navigation. When requesting `cursor_paging.cursor`,
   * `filter`, `sort`, or `search` can't be specified.
   */
  cursorPaging?: CursorPaging;
}

export interface Aggregation extends AggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
  /** Range aggregation configuration. */
  range?: RangeAggregation;
  /** Scalar aggregation configuration. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation configuration. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation configuration. */
  nested?: NestedAggregation;
  /**
   * User-defined name of aggregation. Must be unique and will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation. Client must specify matching aggregation field below. */
  type?: AggregationTypeWithLiterals;
  /**
   * Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.
   * @maxLength 200
   */
  fieldPath?: string;
  /**
   * Deprecated, use `nested` instead.
   * @deprecated Deprecated, use `nested` instead.
   * @replacedBy kind.nested
   * @targetRemovalDate 2025-01-01
   */
  groupBy?: GroupByAggregation;
}

/** @oneof */
export interface AggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
  /** Range aggregation configuration. */
  range?: RangeAggregation;
  /** Scalar aggregation configuration. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation configuration. */
  dateHistogram?: DateHistogramAggregation;
  /** Nested aggregation configuration. */
  nested?: NestedAggregation;
}

export interface RangeBucket {
  /** Inclusive lower bound of the range. Required if `to` isn't specified. */
  from?: number | null;
  /** Exclusive upper bound of the range. Required if `from` isn't specified. */
  to?: number | null;
}

export enum SortType {
  COUNT = 'COUNT',
  VALUE = 'VALUE',
}

/** @enumType */
export type SortTypeWithLiterals = SortType | 'COUNT' | 'VALUE';

export enum SortDirection {
  DESC = 'DESC',
  ASC = 'ASC',
}

/** @enumType */
export type SortDirectionWithLiterals = SortDirection | 'DESC' | 'ASC';

export enum MissingValues {
  EXCLUDE = 'EXCLUDE',
  INCLUDE = 'INCLUDE',
}

/** @enumType */
export type MissingValuesWithLiterals = MissingValues | 'EXCLUDE' | 'INCLUDE';

export interface IncludeMissingValuesOptions {
  /**
   * Custom bucket name for missing values.
   *
   * Default values:
   * - string: `N/A`
   * - int: `0`
   * - bool: `false`
   * @maxLength 20
   */
  addToBucket?: string;
}

export enum ScalarType {
  /** Total number of distinct values. */
  COUNT_DISTINCT = 'COUNT_DISTINCT',
  /** Minimum value. */
  MIN = 'MIN',
  /** Maximum value. */
  MAX = 'MAX',
}

/** @enumType */
export type ScalarTypeWithLiterals =
  | ScalarType
  | 'COUNT_DISTINCT'
  | 'MIN'
  | 'MAX';

export interface ValueAggregation extends ValueAggregationOptionsOneOf {
  /** Options for including missing values in the aggregation results. */
  includeOptions?: IncludeMissingValuesOptions;
  /** Whether to sort by number of matches or value of the field. */
  sortType?: SortTypeWithLiterals;
  /** Whether to sort in ascending or descending order. */
  sortDirection?: SortDirectionWithLiterals;
  /**
   * Number of aggregations to return.
   *
   * Min: `1`
   * Max: `250`
   * Default: `10`
   */
  limit?: number | null;
  /**
   * Whether missing values should be included or excluded from the aggregation results.
   *
   * Default: `EXCLUDE`
   */
  missingValues?: MissingValuesWithLiterals;
}

/** @oneof */
export interface ValueAggregationOptionsOneOf {
  /** Options for including missing values in the aggregation results. */
  includeOptions?: IncludeMissingValuesOptions;
}

export enum NestedAggregationType {
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into 1 key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
}

/** @enumType */
export type NestedAggregationTypeWithLiterals =
  | NestedAggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM';

export interface RangeAggregation {
  /**
   * List of range buckets defining the ranges for aggregation. During aggregation, each entity is placed in the first bucket where its value falls within the specified range bounds.
   * @maxSize 50
   */
  buckets?: RangeBucket[];
}

export interface ScalarAggregation {
  /** Operator for the scalar aggregation, for example `COUNT_DISTINCT`, `MIN`, `MAX`. */
  type?: ScalarTypeWithLiterals;
}

export interface DateHistogramAggregation {
  /** Time interval for date histogram aggregation, for example `DAY`, `HOUR`, `MONTH`. */
  interval?: IntervalWithLiterals;
}

export enum Interval {
  /** Yearly interval. */
  YEAR = 'YEAR',
  /** Monthly interval. */
  MONTH = 'MONTH',
  /** Weekly interval. */
  WEEK = 'WEEK',
  /** Daily interval. */
  DAY = 'DAY',
  /** Hourly interval. */
  HOUR = 'HOUR',
  /** Minute interval. */
  MINUTE = 'MINUTE',
  /** Second interval. */
  SECOND = 'SECOND',
}

/** @enumType */
export type IntervalWithLiterals =
  | Interval
  | 'YEAR'
  | 'MONTH'
  | 'WEEK'
  | 'DAY'
  | 'HOUR'
  | 'MINUTE'
  | 'SECOND';

export interface NestedAggregationItem extends NestedAggregationItemKindOneOf {
  /** Value aggregation configuration. Calculates the distribution of field values within the dataset. */
  value?: ValueAggregation;
  /** Range aggregation configuration. Calculates counts within user-defined value ranges. */
  range?: RangeAggregation;
  /** Scalar aggregation configuration. Calculates single numerical metrics like count, min, max, sum, or average. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation configuration. Calculates counts within time intervals. */
  dateHistogram?: DateHistogramAggregation;
  /**
   * User-defined name of aggregation. Must be unique and will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /** Type of aggregation. Client must specify matching aggregation field below. */
  type?: NestedAggregationTypeWithLiterals;
  /**
   * Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationItemKindOneOf {
  /** Value aggregation configuration. Calculates the distribution of field values within the dataset. */
  value?: ValueAggregation;
  /** Range aggregation configuration. Calculates counts within user-defined value ranges. */
  range?: RangeAggregation;
  /** Scalar aggregation configuration. Calculates single numerical metrics like count, min, max, sum, or average. */
  scalar?: ScalarAggregation;
  /** Date histogram aggregation configuration. Calculates counts within time intervals. */
  dateHistogram?: DateHistogramAggregation;
}

export enum AggregationType {
  /** Calculates the distribution of a specific field's values within a dataset, providing insights into the overall distribution and key statistics of those values. */
  VALUE = 'VALUE',
  /** Calculates the count of the values from the specified field in the dataset that fall within the range of each bucket you define. */
  RANGE = 'RANGE',
  /** Calculates a single numerical value from a dataset, summarizing the dataset into 1 key metric: `COUNT_DISTINCT`, `SUM`, `AVG`, `MIN`, or `MAX`. */
  SCALAR = 'SCALAR',
  /** Calculates the count of time values from the specified field in the dataset that fall within each time interval you define (hour, day, week, etc.). */
  DATE_HISTOGRAM = 'DATE_HISTOGRAM',
  /** Flattened list of aggregations, where each aggregation is nested within previous 1. */
  NESTED = 'NESTED',
}

/** @enumType */
export type AggregationTypeWithLiterals =
  | AggregationType
  | 'VALUE'
  | 'RANGE'
  | 'SCALAR'
  | 'DATE_HISTOGRAM'
  | 'NESTED';

/** Nested aggregation for multi-level faceted search. Allows exploring large amounts of data through multiple levels of categorization, where each subsequent aggregation is nested within the previous aggregation to create hierarchical data summaries. */
export interface NestedAggregation {
  /**
   * Flattened list of aggregations where each next aggregation is nested within the previous 1.
   * @minSize 2
   * @maxSize 10
   */
  nestedAggregations?: NestedAggregationItem[];
}

export interface GroupByAggregation extends GroupByAggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
  /**
   * User-defined name of aggregation. Must be unique and will appear in aggregation results.
   * @maxLength 100
   */
  name?: string | null;
  /**
   * Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface GroupByAggregationKindOneOf {
  /** Value aggregation configuration. */
  value?: ValueAggregation;
}

export interface SearchDetails {
  /** Search mode. Defines the search logic for combining multiple terms in the `expression`. */
  mode?: ModeWithLiterals;
  /**
   * Search term or expression.
   * @maxLength 200
   */
  expression?: string | null;
  /**
   * Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`.
   * @maxSize 10
   * @maxLength 200
   */
  fields?: string[];
  /** Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions. */
  fuzzy?: boolean;
}

export enum Mode {
  /** At least 1 of the search terms must be present. */
  OR = 'OR',
  /** All search terms must be present. */
  AND = 'AND',
}

/** @enumType */
export type ModeWithLiterals = Mode | 'OR' | 'AND';

export interface SearchServicesResponse {
  /**
   * Retrieved services that match the search criteria specified in the request.
   *
   * Each service includes all standard service information including name, description,
   * pricing details, location options, schedule information, and payment configuration.
   */
  services?: Service[];
  /**
   * Cursor-based paging metadata for navigating search results.
   *
   * Contains the current page's cursor information, whether there are more results available,
   * and count details. Use the `next` cursor to retrieve subsequent pages of results.
   */
  pagingMetadata?: CursorPagingMetadata;
  /**
   * Aggregation results based on the aggregations specified in the search request.
   *
   * Provides categorized data summaries such as service counts by type, location distribution,
   * payment method statistics, or custom aggregations. Available only when aggregations
   * are requested in the search criteria.
   */
  aggregationData?: AggregationData;
}

export interface CursorPagingMetadata {
  /**
   * Number of items returned in the current response page.
   *
   * This count reflects the actual number of items in the current result set,
   * which may be less than the requested limit if fewer items are available.
   */
  count?: number | null;
  /**
   * Navigation cursors for moving between result pages.
   *
   * Contains `next` and `prev` cursor tokens for pagination. Use the `next` cursor
   * to retrieve subsequent pages and `prev` cursor to go back to previous pages.
   * Learn more about cursor paging in the [API Query Language guide](https://dev.wix.com/api/rest/getting-started/api-query-language#getting-started_api-query-language_cursor-paging).
   */
  cursors?: Cursors;
  /**
   * Indicates whether additional results are available beyond the current page.
   *
   * - `true`: More results exist and can be retrieved using the `next` cursor.
   * - `false`: This is the final page of results.
   */
  hasNext?: boolean | null;
}

export interface AggregationData {
  /**
   * Array of aggregation results, each containing the aggregation metadata and its calculated values for the specified search criteria.
   * @maxSize 10000
   */
  results?: AggregationResults[];
}

export interface ValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 100
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number;
}

export interface RangeAggregationResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number;
}

export interface NestedAggregationResults
  extends NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /**
   * User-defined name of aggregation, matches the one specified in request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that matches result. */
  type?: AggregationTypeWithLiterals;
  /**
   * Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface NestedAggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
}

export interface ValueResults {
  /**
   * Array of value aggregation results, each containing a field value and the count of entities with that value.
   * @maxSize 250
   */
  results?: ValueAggregationResult[];
}

export interface RangeResults {
  /**
   * Array of range aggregation results returned in the same order as requested, each containing range bounds and count of entities within that range.
   * @maxSize 50
   */
  results?: RangeAggregationResult[];
}

export interface AggregationResultsScalarResult {
  /** Type of scalar aggregation. */
  type?: ScalarTypeWithLiterals;
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface NestedValueAggregationResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Nested aggregations. */
  nestedResults?: NestedAggregationResults;
}

export interface ValueResult {
  /**
   * Value of the field.
   * @maxLength 1000
   */
  value?: string;
  /** Count of entities with this value. */
  count?: number | null;
}

export interface RangeResult {
  /** Inclusive lower bound of the range. */
  from?: number | null;
  /** Exclusive upper bound of the range. */
  to?: number | null;
  /** Count of entities in this range. */
  count?: number | null;
}

export interface ScalarResult {
  /** Value of the scalar aggregation. */
  value?: number;
}

export interface ScalarDateResult {
  /**
   * Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.
   * @maxLength 100
   */
  value?: string;
}

export interface NestedResultValue extends NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
  /** Scalar date aggregation result. */
  scalarDate?: ScalarDateResult;
}

/** @oneof */
export interface NestedResultValueResultOneOf {
  /** Value aggregation result. */
  value?: ValueResult;
  /** Range aggregation result. */
  range?: RangeResult;
  /** Scalar aggregation result. */
  scalar?: ScalarResult;
  /** Date histogram aggregation result. */
  dateHistogram?: ValueResult;
  /** Scalar date aggregation result. */
  scalarDate?: ScalarDateResult;
}

export interface Results {
  /** Map of nested aggregation results, keyed by aggregation name. */
  results?: Record<string, NestedResultValue>;
}

export interface DateHistogramResult {
  /**
   * Date in [ISO 8601 format](https://en.wikipedia.org/wiki/ISO_8601).
   * @maxLength 100
   */
  value?: string;
  /** Count of documents in the bucket. */
  count?: number;
}

export interface GroupByValueResults {
  /**
   * Array of nested value aggregation results, each containing a field value and the associated nested aggregation data.
   * @maxSize 1000
   */
  results?: NestedValueAggregationResult[];
}

export interface DateHistogramResults {
  /**
   * Array of date histogram aggregation results, each containing a date bucket and its count.
   * @maxSize 200
   */
  results?: DateHistogramResult[];
}

/**
 * Results of `NESTED` aggregation type in a flattened form.
 * Aggregations in resulting array are keyed by requested aggregation `name`.
 */
export interface NestedResults {
  /**
   * Array of nested aggregation result groups, each containing multiple aggregation results.
   * @maxSize 1000
   */
  results?: Results[];
}

export interface AggregationResultsScalarDateResult {
  /** Type of scalar date aggregation. Possible values: `MIN`, `MAX`. */
  type?: ScalarTypeWithLiterals;
  /**
   * Date in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format.
   * @maxLength 100
   */
  value?: string;
}

export interface AggregationResults extends AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /** Date aggregation results. */
  scalarDate?: AggregationResultsScalarDateResult;
  /**
   * User-defined name of aggregation as derived from search request.
   * @maxLength 100
   */
  name?: string;
  /** Type of aggregation that must match specified kind as derived from search request. */
  type?: AggregationTypeWithLiterals;
  /**
   * Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`.
   * @maxLength 200
   */
  fieldPath?: string;
}

/** @oneof */
export interface AggregationResultsResultOneOf {
  /** Value aggregation results. */
  values?: ValueResults;
  /** Range aggregation results. */
  ranges?: RangeResults;
  /** Scalar aggregation results. */
  scalar?: AggregationResultsScalarResult;
  /** Group by value aggregation results. */
  groupedByValue?: GroupByValueResults;
  /** Date histogram aggregation results. */
  dateHistogram?: DateHistogramResults;
  /** Nested aggregation results. */
  nested?: NestedResults;
  /** Date aggregation results. */
  scalarDate?: AggregationResultsScalarDateResult;
}

export interface QueryPoliciesRequest {
  /** Information about filters, paging, and sorting. */
  query: CursorQuery;
}

export interface CursorQuery extends CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
  /**
   * Filter object in the following format:
   * `"filter" : {
   * "fieldName1": "value1",
   * "fieldName2":{"$operator":"value2"}
   * }`
   * Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`
   */
  filter?: Record<string, any> | null;
  /**
   * Sort object in the following format:
   * `[{"fieldName":"sortField1","order":"ASC"},{"fieldName":"sortField2","order":"DESC"}]`
   * @maxSize 50
   */
  sort?: Sorting[];
}

/** @oneof */
export interface CursorQueryPagingMethodOneOf {
  /** Cursor token pointing to a page of results. Not used in the first request. Following requests use the cursor token and not `filter` or `sort`. */
  cursorPaging?: CursorPaging;
}

export interface QueryPoliciesResponse {
  /** Retrieved booking policies and information about the services using them. */
  bookingPolicies?: BookingPolicyWithServices[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
}

export interface BookingPolicyWithServices {
  /** Retrieved booking policy. */
  bookingPolicy?: BookingPolicy;
  /**
   * Up to 5 services connected to the booking policy. If `totalServiceCount` is
   * greater than 5, there are additional services connected to the policy.
   * You can call [Search Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/search-services)
   * specifying the relevant policy ID in the filter, to retrieve all services that are
   * connected to a booking policy.
   * @maxSize 5
   */
  connectedServices?: Service[];
  /** Total number of services connected to the booking policy. */
  totalServiceCount?: number;
}

export interface QueryBookingFormsRequest {
  /**
   * Information about filters, paging, and sorting. See the article about
   * form filters ([REST](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object))
   * for all supported filters and sorting options.
   */
  query: CursorQuery;
  /**
   * Conditional fields to return.
   * @maxSize 5
   */
  conditionalFields?: RequestedFieldsWithLiterals[];
}

export enum RequestedFields {
  /** Whether to return the site's default booking form. */
  DEFAULT_BOOKING_FORM = 'DEFAULT_BOOKING_FORM',
}

/** @enumType */
export type RequestedFieldsWithLiterals =
  | RequestedFields
  | 'DEFAULT_BOOKING_FORM';

export interface QueryBookingFormsResponse {
  /** Retrieved booking forms and information about connected services. */
  bookingForms?: BookingForm[];
  /** Paging metadata. */
  pagingMetadata?: CursorPagingMetadata;
  /** The site's default booking form and information about connected services. */
  defaultBookingForm?: BookingForm;
}

export interface BookingForm {
  /** Information about the retrieved booking form. */
  formDetails?: FormDetails;
  /**
   * Up to 5 services connected to the booking form. If `totalServiceCount` is
   * greater than 5, there are additional services connected to the policy.
   * You can call [Search Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/search-services)
   * specifying the relevant policy ID in the filter, to retrieve all services that are
   * connected to a booking policy.
   * @maxSize 5
   */
  connectedServices?: ConnectedService[];
  /** Total number of services connected to the booking form. */
  totalServiceCount?: number;
}

export interface FormDetails {
  /**
   * Form ID.
   * @format GUID
   */
  formId?: string;
  /**
   * Form name.
   * @maxLength 200
   */
  name?: string | null;
  /**
   * Revision number, which increments by 1 each time the form is updated.
   * To prevent conflicting changes,
   * the current revision must be passed when updating the form.
   * @readonly
   */
  revision?: string | null;
}

export interface ConnectedService {
  /**
   * ID of the service connected to the booking form.
   * @format GUID
   */
  _id?: string | null;
  /**
   * Name of the service connected to the booking form.
   * @maxLength 400
   * @minLength 1
   */
  name?: string | null;
}

export interface CountServicesRequest {
  /** Query filter to base the count on. See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters. */
  filter?: Record<string, any> | null;
}

export interface CountServicesResponse {
  /** Number of services matching the specified filter. */
  count?: number;
}

export interface QueryLocationsRequest {
  /** Filter. */
  filter?: QueryLocationsFilter;
}

export interface QueryLocationsFilter {
  /** Service filter. See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters. */
  services?: Record<string, any> | null;
  /**
   * List of business IDs to filter by.
   * @format GUID
   * @maxSize 100
   */
  businessLocationIds?: string[];
}

export interface QueryLocationsResponse {
  /**
   * Retrieved business locations and whether each location is connected to at
   * least one service.
   */
  businessLocations?: BusinessLocations;
  /**
   * Retrieved custom locations and whether each location is connected to at
   * least one service.
   */
  customLocations?: CustomLocations;
  /**
   * Retrieved customer locations and whether each location is connected to at
   * least one service.
   */
  customerLocations?: CustomerLocations;
}

export interface BusinessLocations {
  /**
   * Whether at least one service matching the filter is connected to any of the
   * retrieved business locations.
   */
  exists?: boolean;
  /**
   * Retrieved business locations.
   * @maxSize 100
   */
  locations?: V2Location[];
}

export interface CustomLocations {
  /**
   * Whether at least one service matching the filter is connected to any of the
   * retrieved custom locations.
   */
  exists?: boolean;
}

export interface CustomerLocations {
  /**
   * Whether at least one service matching the filter is connected to any of the
   * retrieved customer locations.
   */
  exists?: boolean;
}

export interface QueryCategoriesRequest {
  /** Filter. */
  filter?: QueryCategoriesFilter;
}

export interface QueryCategoriesFilter {
  /** Service filter. See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters. */
  services?: Record<string, any> | null;
  /**
   * List of category IDs to filter by.
   * @format GUID
   * @maxSize 100
   */
  categoryIds?: string[];
}

export interface QueryCategoriesResponse {
  /** Retrieved categories. */
  categories?: V2Category[];
}

export interface QueryServicesMultiLanguageRequest {
  /** WQL expression. */
  query?: QueryV2;
}

export interface QueryServicesMultiLanguageResponse {
  /** The retrieved services in the main language */
  services?: Service[];
  /**
   * the retrieved services in the requested language according to the
   * provided linguist aspect
   */
  translatedServices?: Service[];
  /** Paging metadata, including offset and count. */
  pagingMetadata?: PagingMetadataV2;
}

export interface SetServiceLocationsRequest {
  /**
   * ID of the service.
   * @format GUID
   */
  serviceId: string;
  /**
   * List of locations replacing existing service locations. Omitting an existing location removes it from the service. Specify `removedLocationSessionsAction` to determine the handling of future sessions scheduled at that location.
   * @maxSize 100
   */
  locations?: V2Location[];
  /**
   * The action to perform on sessions currently set to a removed location. For
   * example, move existing sessions to a new specified location.
   * If not specified, sessions will not be moved to a new location.
   */
  removedLocationSessionsAction?: RemovedLocationSessionsAction;
  /**
   * Whether to notify participants about the change of location, and an
   * Optional custom message. The notification is sent only to participants of sessions that are affected by the change.
   */
  participantNotification?: V2ParticipantNotification;
}

export interface RemovedLocationSessionsAction
  extends RemovedLocationSessionsActionActionOptionsOneOf {
  /**
   * Details about the new location of future events that were scheduled to take
   * place at a removed location.
   */
  moveToLocationOptions?: MoveToNewLocationsOptions;
  /** Information about what to do with future events at the removed location. */
  action?: ActionWithLiterals;
}

/** @oneof */
export interface RemovedLocationSessionsActionActionOptionsOneOf {
  /**
   * Details about the new location of future events that were scheduled to take
   * place at a removed location.
   */
  moveToLocationOptions?: MoveToNewLocationsOptions;
}

export enum Action {
  /** Retain all future sessions at their current location. This is the default. */
  KEEP_AT_CURRENT_LOCATION = 'KEEP_AT_CURRENT_LOCATION',
  /** Move future events to a new location. */
  MOVE_TO_LOCATION = 'MOVE_TO_LOCATION',
}

/** @enumType */
export type ActionWithLiterals =
  | Action
  | 'KEEP_AT_CURRENT_LOCATION'
  | 'MOVE_TO_LOCATION';

export interface MoveToNewLocationsOptions {
  /** The new location to move existing sessions currently set to a removed location, used when `action` is `MOVE_TO_LOCATION`. */
  newLocation?: V2Location;
}

export interface SetServiceLocationsResponse {
  /** The updated service with the newly set locations. */
  service?: Service;
}

export interface EnablePricingPlansForServiceRequest {
  /**
   * ID of the service to update.
   * @format GUID
   */
  serviceId: string;
  /**
   * IDs of the [pricing plans](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/plans/introduction)
   * to add to the service's `payment.pricingPlanIds` array.
   * Maintained for backward compatibility. Use `pricing_plan_selections` for explicit benefit selection.
   * @format GUID
   * @maxSize 100
   */
  pricingPlanIds: string[];
}

/** Pricing plan selection with required benefit IDs. */
export interface PricingPlanSelection {
  /**
   * ID of the pricing plan
   * @format GUID
   */
  pricingPlanId?: string;
  /**
   * IDs of specific benefits to connect/disconnect.
   * This value corresponds to `poolDefinition.details.benefits.itemSetId` in the
   * [Pool Definition Object](https://dev.wix.com/docs/api-reference/business-solutions/benefit-programs/pool-definitions/pool-definition-object),
   * and differs from `benefitKey`.
   * This field is required and must contain at least one benefit ID.
   * @format GUID
   * @minSize 1
   * @maxSize 10
   */
  benefitIds?: string[];
}

export interface EnablePricingPlansForServiceResponse {
  /** Updated service. */
  service?: Service;
}

export interface InvalidPricingPlan {
  /**
   * ID of the invalid pricing plan.
   * @format GUID
   */
  _id?: string;
  /**
   * Explanation why the pricing plan is considered invalid.
   * @maxLength 2000
   */
  message?: string;
}

export interface DisablePricingPlansForServiceRequest {
  /**
   * ID of the service to update.
   * @format GUID
   */
  serviceId: string;
  /**
   * IDs of the [pricing plans](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/plans/introduction)
   * to remove from the service's `payment.pricingPlanIds` array.
   * Maintained for backward compatibility. Use `pricing_plan_selections` for explicit benefit selection.
   * @format GUID
   * @maxSize 75
   */
  pricingPlanIds?: string[];
}

export interface DisablePricingPlansForServiceResponse {
  /** Updated service. */
  service?: Service;
}

export interface SetCustomSlugRequest {
  /**
   * ID of the service for which to update the active slug.
   * @format GUID
   */
  serviceId: string;
  /**
   * Slug to set as the active service slug.
   * @maxLength 500
   */
  slug?: string;
}

export interface SetCustomSlugResponse {
  /** Updated active service slug. */
  slug?: Slug;
  /** Updated service. */
  service?: Service;
}

export interface ValidateSlugRequest {
  /**
   * IO of the service to check custom slug validity for.
   * @format GUID
   */
  serviceId: string;
  /**
   * Custom slug to validate.
   * @maxLength 500
   */
  slug?: string;
}

export interface ValidateSlugResponse {
  /** Whether the slug is valid. */
  valid?: boolean;
  /**
   * Valid slug. Available only if `{"valid": true}`.
   * @maxLength 500
   */
  slug?: string | null;
  /**
   * Reasons why the slug is invalid. Available only if `{"valid": false}`.
   * @maxSize 3
   */
  errors?: InvalidSlugErrorWithLiterals[];
}

export enum InvalidSlugError {
  /** Slug contains illegal characters. */
  SLUG_CONTAINS_ILLEGAL_CHARACTERS = 'SLUG_CONTAINS_ILLEGAL_CHARACTERS',
  /** Slug is already associated with another service. */
  SLUG_ALREADY_EXISTS = 'SLUG_ALREADY_EXISTS',
}

/** @enumType */
export type InvalidSlugErrorWithLiterals =
  | InvalidSlugError
  | 'SLUG_CONTAINS_ILLEGAL_CHARACTERS'
  | 'SLUG_ALREADY_EXISTS';

export interface CloneServiceRequest {
  /**
   * ID of the service to clone.
   * @format GUID
   */
  sourceServiceId: string;
}

export interface CloneServiceResponse {
  /** Cloned service. */
  service?: Service;
  /**
   * Information about connected entities that couldn't be cloned. For example,
   * future recurring events, the booking form, service variants, and connected
   * pricing plans.
   */
  errors?: CloneErrorsWithLiterals[];
}

export enum CloneErrors {
  /** Failed to clone the original service's [options and variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction). */
  OPTIONS_AND_VARIANTS = 'OPTIONS_AND_VARIANTS',
  /** Failed to clone the original service's *booking form*. */
  FORM = 'FORM',
}

/** @enumType */
export type CloneErrorsWithLiterals =
  | CloneErrors
  | 'OPTIONS_AND_VARIANTS'
  | 'FORM';

/** An event sent every time a category entity is changed. */
export interface CategoryNotification {
  category?: Category;
  event?: CategoryNotificationEventWithLiterals;
}

/** Categories are used to group multiple services together. A service must be associated with a category in order to be exposed in the Wix Bookings UI. */
export interface Category {
  /**
   * Category ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * Category name.
   * @maxLength 500
   */
  name?: string | null;
  /**
   * Category status.
   *
   * Default: `CREATED`
   * @readonly
   */
  status?: StatusWithLiterals;
  /** Sort order of the category in the live site and dashboard. */
  sortOrder?: number | null;
}

export enum Status {
  /** The category was created. */
  CREATED = 'CREATED',
  /** The category was deleted. */
  DELETED = 'DELETED',
}

/** @enumType */
export type StatusWithLiterals = Status | 'CREATED' | 'DELETED';

export enum CategoryNotificationEvent {
  /** Category was updated. */
  Updated = 'Updated',
  /** Category was deleted. */
  Deleted = 'Deleted',
  /** Category was created. */
  Created = 'Created',
}

/** @enumType */
export type CategoryNotificationEventWithLiterals =
  | CategoryNotificationEvent
  | 'Updated'
  | 'Deleted'
  | 'Created';

export interface Empty {}

export interface BenefitNotification {
  /**
   * Plan unique ID
   * @format GUID
   */
  planId?: string;
  /**
   * App def ID
   * @format GUID
   */
  appDefId?: string;
  /** Current benefit details */
  benefit?: Benefit;
  /** Previous benefit */
  prevBenefit?: Benefit;
  /** Notification event */
  event?: EventWithLiterals;
}

export interface Benefit {
  /**
   * Benefit unique ID
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /** Benefit Type */
  benefitType?: BenefitTypeWithLiterals;
  /**
   * Resource IDs that serves by this benefit
   * @format GUID
   */
  resourceIds?: string[];
  /** Amount of credits that provided by this benefit */
  creditAmount?: number | null;
  /**
   * additional details related to benefit; limited to 20 entries, 20 symbols for key and 20 symbols for value
   * @maxSize 20
   */
  customFields?: Record<string, string>;
  /** return value only in case it required in the ListRequest, true means that benefit's type could be updated */
  editable?: boolean | null;
  /** Benefit behavior */
  behavior?: Behavior;
  /**
   * Id of the app associated with this benefit
   * @format GUID
   * @readonly
   */
  appDefId?: string | null;
}

export interface EntryPass {}

export interface Discount extends DiscountDiscountOneOf {
  /**
   * Fixed-rate percent off discount
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentOffRate?: string;
  /**
   * Absolute amount discount
   * @decimalValue options { gt:0, maxScale:2 }
   */
  moneyOffAmount?: string;
}

/** @oneof */
export interface DiscountDiscountOneOf {
  /**
   * Fixed-rate percent off discount
   * @decimalValue options { gt:0, lte:100, maxScale:2 }
   */
  percentOffRate?: string;
  /**
   * Absolute amount discount
   * @decimalValue options { gt:0, maxScale:2 }
   */
  moneyOffAmount?: string;
}

export enum BenefitType {
  /** Should never be used */
  UNDEFINED = 'UNDEFINED',
  /** Limited benefit type */
  LIMITED = 'LIMITED',
  /** Unlimited benefit type */
  UNLIMITED = 'UNLIMITED',
}

/** @enumType */
export type BenefitTypeWithLiterals =
  | BenefitType
  | 'UNDEFINED'
  | 'LIMITED'
  | 'UNLIMITED';

export interface Behavior extends BehaviorBehaviorOneOf {
  /** Entry pass for resources, e.g. a ticket for Bookings service or a ticket for Events. */
  defaultBehavior?: EntryPass;
  /** Discount applied to paid resources */
  discount?: Discount;
}

/** @oneof */
export interface BehaviorBehaviorOneOf {
  /** Entry pass for resources, e.g. a ticket for Bookings service or a ticket for Events. */
  defaultBehavior?: EntryPass;
  /** Discount applied to paid resources */
  discount?: Discount;
}

export enum Event {
  Updated = 'Updated',
  Deleted = 'Deleted',
  Created = 'Created',
}

/** @enumType */
export type EventWithLiterals = Event | 'Updated' | 'Deleted' | 'Created';

export interface UserDomainInfoChangedEvent {
  domainName?: string;
  crudType?: CrudTypeWithLiterals;
  /** @format GUID */
  metaSiteId?: string | null;
  changeTime?: Date | null;
}

export enum CrudType {
  INVALID_CRUD_TYPE = 'INVALID_CRUD_TYPE',
  CREATE = 'CREATE',
  UPDATE = 'UPDATE',
  DELETE = 'DELETE',
  /** Unfortunately this action is used by hibernate save in wix-war */
  CREATE_OR_UPDATE = 'CREATE_OR_UPDATE',
}

/** @enumType */
export type CrudTypeWithLiterals =
  | CrudType
  | 'INVALID_CRUD_TYPE'
  | 'CREATE'
  | 'UPDATE'
  | 'DELETE'
  | 'CREATE_OR_UPDATE';

export interface HtmlSitePublished {
  /**
   * Application instance ID
   * @maxLength 50
   */
  appInstanceId?: string;
  /**
   * Application type
   * @maxLength 100
   */
  appType?: string;
  /** Revision */
  revision?: string;
  /**
   * MSID
   * @maxLength 100
   */
  metaSiteId?: string | null;
  /**
   * optional branch id if publish is done from branch
   * @format GUID
   */
  branchId?: string | null;
  /** The site's last transactionId */
  lastTransactionId?: string | null;
  /** A list of the site's pages */
  pages?: Page[];
  /** Site's publish date */
  publishDate?: string;
  /** Determine if this message was produced by editor-deployments service */
  originatedFromDeploymentsService?: boolean;
}

export interface Page {
  /**
   * Page's Id
   * @maxLength 100
   */
  _id?: string;
}

/** Encapsulates all details written to the Greyhound topic when a site's properties are updated. */
export interface SitePropertiesNotification {
  /** The site ID for which this update notification applies. */
  metasiteId?: string;
  /** The actual update event. */
  event?: SitePropertiesEvent;
  /**
   * A convenience set of mappings from the MetaSite ID to its constituent services.
   * @maxSize 500
   */
  translations?: Translation[];
  /** Context of the notification */
  changeContext?: ChangeContext;
}

/** The actual update event for a particular notification. */
export interface SitePropertiesEvent {
  /** Version of the site's properties represented by this update. */
  version?: number;
  /** Set of properties that were updated - corresponds to the fields in "properties". */
  fields?: string[];
  /** Updated properties. */
  properties?: Properties;
}

export interface Properties {
  /** Site categories. */
  categories?: Categories;
  /** Site locale. */
  locale?: Locale;
  /**
   * Site language.
   *
   * Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format.
   */
  language?: string | null;
  /**
   * Site currency format used to bill customers.
   *
   * Three-letter currency code in [ISO-4217 alphabetic](https://en.wikipedia.org/wiki/ISO_4217#Active_codes) format.
   */
  paymentCurrency?: string | null;
  /** Timezone in `America/New_York` format. */
  timeZone?: string | null;
  /** Email address. */
  email?: string | null;
  /** Phone number. */
  phone?: string | null;
  /** Fax number. */
  fax?: string | null;
  /** Address. */
  address?: V4Address;
  /** Site display name. */
  siteDisplayName?: string | null;
  /** Business name. */
  businessName?: string | null;
  /** Path to the site's logo in Wix Media (without Wix Media base URL). */
  logo?: string | null;
  /** Site description. */
  description?: string | null;
  /**
   * Business schedule. Regular and exceptional time periods when the business is open or the service is available.
   *
   * __Note:__ Not supported by Wix Bookings.
   */
  businessSchedule?: BusinessSchedule;
  /** Supported languages of a site and the primary language. */
  multilingual?: Multilingual;
  /** Cookie policy the Wix user defined for their site (before the site visitor interacts with/limits it). */
  consentPolicy?: ConsentPolicy;
  /**
   * Supported values: `FITNESS SERVICE`, `RESTAURANT`, `BLOG`, `STORE`, `EVENT`, `UNKNOWN`.
   *
   * Site business type.
   */
  businessConfig?: string | null;
  /** External site URL that uses Wix as its headless business solution. */
  externalSiteUrl?: string | null;
  /** Track clicks analytics. */
  trackClicksAnalytics?: boolean;
  /**
   * Company ID issued to the business by local authorities. Appears on invoices and price quotes.
   * @maxLength 50
   */
  companyId?: string | null;
}

export interface Categories {
  /** Primary site category. */
  primary?: string;
  /**
   * Secondary site category.
   * @maxSize 50
   */
  secondary?: string[];
  /** Business Term Id */
  businessTermId?: string | null;
}

export interface Locale {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Two-letter country code in [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2#Officially_assigned_code_elements) format. */
  country?: string;
}

export interface V4Address {
  /** Street name. */
  street?: string;
  /** City name. */
  city?: string;
  /** Two-letter country code in an [ISO-3166 alpha-2](https://en.wikipedia.org/wiki/ISO_3166-1_alpha-2) format. */
  country?: string;
  /** State. */
  state?: string;
  /**
   * Zip or postal code.
   * @maxLength 20
   */
  zip?: string;
  /** Extra information to be displayed in the address. */
  hint?: AddressHint;
  /** Whether this address represents a physical location. */
  isPhysical?: boolean;
  /** Google-formatted version of this address. */
  googleFormattedAddress?: string;
  /** Street number. */
  streetNumber?: string;
  /** Apartment number. */
  apartmentNumber?: string;
  /** Geographic coordinates of location. */
  coordinates?: GeoCoordinates;
}

/**
 * Extra information on displayed addresses.
 * This is used for display purposes. Used to add additional data about the address, such as "In the passage".
 * Free text. In addition, the user can state where to display the additional description - before, after, or instead of the address string.
 */
export interface AddressHint {
  /** Extra text displayed next to, or instead of, the actual address. */
  text?: string;
  /** Where the extra text should be displayed. */
  placement?: PlacementTypeWithLiterals;
}

/** Where the extra text should be displayed: before, after or instead of the actual address. */
export enum PlacementType {
  BEFORE = 'BEFORE',
  AFTER = 'AFTER',
  REPLACE = 'REPLACE',
}

/** @enumType */
export type PlacementTypeWithLiterals =
  | PlacementType
  | 'BEFORE'
  | 'AFTER'
  | 'REPLACE';

/** Geocoordinates for a particular address. */
export interface GeoCoordinates {
  /** Latitude of the location. Must be between -90 and 90. */
  latitude?: number;
  /** Longitude of the location. Must be between -180 and 180. */
  longitude?: number;
}

/** Business schedule. Regular and exceptional time periods when the business is open or the service is available. */
export interface BusinessSchedule {
  /**
   * Weekly recurring time periods when the business is regularly open or the service is available. Limited to 100 time periods.
   * @maxSize 100
   */
  periods?: TimePeriod[];
  /**
   * Exceptions to the business's regular hours. The business can be open or closed during the exception.
   * @maxSize 100
   */
  specialHourPeriod?: SpecialHourPeriod[];
}

/** Weekly recurring time periods when the business is regularly open or the service is available. */
export interface TimePeriod {
  /** Day of the week the period starts on. */
  openDay?: DayOfWeekWithLiterals;
  /**
   * Time the period starts in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   */
  openTime?: string;
  /** Day of the week the period ends on. */
  closeDay?: DayOfWeekWithLiterals;
  /**
   * Time the period ends in 24-hour [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) extended format. Valid values are `00:00` to `24:00`, where `24:00` represents
   * midnight at the end of the specified day.
   *
   * __Note:__ If `openDay` and `closeDay` specify the same day of the week `closeTime` must be later than `openTime`.
   */
  closeTime?: string;
}

/** Enumerates the days of the week. */
export enum DayOfWeek {
  MONDAY = 'MONDAY',
  TUESDAY = 'TUESDAY',
  WEDNESDAY = 'WEDNESDAY',
  THURSDAY = 'THURSDAY',
  FRIDAY = 'FRIDAY',
  SATURDAY = 'SATURDAY',
  SUNDAY = 'SUNDAY',
}

/** @enumType */
export type DayOfWeekWithLiterals =
  | DayOfWeek
  | 'MONDAY'
  | 'TUESDAY'
  | 'WEDNESDAY'
  | 'THURSDAY'
  | 'FRIDAY'
  | 'SATURDAY'
  | 'SUNDAY';

/** Exception to the business's regular hours. The business can be open or closed during the exception. */
export interface SpecialHourPeriod {
  /** Start date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  startDate?: string;
  /** End date and time of the exception in [ISO 8601](https://en.wikipedia.org/wiki/ISO_8601) format and [Coordinated Universal Time (UTC)](https://en.wikipedia.org/wiki/Coordinated_Universal_Time). */
  endDate?: string;
  /**
   * Whether the business is closed (or the service is not available) during the exception.
   *
   * Default: `true`.
   */
  isClosed?: boolean;
  /** Additional info about the exception. For example, "We close earlier on New Year's Eve." */
  comment?: string;
}

export interface Multilingual {
  /**
   * Supported languages list.
   * @maxSize 200
   */
  supportedLanguages?: SupportedLanguage[];
  /** Whether to redirect to user language. */
  autoRedirect?: boolean;
}

export interface SupportedLanguage {
  /** Two-letter language code in [ISO 639-1 alpha-2](https://en.wikipedia.org/wiki/List_of_ISO_639-1_codes) format. */
  languageCode?: string;
  /** Locale. */
  locale?: Locale;
  /** Whether the supported language is the primary language for the site. */
  isPrimary?: boolean;
  /** Language icon. */
  countryCode?: string;
  /** How the language will be resolved. For internal use. */
  resolutionMethod?: ResolutionMethodWithLiterals;
  /** Whether the supported language is the primary language for site visitors. */
  isVisitorPrimary?: boolean | null;
}

export enum ResolutionMethod {
  QUERY_PARAM = 'QUERY_PARAM',
  SUBDOMAIN = 'SUBDOMAIN',
  SUBDIRECTORY = 'SUBDIRECTORY',
}

/** @enumType */
export type ResolutionMethodWithLiterals =
  | ResolutionMethod
  | 'QUERY_PARAM'
  | 'SUBDOMAIN'
  | 'SUBDIRECTORY';

export interface ConsentPolicy {
  /** Whether the site uses cookies that are essential to site operation. Always `true`. */
  essential?: boolean | null;
  /** Whether the site uses cookies that affect site performance and other functional measurements. */
  functional?: boolean | null;
  /** Whether the site uses cookies that collect analytics about how the site is used (in order to improve it). */
  analytics?: boolean | null;
  /** Whether the site uses cookies that collect information allowing better customization of the experience for a current visitor. */
  advertising?: boolean | null;
  /** CCPA compliance flag. */
  dataToThirdParty?: boolean | null;
}

/** A single mapping from the MetaSite ID to a particular service. */
export interface Translation {
  /** The service type. */
  serviceType?: string;
  /** The application definition ID; this only applies to services of type ThirdPartyApps. */
  appDefId?: string;
  /** The instance ID of the service. */
  instanceId?: string;
}

export interface ChangeContext extends ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

/** @oneof */
export interface ChangeContextPayloadOneOf {
  /** Properties were updated. */
  propertiesChange?: PropertiesChange;
  /** Default properties were created on site creation. */
  siteCreated?: SiteCreated;
  /** Properties were cloned on site cloning. */
  siteCloned?: SiteCloned;
}

export interface PropertiesChange {}

export interface SiteCreated {
  /** Origin template site id. */
  originTemplateId?: string | null;
}

export interface SiteCloned {
  /** Origin site id. */
  originMetaSiteId?: string;
}

export interface CreateAddOnGroupRequest {
  /** Add-on group to create. */
  addOnGroup: AddOnGroup;
  /**
   * ID of the service to create the add-on group for.
   * @format GUID
   */
  serviceId: string | null;
}

export interface CreateAddOnGroupResponse {
  /** Created add-on group. */
  addOnGroup?: AddOnGroup;
}

export interface DeleteAddOnGroupRequest {
  /**
   * ID of the add-on group to delete.
   * @format GUID
   */
  addOnGroupId: string | null;
  /**
   * ID of the service from which to delete the add-on group.
   * @format GUID
   */
  serviceId: string | null;
}

export interface DeleteAddOnGroupResponse {}

export interface UpdateAddOnGroupRequest {
  /** Add-on group to update. */
  addOnGroup: AddOnGroup;
  /**
   * ID of the service that contains the add-on group.
   * @format GUID
   */
  serviceId: string | null;
}

export interface UpdateAddOnGroupResponse {
  /** Updated add-on group. */
  addOnGroup?: AddOnGroup;
}

export interface ListAddOnGroupsByServiceIdRequest {
  /**
   * ID of the service to retrieve add-on groups for.
   * @format GUID
   */
  serviceId: string | null;
  /**
   * List of group IDs to return. By default, all groups are returned.
   * @format GUID
   * @maxSize 3
   */
  groupIds?: string[] | null;
}

export interface ListAddOnGroupsByServiceIdResponse {
  /**
   * List of group details with their linked add-ons.
   * @maxSize 3
   */
  addOnGroupsDetails?: AddOnGroupDetail[];
}

export interface AddOn extends AddOnAddOnInfoOneOf {
  /** Duration in minutes for duration-based add-ons that extend booking service time. */
  durationInMinutes?: number;
  /** Maximum quantity customers can purchase for quantity-based add-ons. */
  maxQuantity?: number;
  /**
   * Add-on ID.
   * @format GUID
   */
  addOnId?: string | null;
  /**
   * Add-on name displayed to customers.
   * @maxLength 100
   */
  name?: string | null;
  /** Add-on price. */
  price?: Money;
  /**
   * Translated add-on name.
   * @maxLength 100
   */
  nameTranslated?: string | null;
}

/** @oneof */
export interface AddOnAddOnInfoOneOf {
  /** Duration in minutes for duration-based add-ons that extend booking service time. */
  durationInMinutes?: number;
  /** Maximum quantity customers can purchase for quantity-based add-ons. */
  maxQuantity?: number;
}

export interface AddOnGroupDetail {
  /**
   * Group ID.
   * @format GUID
   */
  groupId?: string | null;
  /** Maximum number of add-ons customers can select from this group. */
  maxNumberOfAddOns?: number | null;
  /**
   * Group name displayed to customers.
   * @maxLength 100
   */
  groupName?: string | null;
  /**
   * Add-ons linked to this group in display order.
   * @maxSize 7
   */
  addOns?: AddOn[];
  /**
   * Instructional prompt displayed to customers. For example, `Choose up to 2 relaxing spa treatments to enhance your experience`. You can also use this field as a group description. For example, `Beginner-friendly rental equipment`.
   * @maxLength 200
   */
  prompt?: string | null;
  /**
   * Translated group name.
   * @maxLength 100
   */
  groupNameTranslated?: string | null;
  /**
   * Instructional translated prompt displayed to customers. For example, `Choose up to 2 relaxing spa treatments to enhance your experience`. You can also use this field as a group description. For example, `Beginner-friendly rental equipment`.
   * @maxLength 200
   */
  promptTranslated?: string | null;
}

export interface SetAddOnsForGroupRequest {
  /**
   * Service ID containing the group.
   * @format GUID
   */
  serviceId: string | null;
  /**
   * Group ID to set add-ons for.
   * @format GUID
   */
  groupId: string | null;
  /**
   * IDs of add-ons to set for the group in display order.
   * @format GUID
   * @minSize 1
   * @maxSize 7
   */
  addOnIds: string[] | null;
}

export interface SetAddOnsForGroupResponse {
  /** Updated add-on group. */
  addOnGroup?: AddOnGroup;
}

/** @docsIgnore */
export type CreateServiceValidationErrors =
  | {
      ruleName?: 'INVALID_FORM';
    }
  | {
      ruleName?: 'INVALID_CATEGORY';
    }
  | {
      ruleName?: 'INVALID_BOOKING_POLICY';
    }
  | {
      ruleName?: 'INVALID_SERVICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_SERVICE_NAME';
    }
  | {
      ruleName?: 'INVALID_ONLINE_BOOKING';
    }
  | {
      ruleName?: 'INVALID_STAFF_MEMBER_IDS';
    }
  | {
      ruleName?: 'PAYMENT_REQUIRED';
    }
  | {
      ruleName?: 'INVALID_PAYMENT_TYPE';
    }
  | {
      ruleName?: 'INVALID_RATE';
    }
  | {
      ruleName?: 'INVALID_PAYMENT_OPTIONS';
    }
  | {
      ruleName?: 'INVALID_BUSINESS_LOCATIONS';
    }
  | {
      ruleName?: 'INVALID_LOCATIONS';
    }
  | {
      ruleName?: 'INVALID_BUSINESS_LOCATION';
    }
  | {
      ruleName?: 'INVALID_CUSTOM_LOCATION';
    }
  | {
      ruleName?: 'INVALID_CUSTOMER_LOCATION';
    }
  | {
      ruleName?: 'INVALID_UNKNOWN_LOCATION';
    }
  | {
      ruleName?: 'INVALID_MANUAL_APPROVAL_WITH_PRICING_PLANS';
    }
  | {
      ruleName?: 'INVALID_DEFAULT_CAPACITY';
    }
  | {
      ruleName?: 'INVALID_APPOINTMENT_CAPACITY';
    }
  | {
      ruleName?: 'INVALID_SESSION_DURATION';
    };
/** @docsIgnore */
export type UpdateServiceValidationErrors =
  | {
      ruleName?: 'INVALID_FORM';
    }
  | {
      ruleName?: 'INVALID_CATEGORY';
    }
  | {
      ruleName?: 'INVALID_BOOKING_POLICY';
    }
  | {
      ruleName?: 'INVALID_SERVICE_TYPE';
    }
  | {
      ruleName?: 'INVALID_SERVICE_NAME';
    }
  | {
      ruleName?: 'INVALID_ONLINE_BOOKING';
    }
  | {
      ruleName?: 'INVALID_STAFF_MEMBER_IDS';
    }
  | {
      ruleName?: 'PAYMENT_REQUIRED';
    }
  | {
      ruleName?: 'INVALID_PAYMENT_TYPE';
    }
  | {
      ruleName?: 'INVALID_RATE';
    }
  | {
      ruleName?: 'INVALID_PAYMENT_OPTIONS';
    }
  | {
      ruleName?: 'INVALID_BUSINESS_LOCATIONS';
    }
  | {
      ruleName?: 'INVALID_LOCATIONS';
    }
  | {
      ruleName?: 'INVALID_BUSINESS_LOCATION';
    }
  | {
      ruleName?: 'INVALID_CUSTOM_LOCATION';
    }
  | {
      ruleName?: 'INVALID_CUSTOMER_LOCATION';
    }
  | {
      ruleName?: 'INVALID_UNKNOWN_LOCATION';
    }
  | {
      ruleName?: 'INVALID_MANUAL_APPROVAL_WITH_PRICING_PLANS';
    }
  | {
      ruleName?: 'INVALID_DEFAULT_CAPACITY';
    }
  | {
      ruleName?: 'INVALID_APPOINTMENT_CAPACITY';
    }
  | {
      ruleName?: 'INVALID_SESSION_DURATION';
    };
/** @docsIgnore */
export type QueryBookingFormsApplicationErrors = {
  code?: 'DEFAULT_BOOKING_FORM_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type EnablePricingPlansForServiceApplicationErrors =
  | {
      code?: 'INVALID_PRICING_PLAN';
      description?: string;
      data?: InvalidPricingPlan;
    }
  | {
      code?: 'SERVICE_DOES_NOT_SUPPORT_PRICING_PLANS';
      description?: string;
      data?: Record<string, any>;
    };
/** @docsIgnore */
export type DisablePricingPlansForServiceApplicationErrors = {
  code?: 'INVALID_PRICING_PLAN';
  description?: string;
  data?: InvalidPricingPlan;
};
/** @docsIgnore */
export type SetCustomSlugApplicationErrors = {
  code?: 'SLUG_ALREADY_EXISTS';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type SetCustomSlugValidationErrors = {
  ruleName?: 'SLUG_CONTAINS_ILLEGAL_CHARACTERS';
};
/** @docsIgnore */
export type DeleteAddOnGroupApplicationErrors = {
  code?: 'GROUP_NOT_ON_SERVICE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type UpdateAddOnGroupApplicationErrors = {
  code?: 'GROUP_NOT_ON_SERVICE';
  description?: string;
  data?: Record<string, any>;
};
/** @docsIgnore */
export type SetAddOnsForGroupApplicationErrors = {
  code?: 'ADD_ON_GROUP_NOT_FOUND';
  description?: string;
  data?: Record<string, any>;
};

export interface BaseEventMetadata {
  /**
   * App instance ID.
   * @format GUID
   */
  instanceId?: string | null;
  /**
   * Event type.
   * @maxLength 150
   */
  eventType?: string;
  /** The identification type and identity data. */
  identity?: IdentificationData;
  /** Details related to the account */
  accountInfo?: AccountInfo;
}

export interface EventMetadata extends BaseEventMetadata {
  /** Event ID. With this ID you can easily spot duplicated events and ignore them. */
  _id?: string;
  /**
   * Fully Qualified Domain Name of an entity. This is a unique identifier assigned to the API main business entities.
   * For example, `wix.stores.catalog.product`, `wix.bookings.session`, `wix.payments.transaction`.
   */
  entityFqdn?: string;
  /**
   * Event action name, placed at the top level to make it easier for users to dispatch messages.
   * For example: `created`/`updated`/`deleted`/`started`/`completed`/`email_opened`.
   */
  slug?: string;
  /** ID of the entity associated with the event. */
  entityId?: string;
  /** Event timestamp in [ISO-8601](https://en.wikipedia.org/wiki/ISO_8601) format and UTC time. For example, `2020-04-26T13:57:50.699Z`. */
  eventTime?: Date | null;
  /**
   * Whether the event was triggered as a result of a privacy regulation application
   * (for example, GDPR).
   */
  triggeredByAnonymizeRequest?: boolean | null;
  /** If present, indicates the action that triggered the event. */
  originatedFrom?: string | null;
  /**
   * A sequence number that indicates the order of updates to an entity. For example, if an entity was updated at 16:00 and then again at 16:01, the second update will always have a higher sequence number.
   * You can use this number to make sure you're handling updates in the right order. Just save the latest sequence number on your end and compare it to the one in each new message. If the new message has an older (lower) number, you can safely ignore it.
   */
  entityEventSequence?: string | null;
  accountInfo?: AccountInfoMetadata;
}

export interface AccountInfoMetadata {
  /** ID of the Wix account associated with the event */
  accountId: string;
  /** ID of the Wix site associated with the event. Only included when the event is tied to a specific site. */
  siteId?: string;
  /** ID of the parent Wix account. Only included when 'accountId' belongs to a child account. */
  parentAccountId?: string;
}

export interface ServiceCreatedEnvelope {
  entity: Service;
  metadata: EventMetadata;
}

/**
 * Triggered when a service is created.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read Bookings - Public Data
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-PUBLIC
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId BOOKINGS.SERVICES_READ
 * @webhook
 * @eventType wix.bookings.services.v2.service_created
 * @slug created
 */
export declare function onServiceCreated(
  handler: (event: ServiceCreatedEnvelope) => void | Promise<void>
): void;

export interface ServiceDeletedEnvelope {
  entity: Service;
  metadata: EventMetadata;
}

/**
 * Triggered when a service is deleted.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read Bookings - Public Data
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-PUBLIC
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId BOOKINGS.SERVICES_READ
 * @webhook
 * @eventType wix.bookings.services.v2.service_deleted
 * @slug deleted
 */
export declare function onServiceDeleted(
  handler: (event: ServiceDeletedEnvelope) => void | Promise<void>
): void;

export interface ServiceUpdatedEnvelope {
  entity: Service;
  metadata: EventMetadata;
  /** @hidden */
  modifiedFields: Record<string, any>;
}

/**
 * Triggered when a service is updated.
 * @permissionScope Manage Stores
 * @permissionScopeId SCOPE.STORES.MANAGE-STORES
 * @permissionScope Read Bookings - Public Data
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-PUBLIC
 * @permissionScope Manage Bookings Services and Settings
 * @permissionScopeId SCOPE.BOOKINGS.CONFIGURATION
 * @permissionScope Wix Multilingual - Nile Wrapper Domain Events Read
 * @permissionScopeId SCOPE.MULTILINGUAL.NILE_WRAPPER_DOMAIN_EVENTS_READ
 * @permissionScope Manage Events
 * @permissionScopeId SCOPE.EVENTS.MANAGE-EVENTS
 * @permissionScope Manage Bookings
 * @permissionScopeId SCOPE.DC-BOOKINGS.MANAGE-BOOKINGS
 * @permissionScope Read Bookings - Including Participants
 * @permissionScopeId SCOPE.DC-BOOKINGS.READ-BOOKINGS-SENSITIVE
 * @permissionScope Read Bookings - all read permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.READ-BOOKINGS
 * @permissionScope Manage Bookings - all permissions
 * @permissionScopeId SCOPE.DC-BOOKINGS-MEGA.MANAGE-BOOKINGS
 * @permissionId BOOKINGS.SERVICES_READ
 * @webhook
 * @eventType wix.bookings.services.v2.service_updated
 * @slug updated
 */
export declare function onServiceUpdated(
  handler: (event: ServiceUpdatedEnvelope) => void | Promise<void>
): void;

/**
 * Creates a service.
 *
 *
 * ### Required fields
 *
 * When creating a service you must specify the following fields:
 * - [`type`](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-types):
 * Whether it's an appointment-based service, class, or course.
 * - `name`: Service name that's displayed to customers.
 * - `onlineBooking`:
 * Settings determining whether customers can book online, whether the business
 * must manually confirm bookings, and whether customers can request to book an
 * appointment time slot that already has a booking request awaiting business
 * confirmation.
 * - [`payment`](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments):
 * How customers can pay when signing up for the service.
 * - `defaultCapacity`: The maximum number of customers that can book the service. Required for all service types.
 * - For appointment-based services, it must be `1`.
 * - For classes, set to the maximum number of participants per session.
 * - For courses, set to the maximum number of participants for the course.
 *
 * ### Category
 *
 * Set `category.id` to assign the service to a [category](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/categories/introduction).
 * Without an assigned category, the service isn't visible on the live site.
 *
 * ### Online booking
 *
 * Set `onlineBooking.enabled` to `true` so customers can book the service online.
 * When enabled, the service is included in the [single-service booking flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/flow-single-service-booking).
 *
 * ### Approval
 *
 * Set `onlineBooking.requireManualApproval` to control whether bookings need business approval before confirmation.
 * Services with manual approval enabled can't accept pricing plan payments.
 *
 * ### Payment
 *
 * Set `payment.rateType` to `FIXED`, `VARIED`, `CUSTOM`, or `NO_FEE` and specify the corresponding rate details:
 * - `FIXED`: Set `payment.fixed.price`.
 * - `VARIED`: Set `payment.varied.defaultPrice`.
 * - `CUSTOM`: Set `payment.custom.description`.
 * - `NO_FEE`: No additional rate details required.
 *
 * Configure `payment.options` to control how customers can pay:
 * - `online`: Customers can pay online. Requires `rateType` of `FIXED` or `VARIED`.
 * - `inPerson`: Customers can pay in person.
 * - `deposit`: A deposit is required to book. Requires `rateType` of `FIXED` or `VARIED` and a `deposit` amount in the rate details.
 * - `pricingPlan`: Customers can pay using a [pricing plan](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/plans/introduction).
 * Can't be used in combination with manual approval.
 *
 * Learn more about [service payments](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-payments).
 *
 * ### Session durations
 *
 * Depending on which type of service you're creating, you may also need to specify
 * supported session durations.
 *
 * __Classes and courses__
 *
 * Don't specify `schedule.availabilityConstraints.sessionDurations`.
 *
 * __Appointment-based services without varied pricing based on session length__
 *
 * Specify the single supported session duration in the
 * `schedule.availabilityConstraints.sessionDurations` array.
 *
 * __Appointment-based services with varied pricing based on session length__
 *
 * - Specify all supported session durations in `schedule.availabilityConstraints.sessionDurations`.
 * - Note that Wix Bookings doesn't display these values to customers and
 * ignores them in pricing and availability calculations. Instead session
 * durations are retrieved from the relevant [service variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction).
 * - It is mandatory to specify `schedule.availabilityConstraints.sessionDurations`,
 * even though these values are ignored.
 *
 * ### Buffer time
 *
 * For appointment-based services, you can set `schedule.availabilityConstraints.timeBetweenSessions` to add buffer time between booked sessions.
 *
 * ### Locations
 *
 * Set the service's location types and options. You can set 1, some, or all types simultaneously. See [About service locations](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-service-locations) for details.
 *
 * ### Media
 *
 * Specify `media` so customers see images when selecting the service.
 * Set `media.mainMedia` as the primary image and optionally `media.coverMedia` or `media.items`.
 * Each item is an image with `id`, `height`, and `width`, and optionally `url` and `altText`.
 * Refer to the [Media Manager API](https://dev.wix.com/docs/rest/assets/media/media-manager/introduction) for details about uploading images and retrieving their IDs.
 *
 * ### Add-on groups
 *
 * Don't specify existing add-on groups when calling Create Service.
 * Instead, use dedicated add-on group and add-on creation methods to ensure proper validation and maintain referential integrity between services and their optional extras.
 * You can follow this [sample flow](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/sample-flows#create-bookable-add-ons-for-a-new-group) to create bookable add-ons.
 * @param service - Service to create.
 * @public
 * @requiredField service
 * @permissionId BOOKINGS.SERVICES_CREATE
 * @applicableIdentity APP
 * @returns Created service.
 * @fqn wix.bookings.services.v2.ServicesService.CreateService
 */
export async function createService(service: Service): Promise<
  NonNullablePaths<
    Service,
    | `type`
    | `media.items`
    | `category._id`
    | `form._id`
    | `payment.fixed.price.value`
    | `payment.fixed.price.currency`
    | `payment.rateType`
    | `payment.pricingPlanIds`
    | `payment.addOnOption`
    | `locations`
    | `locations.${number}.business._id`
    | `locations.${number}.business.name`
    | `locations.${number}.custom._id`
    | `locations.${number}._id`
    | `locations.${number}.type`
    | `bookingPolicy._id`
    | `bookingPolicy.customPolicyDescription.enabled`
    | `bookingPolicy.customPolicyDescription.description`
    | `bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `bookingPolicy.limitLateBookingPolicy.enabled`
    | `bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `bookingPolicy.bookAfterStartPolicy.enabled`
    | `bookingPolicy.cancellationPolicy.enabled`
    | `bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `bookingPolicy.reschedulePolicy.enabled`
    | `bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `bookingPolicy.waitlistPolicy.enabled`
    | `bookingPolicy.waitlistPolicy.capacity`
    | `bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `bookingPolicy.resourcesPolicy.enabled`
    | `bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `bookingPolicy.cancellationFeePolicy.enabled`
    | `bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `bookingPolicy.saveCreditCardPolicy.enabled`
    | `schedule.availabilityConstraints.durations`
    | `schedule.availabilityConstraints.durations.${number}.minutes`
    | `schedule.availabilityConstraints.sessionDurations`
    | `schedule.availabilityConstraints.timeBetweenSessions`
    | `staffMemberIds`
    | `serviceResources`
    | `supportedSlugs`
    | `supportedSlugs.${number}.name`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`
    | `addOnGroups`
    | `addOnDetails`
    | `taxableAddress.taxableAddressType`,
    6
  > & {
    __validationErrorsType?: CreateServiceValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ service: service }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'service.media.items.image' },
          { path: 'service.media.mainMedia.image' },
          { path: 'service.media.coverMedia.image' },
          { path: 'service.staffMembers.mainMedia.image' },
          { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
        ],
      },
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'service.locations.calculatedAddress' },
          { path: 'service.locations.business.address' },
          { path: 'service.locations.custom.address' },
        ],
      },
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [
          { path: 'service.urls.servicePage' },
          { path: 'service.urls.bookingPage' },
          { path: 'service.urls.calendarPage' },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixBookingsServicesV2Service.createService(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )?.service!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { service: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['service']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates multiple services.
 *
 *
 * See [Create Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/create-service) for more details.
 * @param services - Services to create.
 * @public
 * @requiredField services
 * @permissionId BOOKINGS.SERVICES_CREATE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.BulkCreateServices
 */
export async function bulkCreateServices(
  services: Service[],
  options?: BulkCreateServicesOptions
): Promise<
  NonNullablePaths<
    BulkCreateServicesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.type`
    | `results.${number}.item.category._id`
    | `results.${number}.item.form._id`
    | `results.${number}.item.payment.rateType`
    | `results.${number}.item.payment.addOnOption`
    | `results.${number}.item.bookingPolicy._id`
    | `results.${number}.item.taxableAddress.taxableAddressType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      services: services,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'services.media.items.image' },
          { path: 'services.media.mainMedia.image' },
          { path: 'services.media.coverMedia.image' },
          { path: 'services.staffMembers.mainMedia.image' },
          { path: 'services.staffMemberDetails.staffMembers.mainMedia.image' },
        ],
      },
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'services.locations.calculatedAddress' },
          { path: 'services.locations.business.address' },
          { path: 'services.locations.custom.address' },
        ],
      },
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [
          { path: 'services.urls.servicePage' },
          { path: 'services.urls.bookingPage' },
          { path: 'services.urls.calendarPage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.bulkCreateServices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.media.items.image' },
            { path: 'results.item.media.mainMedia.image' },
            { path: 'results.item.media.coverMedia.image' },
            { path: 'results.item.staffMembers.mainMedia.image' },
            {
              path: 'results.item.staffMemberDetails.staffMembers.mainMedia.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'results.item.locations.calculatedAddress' },
            { path: 'results.item.locations.business.address' },
            { path: 'results.item.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'results.item.urls.servicePage' },
            { path: 'results.item.urls.bookingPage' },
            { path: 'results.item.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          services: '$[0]',
          returnEntity: '$[1].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['services', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkCreateServicesOptions {
  /** Whether to return the created service objects. */
  returnEntity?: boolean;
}

/**
 * Retrieves a service.
 * @param serviceId - ID of the service to retrieve.
 * @public
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_READ
 * @applicableIdentity APP
 * @returns Retrieved service.
 * @fqn wix.bookings.services.v2.ServicesService.GetService
 */
export async function getService(
  serviceId: string
): Promise<
  NonNullablePaths<
    Service,
    | `type`
    | `media.items`
    | `category._id`
    | `form._id`
    | `payment.fixed.price.value`
    | `payment.fixed.price.currency`
    | `payment.rateType`
    | `payment.pricingPlanIds`
    | `payment.addOnOption`
    | `locations`
    | `locations.${number}.business._id`
    | `locations.${number}.business.name`
    | `locations.${number}.custom._id`
    | `locations.${number}._id`
    | `locations.${number}.type`
    | `bookingPolicy._id`
    | `bookingPolicy.customPolicyDescription.enabled`
    | `bookingPolicy.customPolicyDescription.description`
    | `bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `bookingPolicy.limitLateBookingPolicy.enabled`
    | `bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `bookingPolicy.bookAfterStartPolicy.enabled`
    | `bookingPolicy.cancellationPolicy.enabled`
    | `bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `bookingPolicy.reschedulePolicy.enabled`
    | `bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `bookingPolicy.waitlistPolicy.enabled`
    | `bookingPolicy.waitlistPolicy.capacity`
    | `bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `bookingPolicy.resourcesPolicy.enabled`
    | `bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `bookingPolicy.cancellationFeePolicy.enabled`
    | `bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `bookingPolicy.saveCreditCardPolicy.enabled`
    | `schedule.availabilityConstraints.durations`
    | `schedule.availabilityConstraints.durations.${number}.minutes`
    | `schedule.availabilityConstraints.sessionDurations`
    | `schedule.availabilityConstraints.timeBetweenSessions`
    | `staffMemberIds`
    | `serviceResources`
    | `supportedSlugs`
    | `supportedSlugs.${number}.name`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`
    | `addOnGroups`
    | `addOnDetails`
    | `taxableAddress.taxableAddressType`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
  });

  const reqOpts = ambassadorWixBookingsServicesV2Service.getService(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )?.service!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { serviceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['serviceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Updates a service.
 *
 *
 * Each time the service is updated, `revision` increments by 1. You must
 * include the number of the existing revision when updating the service.
 * This ensures you're working with the latest service information and
 * prevents unintended overwrites.
 *
 * ### Session durations
 *
 * Specify `schedule.availabilityConstraints.sessionDurations`
 * only if you want to update it for appointment-based services without varied
 * pricing based on session length. Don't specify `schedule.availabilityConstraints.sessionDurations`
 * for all other appointment-based services, classes, or courses. See [Create Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/create-service#session-durations) for more details.
 *
 * ### Service locations
 *
 * Don't call Update Service to adjust service locations, call [Set Service Locations](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/set-service-locations) instead.
 *
 * ### Add-on groups
 *
 * Don't use Update Service to modify service add-on groups. Use these dedicated methods instead:
 * - **Update add-on group properties**: Call Update Add On Group ([SDK](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-add-on-group?apiView=SDK) | [REST](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-add-on-group)).
 * - **Update add-on display order**: Call Set Add Ons For Group ([SDK](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/set-add-ons-for-group?apiView=SDK) | [REST](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/set-add-ons-for-group)).
 * @param _id - Service ID.
 * @public
 * @requiredField _id
 * @requiredField service
 * @requiredField service.revision
 * @permissionId BOOKINGS.SERVICES_UPDATE
 * @applicableIdentity APP
 * @returns Updated service.
 * @fqn wix.bookings.services.v2.ServicesService.UpdateService
 */
export async function updateService(
  _id: string,
  service: NonNullablePaths<UpdateService, `revision`, 2>
): Promise<
  NonNullablePaths<
    Service,
    | `type`
    | `media.items`
    | `category._id`
    | `form._id`
    | `payment.fixed.price.value`
    | `payment.fixed.price.currency`
    | `payment.rateType`
    | `payment.pricingPlanIds`
    | `payment.addOnOption`
    | `locations`
    | `locations.${number}.business._id`
    | `locations.${number}.business.name`
    | `locations.${number}.custom._id`
    | `locations.${number}._id`
    | `locations.${number}.type`
    | `bookingPolicy._id`
    | `bookingPolicy.customPolicyDescription.enabled`
    | `bookingPolicy.customPolicyDescription.description`
    | `bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `bookingPolicy.limitLateBookingPolicy.enabled`
    | `bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `bookingPolicy.bookAfterStartPolicy.enabled`
    | `bookingPolicy.cancellationPolicy.enabled`
    | `bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `bookingPolicy.reschedulePolicy.enabled`
    | `bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `bookingPolicy.waitlistPolicy.enabled`
    | `bookingPolicy.waitlistPolicy.capacity`
    | `bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `bookingPolicy.resourcesPolicy.enabled`
    | `bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `bookingPolicy.cancellationFeePolicy.enabled`
    | `bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `bookingPolicy.saveCreditCardPolicy.enabled`
    | `schedule.availabilityConstraints.durations`
    | `schedule.availabilityConstraints.durations.${number}.minutes`
    | `schedule.availabilityConstraints.sessionDurations`
    | `schedule.availabilityConstraints.timeBetweenSessions`
    | `staffMemberIds`
    | `serviceResources`
    | `supportedSlugs`
    | `supportedSlugs.${number}.name`
    | `seoData.tags`
    | `seoData.tags.${number}.type`
    | `seoData.tags.${number}.children`
    | `seoData.tags.${number}.custom`
    | `seoData.tags.${number}.disabled`
    | `seoData.settings.preventAutoRedirect`
    | `seoData.settings.keywords`
    | `seoData.settings.keywords.${number}.term`
    | `seoData.settings.keywords.${number}.isMain`
    | `addOnGroups`
    | `addOnDetails`
    | `taxableAddress.taxableAddressType`,
    6
  > & {
    __validationErrorsType?: UpdateServiceValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({ service: { ...service, id: _id } }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'service.media.items.image' },
          { path: 'service.media.mainMedia.image' },
          { path: 'service.media.coverMedia.image' },
          { path: 'service.staffMembers.mainMedia.image' },
          { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
        ],
      },
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'service.locations.calculatedAddress' },
          { path: 'service.locations.business.address' },
          { path: 'service.locations.custom.address' },
        ],
      },
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [
          { path: 'service.urls.servicePage' },
          { path: 'service.urls.bookingPage' },
          { path: 'service.urls.calendarPage' },
        ],
      },
    ]
  );

  const reqOpts = ambassadorWixBookingsServicesV2Service.updateService(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )?.service!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: { service: '$[1]' },
        explicitPathsToArguments: { 'service.id': '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['_id', 'service']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateService {
  /**
   * Service ID.
   * @format GUID
   * @readonly
   */
  _id?: string | null;
  /**
   * ID of the app associated with the service. You can't update `appId`.
   * Services are displayed in Wix Bookings only if they are associated with the Wix Bookings appId or have no associated app ID.
   * Default: `13d21c63-b5ec-5912-8397-c3a5ddb27a97` (Wix Bookings app ID)
   * For services from Wix apps, the following values apply:
   * - Wix Bookings: `"13d21c63-b5ec-5912-8397-c3a5ddb27a97"`
   * - Wix Services: `"cc552162-24a4-45e0-9695-230c4931ef40"`
   * - Wix Meetings: `"6646a75c-2027-4f49-976c-58f3d713ed0f"`
   * [Full list of apps created by Wix](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/platform/about-apps-created-by-wix).
   * <!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->
   * @format GUID
   * @immutable
   */
  appId?: string | null;
  /**
   * ID of the app that created the service. This field is used for analytics, auditing, and tracking creation sources.
   * This read-only field is automatically populated during service creation by checking these sources in order:
   * 1. The caller's App ID from the request identity context.
   * 2. The service's `appId` field.
   * 3. The Wix Bookings App ID (`13d21c63-b5ec-5912-8397-c3a5ddb27a97`) as the final fallback.
   * <!-- TODO: Uncomment when Platform docs are published - Learn more about [app identity in the Bookings Platform](https://dev.wix.com/docs/api-reference/business-solutions/bookings/bookings-platform/app-identity-in-the-bookings-platform). -->
   * @format GUID
   * @readonly
   */
  createdByAppId?: string | null;
  /**
   * Service type.
   * Learn more about [service types](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-types).
   */
  type?: ServiceTypeWithLiterals;
  /** Order of the service within a [category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v1/category-object). */
  sortOrder?: number | null;
  /**
   * Service name.
   * @maxLength 400
   * @minLength 1
   */
  name?: string | null;
  /**
   * Service description. For example, `High-class hair styling, cuts, straightening and color`.
   * @maxLength 7000
   */
  description?: string | null;
  /**
   * Short service description, such as `Hair styling`.
   * @maxLength 6000
   */
  tagLine?: string | null;
  /**
   * Default maximum number of customers that can book the service. The service cannot be booked beyond this capacity.
   * @min 1
   * @max 1000
   */
  defaultCapacity?: number | null;
  /** Media associated with the service. */
  media?: Media;
  /** Whether the service is hidden from Wix Bookings pages and widgets. */
  hidden?: boolean | null;
  /**
   * [Category](https://dev.wix.com/docs/rest/business-solutions/bookings/services/categories-v2/introduction)
   * the service is associated with. Services aren't automatically assigned to a category.
   * Without an associated category, the service isn't visible on the live site.
   */
  category?: V2Category;
  /** Form the customer filled out when booking the service. */
  form?: Form;
  /**
   * Payment options for booking the service.
   * Learn more about [service payments](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-payments).
   */
  payment?: Payment;
  /** Online booking settings. */
  onlineBooking?: OnlineBooking;
  /** Conferencing options for the service. */
  conferencing?: Conferencing;
  /**
   * The locations this service is offered at. Read more about [service locations](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-locations).
   * @immutable
   * @maxSize 500
   */
  locations?: V2Location[];
  /**
   * [Policy](https://dev.wix.com/docs/rest/business-solutions/bookings/policies/booking-policies/introduction)
   * determining under what conditions this service can be booked. For example, whether the service can only be booked up to 30 minutes before it begins.
   */
  bookingPolicy?: BookingPolicy;
  /**
   * The service's [schedule](https://dev.wix.com/docs/rest/business-management/calendar/schedules-v3/introduction),
   * which can be used to manage the service's [events](https://dev.wix.com/docs/rest/business-management/calendar/events-v3/introduction).
   */
  schedule?: Schedule;
  /**
   * IDs of the [resources](https://dev.wix.com/docs/api-reference/business-solutions/bookings/resources/resources-v2/introduction) associated with the [staff members](https://dev.wix.com/docs/api-reference/business-solutions/bookings/staff-members/staff-members/introduction) providing the service.
   * Note that these are the resource IDs, not the staff member IDs.
   *
   * For appointment-based services, set this field when creating or updating the service.
   * For classes and courses, this field is read-only and is automatically derived from staff assigned to the service's recurring scheduled sessions.
   * Staff assigned only to single, non-recurring events are not included.
   * Once all of a staff member's upcoming recurring sessions have ended, their ID is removed from this field.
   * To retrieve the full list of staff for classes or courses, query the service's calendar events instead.
   * Learn more about [retrieving staff for classes and courses](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/sample-flows#retrieve-staff-members-for-a-class-or-course).
   * @maxSize 220
   * @format GUID
   */
  staffMemberIds?: string[];
  /**
   * Information about which resources must be available so customers can book the service.
   * For example, a meeting room or equipment.
   * @maxSize 3
   */
  serviceResources?: ServiceResource[];
  /**
   * A slug is the last part of the URL address that serves as a unique identifier of the service.
   * The list of supported slugs includes past service names for backwards compatibility, and a custom slug if one was set by the business owner.
   * @readonly
   * @maxSize 100
   */
  supportedSlugs?: Slug[];
  /**
   * Active slug for the service.
   * Learn more about [service slugs](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/about-service-slugs).
   * @readonly
   */
  mainSlug?: Slug;
  /**
   * URLs to various service-related pages, such as the calendar page and the booking page.
   * @readonly
   */
  urls?: URLs;
  /** Extensions enabling users to save custom data related to the service. */
  extendedFields?: ExtendedFields;
  /** Custom SEO data for the service. */
  seoData?: SeoSchema;
  /**
   * Date and time the service was created in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _createdDate?: Date | null;
  /**
   * Date and time the service was updated in `YYYY-MM-DDThh:mm:ss.sssZ` format.
   * @readonly
   */
  _updatedDate?: Date | null;
  /**
   * Revision number, which increments by 1 each time the service is updated. To
   * prevent conflicting changes, the existing revision must be used when updating
   * a service.
   * @readonly
   */
  revision?: string | null;
  /**
   * Information about the [add-on groups](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/about-add-on-groups) associated with the service.
   * @maxSize 3
   */
  addOnGroups?: AddOnGroup[];
  /**
   * Details about all [add-ons](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/add-ons/introduction) customers can choose when booking the service.
   * @readonly
   * @maxSize 50
   */
  addOnDetails?: AddOnDetails[];
  /** Taxable address used to calculate tax */
  taxableAddress?: TaxableAddress;
}

/**
 * Updates up to 100 services.
 *
 *
 * See [Update Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-service)
 * for more details.
 *
 * The call succeeds even if one or more individual services can't be updated.
 * Information about failures is returned in `bulkActionMetadata`.
 * @public
 * @requiredField options.services.service
 * @requiredField options.services.service._id
 * @requiredField options.services.service.revision
 * @permissionId BOOKINGS.SERVICES_UPDATE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.BulkUpdateServices
 */
export async function bulkUpdateServices(
  options?: NonNullablePaths<
    BulkUpdateServicesOptions,
    | `services.${number}.service`
    | `services.${number}.service._id`
    | `services.${number}.service.revision`,
    5
  >
): Promise<
  NonNullablePaths<
    BulkUpdateServicesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.type`
    | `results.${number}.item.category._id`
    | `results.${number}.item.form._id`
    | `results.${number}.item.payment.rateType`
    | `results.${number}.item.payment.addOnOption`
    | `results.${number}.item.bookingPolicy._id`
    | `results.${number}.item.taxableAddress.taxableAddressType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      services: options?.services,
      returnEntity: options?.returnEntity,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'services.service.media.items.image' },
          { path: 'services.service.media.mainMedia.image' },
          { path: 'services.service.media.coverMedia.image' },
          { path: 'services.service.staffMembers.mainMedia.image' },
          {
            path: 'services.service.staffMemberDetails.staffMembers.mainMedia.image',
          },
        ],
      },
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'services.service.locations.calculatedAddress' },
          { path: 'services.service.locations.business.address' },
          { path: 'services.service.locations.custom.address' },
        ],
      },
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [
          { path: 'services.service.urls.servicePage' },
          { path: 'services.service.urls.bookingPage' },
          { path: 'services.service.urls.calendarPage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.bulkUpdateServices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.media.items.image' },
            { path: 'results.item.media.mainMedia.image' },
            { path: 'results.item.media.coverMedia.image' },
            { path: 'results.item.staffMembers.mainMedia.image' },
            {
              path: 'results.item.staffMemberDetails.staffMembers.mainMedia.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'results.item.locations.calculatedAddress' },
            { path: 'results.item.locations.business.address' },
            { path: 'results.item.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'results.item.urls.servicePage' },
            { path: 'results.item.urls.bookingPage' },
            { path: 'results.item.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          services: '$[0].services',
          returnEntity: '$[0].returnEntity',
        },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateServicesOptions {
  /**
   * Services to update.
   * @maxSize 100
   */
  services?: MaskedService[];
  /** Whether to include the updated services in the response. Default: `false` */
  returnEntity?: boolean;
}

/**
 * Updates multiple services by filter.
 *
 *
 * See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters.
 *
 * See [Update Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-service) for more details about updating a service.
 *
 * The call succeeds even if one or more individual services can't be updated.
 * Information about failures is returned in `bulkActionMetadata`.
 * @param filter - Filter to identify the services to update.
 * @public
 * @requiredField filter
 * @requiredField options
 * @requiredField options.service
 * @permissionId BOOKINGS.SERVICES_UPDATE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.BulkUpdateServicesByFilter
 */
export async function bulkUpdateServicesByFilter(
  filter: Record<string, any>,
  options: NonNullablePaths<BulkUpdateServicesByFilterOptions, `service`, 2>
): Promise<NonNullablePaths<BulkUpdateServicesByFilterResponse, `jobId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      filter: filter,
      service: options?.service,
    }),
    [
      {
        transformFn: transformSDKImageToRESTImage,
        paths: [
          { path: 'service.media.items.image' },
          { path: 'service.media.mainMedia.image' },
          { path: 'service.media.coverMedia.image' },
          { path: 'service.staffMembers.mainMedia.image' },
          { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
        ],
      },
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'service.locations.calculatedAddress' },
          { path: 'service.locations.business.address' },
          { path: 'service.locations.custom.address' },
        ],
      },
      {
        transformFn: transformSDKPageURLV2ToRESTPageURLV2,
        paths: [
          { path: 'service.urls.servicePage' },
          { path: 'service.urls.bookingPage' },
          { path: 'service.urls.calendarPage' },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.bulkUpdateServicesByFilter(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0]', service: '$[1].service' },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkUpdateServicesByFilterOptions {
  /** Service to update. */
  service: Service;
}

/**
 * Deletes a service.
 *
 *
 * Specify `{"preserveFutureSessionsWithParticipants": true}` to retain all
 * future sessions for the service. By default, all future sessions are canceled.
 * @param serviceId - ID of the service to delete.
 * @public
 * @requiredField serviceId
 * @param options - Allows you to configure how to handle the deleted service's future sessions and how to notify the sessions participants.
 * @permissionId BOOKINGS.SERVICES_DELETE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.DeleteService
 */
export async function deleteService(
  serviceId: string,
  options?: DeleteServiceOptions
): Promise<void> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
    preserveFutureSessionsWithParticipants:
      options?.preserveFutureSessionsWithParticipants,
    participantNotification: options?.participantNotification,
  });

  const reqOpts = ambassadorWixBookingsServicesV2Service.deleteService(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          serviceId: '$[0]',
          preserveFutureSessionsWithParticipants:
            '$[1].preserveFutureSessionsWithParticipants',
          participantNotification: '$[1].participantNotification',
        },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteServiceOptions {
  /**
   * Whether to preserve future sessions with participants.
   *
   * Default: `false`
   */
  preserveFutureSessionsWithParticipants?: boolean;
  /**
   * Whether to notify participants about the change and an optional
   * custom message.
   */
  participantNotification?: V2ParticipantNotification;
}

/**
 * Deletes multiple services.
 *
 *
 * See [Delete Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/delete-service)
 * for more details about deleting a service.
 *
 * The call succeeds even if one or more individual services can't be deleted.
 * Information about failures is returned in `bulkActionMetadata`.
 * @param ids - IDs of the services to delete.
 * @public
 * @requiredField ids
 * @permissionId BOOKINGS.SERVICES_DELETE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.BulkDeleteServices
 */
export async function bulkDeleteServices(
  ids: string[],
  options?: BulkDeleteServicesOptions
): Promise<
  NonNullablePaths<
    BulkDeleteServicesResponse,
    | `results`
    | `results.${number}.itemMetadata.originalIndex`
    | `results.${number}.itemMetadata.success`
    | `results.${number}.itemMetadata.error.code`
    | `results.${number}.itemMetadata.error.description`
    | `results.${number}.item.type`
    | `results.${number}.item.category._id`
    | `results.${number}.item.form._id`
    | `results.${number}.item.payment.rateType`
    | `results.${number}.item.payment.addOnOption`
    | `results.${number}.item.bookingPolicy._id`
    | `results.${number}.item.taxableAddress.taxableAddressType`
    | `bulkActionMetadata.totalSuccesses`
    | `bulkActionMetadata.totalFailures`
    | `bulkActionMetadata.undetailedFailures`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    ids: ids,
    preserveFutureSessionsWithParticipants:
      options?.preserveFutureSessionsWithParticipants,
    participantNotification: options?.participantNotification,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.bulkDeleteServices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'results.item.media.items.image' },
            { path: 'results.item.media.mainMedia.image' },
            { path: 'results.item.media.coverMedia.image' },
            { path: 'results.item.staffMembers.mainMedia.image' },
            {
              path: 'results.item.staffMemberDetails.staffMembers.mainMedia.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'results.item.locations.calculatedAddress' },
            { path: 'results.item.locations.business.address' },
            { path: 'results.item.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'results.item.urls.servicePage' },
            { path: 'results.item.urls.bookingPage' },
            { path: 'results.item.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          ids: '$[0]',
          preserveFutureSessionsWithParticipants:
            '$[1].preserveFutureSessionsWithParticipants',
          participantNotification: '$[1].participantNotification',
        },
        singleArgumentUnchanged: false,
      },
      ['ids', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDeleteServicesOptions {
  /**
   * Whether to preserve future sessions with participants.
   *
   * Default: `false`.
   */
  preserveFutureSessionsWithParticipants?: boolean;
  /**
   * Whether to notify participants about the change and an optional
   * custom message.
   */
  participantNotification?: V2ParticipantNotification;
}

/**
 * Deletes multiple services by filter.
 *
 *
 * See [Delete Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/delete-service)
 * for more details about deleting a service.
 *
 * The call succeeds even if one or more individual services can't be deleted.
 * Information about failures is returned in `bulkActionMetadata`.
 *
 * Refer to the [supported filters article](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/introduction#services-filtering-and-sorting)
 * for more details.
 *
 * To learn about working with filters in general, see
 * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters).
 * @param filter - Filter to identify the services that need to be deleted.
 * @public
 * @requiredField filter
 * @permissionId BOOKINGS.SERVICES_DELETE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.BulkDeleteServicesByFilter
 */
export async function bulkDeleteServicesByFilter(
  filter: Record<string, any>,
  options?: BulkDeleteServicesByFilterOptions
): Promise<NonNullablePaths<BulkDeleteServicesByFilterResponse, `jobId`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: filter,
    preserveFutureSessionsWithParticipants:
      options?.preserveFutureSessionsWithParticipants,
    participantNotification: options?.participantNotification,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.bulkDeleteServicesByFilter(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          filter: '$[0]',
          preserveFutureSessionsWithParticipants:
            '$[1].preserveFutureSessionsWithParticipants',
          participantNotification: '$[1].participantNotification',
        },
        singleArgumentUnchanged: false,
      },
      ['filter', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface BulkDeleteServicesByFilterOptions {
  /**
   * Whether to preserve future sessions with participants.
   *
   * Default: `false`.
   */
  preserveFutureSessionsWithParticipants?: boolean;
  /** Whether to notify participants about the change and an optional custom message. */
  participantNotification?: V2ParticipantNotification;
}

/**
 * Creates a query to retrieve a list of `service` objects.
 *
 * The `queryServices()` function builds a query to retrieve a list of `service` objects and returns a `ServicesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the [find()](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/services-query-builder/find) function.
 *
 * You can refine the query by chaining `ServicesQueryBuilder` functions onto the query. `ServicesQueryBuilder` functions enable you to sort, filter, and control the results that `queryServices()` returns.
 *
 * `queryServices()` runs with the following `ServicesQueryBuilder` defaults that you can override:
 *
 * + `limit` is `100`.
 * + Sorted by `createdDate` in ascending order.
 *
 * The functions that are chained to `queryServices()` are applied in the order they are called. For example, if you apply `ascending("category.name")` and then `ascending("name")`, the results are sorted first by `category.name`, and then, if there are multiple results with the same `category.name`, the items are sorted by `name`.
 *
 * The following `ServicesQueryBuilder` functions are supported for the `queryServices()` function. For a full description of the `service` object, see the object returned for the [items](https://dev.wix.com/docs/sdk/backend-modules/bookings/services/services-query-result/items) property in `ServicesQueryResult`.
 * @public
 * @permissionId BOOKINGS.SERVICES_READ
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.QueryServices
 */
export function queryServices(
  options?: QueryServicesOptions
): ServicesQueryBuilder {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  return queryBuilder<
    Service,
    'OFFSET',
    QueryServicesRequest,
    QueryServicesResponse
  >({
    func: async (payload: QueryServicesRequest) => {
      const reqOpts = ambassadorWixBookingsServicesV2Service.queryServices({
        ...payload,
        ...(options ?? {}),
      });

      sideEffects?.onSiteCall?.();
      try {
        const result = await httpClient.request(reqOpts);
        sideEffects?.onSuccess?.(result);
        return result;
      } catch (err) {
        sideEffects?.onError?.(err);
        throw err;
      }
    },
    requestTransformer: (query: QueryServicesRequest['query']) => {
      const args = [query, options] as [
        QueryServicesRequest['query'],
        QueryServicesOptions
      ];
      return renameKeysFromSDKRequestToRESTRequest({
        ...args?.[1],
        query: args?.[0],
      });
    },
    responseTransformer: ({ data }: HttpResponse<QueryServicesResponse>) => {
      const transformedData = renameKeysFromRESTResponseToSDKResponse(
        transformPaths(data, [
          {
            transformFn: transformRESTImageToSDKImage,
            paths: [
              { path: 'services.media.items.image' },
              { path: 'services.media.mainMedia.image' },
              { path: 'services.media.coverMedia.image' },
              { path: 'services.staffMembers.mainMedia.image' },
              {
                path: 'services.staffMemberDetails.staffMembers.mainMedia.image',
              },
            ],
          },
          {
            transformFn: transformRESTAddressToSDKAddress,
            paths: [
              { path: 'services.locations.calculatedAddress' },
              { path: 'services.locations.business.address' },
              { path: 'services.locations.custom.address' },
            ],
          },
          {
            transformFn: transformRESTPageURLV2ToSDKPageURLV2,
            paths: [
              { path: 'services.urls.servicePage' },
              { path: 'services.urls.bookingPage' },
              { path: 'services.urls.calendarPage' },
            ],
          },
        ])
      );

      return {
        items: transformedData?.services,
        pagingMetadata: transformedData?.pagingMetadata,
      };
    },
    errorTransformer: (err: unknown) => {
      const transformedError = sdkTransformError(err, {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      });

      throw transformedError;
    },
    pagingMethod: 'OFFSET',
    transformationPaths: {},
  });
}

export interface QueryServicesOptions {}

interface QueryOffsetResult {
  currentPage: number | undefined;
  totalPages: number | undefined;
  totalCount: number | undefined;
  hasNext: () => boolean;
  hasPrev: () => boolean;
  length: number;
  pageSize: number;
}

export interface ServicesQueryResult extends QueryOffsetResult {
  items: Service[];
  query: ServicesQueryBuilder;
  next: () => Promise<ServicesQueryResult>;
  prev: () => Promise<ServicesQueryResult>;
}

export interface ServicesQueryBuilder {
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  eq: (
    propertyName:
      | '_id'
      | 'appId'
      | 'type'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'hidden'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'payment.options.online'
      | 'payment.options.inPerson'
      | 'payment.options.pricingPlan'
      | 'onlineBooking.enabled'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'staffMemberIds'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ne: (
    propertyName:
      | '_id'
      | 'appId'
      | 'type'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'hidden'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'payment.options.online'
      | 'payment.options.inPerson'
      | 'payment.options.pricingPlan'
      | 'onlineBooking.enabled'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'staffMemberIds'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  ge: (
    propertyName:
      | '_id'
      | 'appId'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  gt: (
    propertyName:
      | '_id'
      | 'appId'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  le: (
    propertyName:
      | '_id'
      | 'appId'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `value`.
   * @param value - Value to compare against.
   */
  lt: (
    propertyName:
      | '_id'
      | 'appId'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `string`.
   * @param string - String to compare against. Case-insensitive.
   */
  startsWith: (
    propertyName:
      | '_id'
      | 'appId'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'locations.business.id'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: string
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasSome: (
    propertyName:
      | '_id'
      | 'appId'
      | 'type'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'hidden'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'payment.options.online'
      | 'payment.options.inPerson'
      | 'payment.options.pricingPlan'
      | 'onlineBooking.enabled'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'staffMemberIds'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any[]
  ) => ServicesQueryBuilder;
  /** @param propertyName - Property whose value is compared with `values`.
   * @param values - List of values to compare against.
   */
  hasAll: (
    propertyName: 'staffMemberIds',
    value: any[]
  ) => ServicesQueryBuilder;
  in: (
    propertyName:
      | '_id'
      | 'appId'
      | 'type'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'hidden'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'payment.options.online'
      | 'payment.options.inPerson'
      | 'payment.options.pricingPlan'
      | 'onlineBooking.enabled'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'staffMemberIds'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: any
  ) => ServicesQueryBuilder;
  exists: (
    propertyName:
      | '_id'
      | 'appId'
      | 'type'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'hidden'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'payment.options.online'
      | 'payment.options.inPerson'
      | 'payment.options.pricingPlan'
      | 'onlineBooking.enabled'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'staffMemberIds'
      | 'supportedSlugs.name'
      | 'mainSlug.name',
    value: boolean
  ) => ServicesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  ascending: (
    ...propertyNames: Array<
      | '_id'
      | 'appId'
      | 'type'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'hidden'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'payment.options.online'
      | 'payment.options.inPerson'
      | 'payment.options.pricingPlan'
      | 'onlineBooking.enabled'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'staffMemberIds'
      | 'supportedSlugs.name'
      | 'mainSlug.name'
    >
  ) => ServicesQueryBuilder;
  /** @param propertyNames - Properties used in the sort. To sort by multiple properties, pass properties as additional arguments. */
  descending: (
    ...propertyNames: Array<
      | '_id'
      | 'appId'
      | 'type'
      | 'name'
      | 'description'
      | 'tagLine'
      | 'hidden'
      | 'category.id'
      | 'category.name'
      | 'form.id'
      | 'payment.options.online'
      | 'payment.options.inPerson'
      | 'payment.options.pricingPlan'
      | 'onlineBooking.enabled'
      | 'locations.business.id'
      | 'schedule.firstSessionStart'
      | 'schedule.lastSessionEnd'
      | 'staffMemberIds'
      | 'supportedSlugs.name'
      | 'mainSlug.name'
    >
  ) => ServicesQueryBuilder;
  /** @param limit - Number of items to return, which is also the `pageSize` of the results object. */
  limit: (limit: number) => ServicesQueryBuilder;
  /** @param skip - Number of items to skip in the query results before returning the results. */
  skip: (skip: number) => ServicesQueryBuilder;
  find: () => Promise<ServicesQueryResult>;
}

/**
 * @hidden
 * @fqn wix.bookings.services.v2.ServicesService.QueryServices
 * @requiredField query
 */
export async function typedQueryServices(
  query: ServiceQuery,
  options?: QueryServicesOptions
): Promise<
  NonNullablePaths<
    QueryServicesResponse,
    | `services`
    | `services.${number}.type`
    | `services.${number}.category._id`
    | `services.${number}.form._id`
    | `services.${number}.payment.rateType`
    | `services.${number}.payment.addOnOption`
    | `services.${number}.bookingPolicy._id`
    | `services.${number}.bookingPolicy.customPolicyDescription.enabled`
    | `services.${number}.bookingPolicy.customPolicyDescription.description`
    | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `services.${number}.bookingPolicy.limitLateBookingPolicy.enabled`
    | `services.${number}.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `services.${number}.bookingPolicy.bookAfterStartPolicy.enabled`
    | `services.${number}.bookingPolicy.cancellationPolicy.enabled`
    | `services.${number}.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `services.${number}.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `services.${number}.bookingPolicy.reschedulePolicy.enabled`
    | `services.${number}.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `services.${number}.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `services.${number}.bookingPolicy.waitlistPolicy.enabled`
    | `services.${number}.bookingPolicy.waitlistPolicy.capacity`
    | `services.${number}.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `services.${number}.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `services.${number}.bookingPolicy.resourcesPolicy.enabled`
    | `services.${number}.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `services.${number}.bookingPolicy.cancellationFeePolicy.enabled`
    | `services.${number}.bookingPolicy.saveCreditCardPolicy.enabled`
    | `services.${number}.schedule.availabilityConstraints.timeBetweenSessions`
    | `services.${number}.seoData.settings.preventAutoRedirect`
    | `services.${number}.taxableAddress.taxableAddressType`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    ...options,
  });

  const reqOpts = ambassadorWixBookingsServicesV2Service.queryServices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'services.media.items.image' },
            { path: 'services.media.mainMedia.image' },
            { path: 'services.media.coverMedia.image' },
            { path: 'services.staffMembers.mainMedia.image' },
            {
              path: 'services.staffMemberDetails.staffMembers.mainMedia.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'services.locations.calculatedAddress' },
            { path: 'services.locations.business.address' },
            { path: 'services.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'services.urls.servicePage' },
            { path: 'services.urls.bookingPage' },
            { path: 'services.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ServiceQuerySpec extends QuerySpec {
  paging: 'offset';
  wql: [
    {
      fields: [
        'locations.business._id',
        'staffMemberIds',
        'supportedSlugs.name'
      ];
      operators: ['$hasAll', '$hasSome'];
      sort: 'BOTH';
    },
    {
      fields: [
        '_id',
        'appId',
        'category._id',
        'category.name',
        'description',
        'form._id',
        'hidden',
        'mainSlug.name',
        'name',
        'onlineBooking.enabled',
        'payment.options.inPerson',
        'payment.options.online',
        'payment.options.pricingPlan',
        'schedule.firstSessionStart',
        'schedule.lastSessionEnd',
        'tagLine',
        'type'
      ];
      operators: '*';
      sort: 'BOTH';
    }
  ];
}

export type CommonQueryWithEntityContext = QuerySdkType<
  Service,
  ServiceQuerySpec
>;
export type ServiceQuery = {
  /** 
  Paging options to limit and skip the number of items.  
  */
  paging?: {
    /** 
  Number of items to load.  
  */
    limit?: NonNullable<CommonQueryWithEntityContext['paging']>['limit'] | null;
    /** 
  Number of items to skip in the current sort order.  
  */
    offset?:
      | NonNullable<CommonQueryWithEntityContext['paging']>['offset']
      | null;
  };
  /** 
  Filter object in the following format:

  `"filter" : {
  "fieldName1": "value1",
  "fieldName2":{"$operator":"value2"}
  }`

  Example of operators: `$eq`, `$ne`, `$lt`, `$lte`, `$gt`, `$gte`, `$in`, `$hasSome`, `$hasAll`, `$startsWith`

  Read more about [supported fields and operators](https://dev.wix.com/api/rest/wix-bookings/services-v2/filtering-and-sorting).  
  */
  filter?: CommonQueryWithEntityContext['filter'] | null;
  /** 
  Sort object in the following format:
  `[ {"fieldName":"sortField1","order":"ASC"},
  {"fieldName":"sortField2","order":"DESC"} ]`

  Read more about [sorting](https://dev.wix.com/api/rest/wix-bookings/services-v2/filtering-and-sorting#wix-bookings_services-v2_filtering-and-sorting_sorting). 
  @maxSize: 50 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonQueryWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonQueryWithEntityContext['sort']>[number]['order'];
  }[];
};

export const utils = {
  query: {
    ...createQueryUtils<Service, ServiceQuerySpec, ServiceQuery>(),
  },
  search: {
    ...createSearchUtils<Service, ServiceSearchSpec, ServiceSearch>(),
  },
};

/**
 * Retrieves a list of up to 100 services, given the provided filtering, paging,
 * and sorting.
 *
 *
 * ### Defaults
 *
 * Search Services has the following default settings, which you can override:
 * + Sorted by `createdDate` in ascending order.
 * + `paging.limit` set to `100`.
 * + `paging.offset` set to `0`.
 *
 * ### Filters
 *
 * When using filters for dates, you must use
 * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * ### See also
 *
 * To learn about working with Search methods, see [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language) and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 * @param search - Search criteria including filter, sort, aggregations, and paging options.
 * @public
 * @requiredField search
 * @permissionId BOOKINGS.SERVICES_READ
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.SearchServices
 */
export async function searchServices(
  search: ServiceSearch
): Promise<
  NonNullablePaths<
    SearchServicesResponse,
    | `services`
    | `services.${number}.type`
    | `services.${number}.category._id`
    | `services.${number}.form._id`
    | `services.${number}.payment.rateType`
    | `services.${number}.payment.addOnOption`
    | `services.${number}.bookingPolicy._id`
    | `services.${number}.bookingPolicy.customPolicyDescription.enabled`
    | `services.${number}.bookingPolicy.customPolicyDescription.description`
    | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `services.${number}.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `services.${number}.bookingPolicy.limitLateBookingPolicy.enabled`
    | `services.${number}.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `services.${number}.bookingPolicy.bookAfterStartPolicy.enabled`
    | `services.${number}.bookingPolicy.cancellationPolicy.enabled`
    | `services.${number}.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `services.${number}.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `services.${number}.bookingPolicy.reschedulePolicy.enabled`
    | `services.${number}.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `services.${number}.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `services.${number}.bookingPolicy.waitlistPolicy.enabled`
    | `services.${number}.bookingPolicy.waitlistPolicy.capacity`
    | `services.${number}.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `services.${number}.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `services.${number}.bookingPolicy.resourcesPolicy.enabled`
    | `services.${number}.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `services.${number}.bookingPolicy.cancellationFeePolicy.enabled`
    | `services.${number}.bookingPolicy.saveCreditCardPolicy.enabled`
    | `services.${number}.schedule.availabilityConstraints.timeBetweenSessions`
    | `services.${number}.seoData.settings.preventAutoRedirect`
    | `services.${number}.taxableAddress.taxableAddressType`
    | `aggregationData.results`
    | `aggregationData.results.${number}.scalar.type`
    | `aggregationData.results.${number}.scalar.value`
    | `aggregationData.results.${number}.scalarDate.type`
    | `aggregationData.results.${number}.scalarDate.value`
    | `aggregationData.results.${number}.name`
    | `aggregationData.results.${number}.type`
    | `aggregationData.results.${number}.fieldPath`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ search: search });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.searchServices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'services.media.items.image' },
            { path: 'services.media.mainMedia.image' },
            { path: 'services.media.coverMedia.image' },
            { path: 'services.staffMembers.mainMedia.image' },
            {
              path: 'services.staffMemberDetails.staffMembers.mainMedia.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'services.locations.calculatedAddress' },
            { path: 'services.locations.business.address' },
            { path: 'services.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'services.urls.servicePage' },
            { path: 'services.urls.bookingPage' },
            { path: 'services.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { search: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['search']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ServiceSearchSpec extends SearchSpec {
  searchable: ['name'];
  aggregatable: [
    '_id',
    'addOnDetails.addOnId',
    'addOnDetails.durationInMinutes',
    'appId',
    'category._id',
    'category.name',
    'category.sortOrder',
    'description',
    'form._id',
    'hidden',
    'locations.business._id',
    'locations.business.name',
    'locations.type',
    'mainSlug.name',
    'name',
    'onlineBooking.enabled',
    'payment.options.inPerson',
    'payment.options.online',
    'payment.options.pricingPlan',
    'staffMemberIds',
    'supportedSlugs.name',
    'tagLine',
    'type'
  ];
  paging: 'cursor';
  wql: [
    {
      operators: ['$hasAll', '$hasSome'];
      fields: [
        'addOnDetails.addOnId',
        'addOnDetails.durationInMinutes',
        'locations.business._id',
        'locations.business.name',
        'locations.calculatedAddress.formattedAddress',
        'locations.type',
        'staffMemberIds',
        'supportedSlugs.name'
      ];
      sort: 'BOTH';
    },
    {
      operators: '*';
      fields: [
        '_id',
        'appId',
        'category._id',
        'category.name',
        'category.sortOrder',
        'description',
        'form._id',
        'hidden',
        'mainSlug.name',
        'name',
        'onlineBooking.enabled',
        'payment.options.inPerson',
        'payment.options.online',
        'payment.options.pricingPlan',
        'tagLine',
        'type'
      ];
      sort: 'BOTH';
    }
  ];
}

export type CommonSearchWithEntityContext = SearchSdkType<
  Service,
  ServiceSearchSpec
>;
export type ServiceSearch = {
  /** 
  Cursor-based paging for result navigation. When requesting `cursor_paging.cursor`,
  `filter`, `sort`, or `search` can't be specified.  
  */
  cursorPaging?: {
    /** 
  Number of items to load. 
  @max: 100 
  */
    limit?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['limit']
      | null;
    /** 
  Pointer to the next or previous page in the list of results.

  You can get the relevant cursor token
  from the `pagingMetadata` object in the previous call's response.
  Not relevant for the first request. 
  @maxLength: 16000 
  */
    cursor?:
      | NonNullable<CommonSearchWithEntityContext['cursorPaging']>['cursor']
      | null;
  };
  /** 
  Filter object for narrowing search results. For example, to return only services with specific payment options: `"filter": {"payment.options.online": true, "payment.options.in_person": false}`.

  Learn more about the filter format in the [supported filters article](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/filtering-and-sorting).  
  */
  filter?: CommonSearchWithEntityContext['filter'] | null;
  /** 
  Array of sort objects specifying result order. For example, to sort by creation date in descending order: `"sort": [{"fieldName": "createdDate", "order": "DESC"}]`.

  Learn more about the sort format in the [supported filters article](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/filtering-and-sorting). 
  @maxSize: 10 
  */
  sort?: {
    /** 
  Name of the field to sort by. 
  @maxLength: 512 
  */
    fieldName?: NonNullable<
      CommonSearchWithEntityContext['sort']
    >[number]['fieldName'];
    /** 
  Sort order.  
  */
    order?: NonNullable<CommonSearchWithEntityContext['sort']>[number]['order'];
  }[];
  /** 
  Aggregations for grouping data into categories (facets) and providing summaries for each category.
  For example, use aggregations to categorize search results by service type, payment options, or locations. 
  @maxSize: 10 
  */
  aggregations?: {
    /** 
  Value aggregation configuration.  
  */
    value?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['value'];
    /** 
  Range aggregation configuration.  
  */
    range?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['range'];
    /** 
  Scalar aggregation configuration.  
  */
    scalar?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['scalar'];
    /** 
  Date histogram aggregation configuration.  
  */
    dateHistogram?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['dateHistogram'];
    /** 
  Nested aggregation configuration.  
  */
    nested?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['nested'];
    /** 
  User-defined name of aggregation. Must be unique and will appear in aggregation results. 
  @maxLength: 100 
  */
    name?:
      | NonNullable<
          CommonSearchWithEntityContext['aggregations']
        >[number]['name']
      | null;
    /** 
  Type of aggregation. Client must specify matching aggregation field below.  
  */
    type?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['type'];
    /** 
  Path to the field to aggregate by in dot notation. For example `name` or `paymentOptions.online`. 
  @maxLength: 200 
  */
    fieldPath?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['fieldPath'];
    /** 
  Deprecated, use `nested` instead. 
  @deprecated: Deprecated, use `nested` instead.,
  @replacedBy: kind.nested,
  @targetRemovalDate: 2025-01-01 
  */
    groupBy?: NonNullable<
      CommonSearchWithEntityContext['aggregations']
    >[number]['groupBy'];
  }[];
  /** 
  Free text to match in searchable fields.  
  */
  search?: {
    /** 
  Search mode. Defines the search logic for combining multiple terms in the `expression`.  
  */
    mode?: NonNullable<CommonSearchWithEntityContext['search']>['mode'];
    /** 
  Search term or expression. 
  @maxLength: 200 
  */
    expression?:
      | NonNullable<CommonSearchWithEntityContext['search']>['expression']
      | null;
    /** 
  Fields to search in. If the array is empty, all searchable fields are searched. Use dot notation to specify a JSON path. For example, `order.address.streetName`. 
  @maxSize: 10,
  @maxLength: 200 
  */
    fields?: NonNullable<CommonSearchWithEntityContext['search']>['fields'];
    /** 
  Whether to enable the search function to use an algorithm to automatically find results that are close to the search expression, such as typos and declensions.  
  */
    fuzzy?: NonNullable<CommonSearchWithEntityContext['search']>['fuzzy'];
  };
  /** 
  Time zone in [IANA tz database format](https://en.wikipedia.org/wiki/Tz_database) or [ISO 8601 UTC offset format](https://en.wikipedia.org/wiki/ISO_8601#Time_offsets_from_UTC) for adjusting time fields in the specified filters and returned aggregation data. For example, `America/New_York`, `UTC`, or `+02:00`.

  Default: Time zone specified in the business [site properties](https://dev.wix.com/docs/rest/business-management/site-properties/properties/get-site-properties). 
  @maxLength: 50 
  */
  timeZone?: CommonSearchWithEntityContext['timeZone'] | null;
};

/**
 * Retrieves a list of up to 100 [booking policies](https://dev.wix.com/docs/api-reference/business-solutions/bookings/policies/booking-policies/introduction),
 * and information about the services that are connected to them,
 * given the provided filtering, paging, and sorting.
 *
 * ### Defaults
 *
 * Query Policies has the following default settings, which you can override:
 *
 * + Sorted by `id` in ascending order.
 * + `cursorPaging.limit` set to `100`.
 *
 * ### Filters
 *
 * When using date filters, you must use
 * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * ### Returned services
 *
 * If a booking policy is connected to more than 5 services, only a subset of
 * those services is returned. The `bookingPolicies.totalServiceCount` values
 * indicate the total number of services linked to each policy. You can call [Search Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/search-services)
 * and specify the relevant policy ID in the filter to retrieve all services
 * connected to the booking policy.
 *
 * ### See also
 *
 * To learn about working with Query methods, see
 * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
 * and [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
 * @param query - Information about filters, paging, and sorting.
 * @public
 * @requiredField query
 * @permissionId BOOKINGS.BOOKING_POLICY_READ
 * @permissionId BOOKINGS.SERVICES_READ
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.QueryPolicies
 */
export async function queryPolicies(
  query: CursorQuery
): Promise<
  NonNullablePaths<
    QueryPoliciesResponse,
    | `bookingPolicies`
    | `bookingPolicies.${number}.bookingPolicy._id`
    | `bookingPolicies.${number}.bookingPolicy.customPolicyDescription.enabled`
    | `bookingPolicies.${number}.bookingPolicy.customPolicyDescription.description`
    | `bookingPolicies.${number}.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `bookingPolicies.${number}.bookingPolicy.limitLateBookingPolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `bookingPolicies.${number}.bookingPolicy.bookAfterStartPolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.cancellationPolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `bookingPolicies.${number}.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `bookingPolicies.${number}.bookingPolicy.reschedulePolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `bookingPolicies.${number}.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `bookingPolicies.${number}.bookingPolicy.waitlistPolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.waitlistPolicy.capacity`
    | `bookingPolicies.${number}.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `bookingPolicies.${number}.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `bookingPolicies.${number}.bookingPolicy.resourcesPolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `bookingPolicies.${number}.bookingPolicy.cancellationFeePolicy.enabled`
    | `bookingPolicies.${number}.bookingPolicy.saveCreditCardPolicy.enabled`
    | `bookingPolicies.${number}.totalServiceCount`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({ query: query });

  const reqOpts = ambassadorWixBookingsServicesV2Service.queryPolicies(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'bookingPolicies.services.media.items.image' },
            { path: 'bookingPolicies.services.media.mainMedia.image' },
            { path: 'bookingPolicies.services.media.coverMedia.image' },
            { path: 'bookingPolicies.services.staffMembers.mainMedia.image' },
            {
              path: 'bookingPolicies.services.staffMemberDetails.staffMembers.mainMedia.image',
            },
            { path: 'bookingPolicies.connectedServices.media.items.image' },
            { path: 'bookingPolicies.connectedServices.media.mainMedia.image' },
            {
              path: 'bookingPolicies.connectedServices.media.coverMedia.image',
            },
            {
              path: 'bookingPolicies.connectedServices.staffMembers.mainMedia.image',
            },
            {
              path: 'bookingPolicies.connectedServices.staffMemberDetails.staffMembers.mainMedia.image',
            },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'bookingPolicies.services.locations.calculatedAddress' },
            { path: 'bookingPolicies.services.locations.business.address' },
            { path: 'bookingPolicies.services.locations.custom.address' },
            {
              path: 'bookingPolicies.connectedServices.locations.calculatedAddress',
            },
            {
              path: 'bookingPolicies.connectedServices.locations.business.address',
            },
            {
              path: 'bookingPolicies.connectedServices.locations.custom.address',
            },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'bookingPolicies.services.urls.servicePage' },
            { path: 'bookingPolicies.services.urls.bookingPage' },
            { path: 'bookingPolicies.services.urls.calendarPage' },
            { path: 'bookingPolicies.connectedServices.urls.servicePage' },
            { path: 'bookingPolicies.connectedServices.urls.bookingPage' },
            { path: 'bookingPolicies.connectedServices.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { query: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['query']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Retrieves a list of up to 100 booking [forms](https://dev.wix.com/docs/api-reference/crm/forms/form-schemas/form-object),
 * and information about the services that are connected to them,
 * given the provided filtering, paging, and sorting.
 *
 *
 * ### Defaults
 *
 * Query Booking Forms has the following default settings, which you can override:
 *
 * + Sorted by `id` in ascending order.
 * + `cursorPaging.limit` set to `100`.
 *
 * ### Filters
 *
 * When using date filters, you must use
 * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * ### Returned services
 *
 * If a booking policy is connected to more than 5 services, only a subset of
 * these service IDs and names is returned. The `bookingForms.totalServiceCount`
 * values indicate the total number of services linked to each form. You can call [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services)
 * and specify the relevant form ID in the filter to retrieve all services
 * connected to the booking form.
 *
 * ### Default booking forms
 *
 * By default, all Wix Bookings services use a standard booking form. To retrieve
 * a site's default booking form with Query Booking Forms, specify
 * `{"conditionalFields": ["DEFAULT_BOOKING_FORM"]}`.
 *
 * ### See also
 *
 * To learn about working with Query methods, see
 * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
 * and [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
 * @param query - Information about filters, paging, and sorting. See the article about
 * form filters ([REST](https://dev.wix.com/docs/rest/crm/forms/form-schemas/form-object))
 * for all supported filters and sorting options.
 * @public
 * @requiredField query
 * @permissionId BOOKINGS.SERVICE_BOOKING_FORMS_READ
 * @permissionId BOOKINGS.SERVICES_READ
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.QueryBookingForms
 */
export async function queryBookingForms(
  query: CursorQuery,
  options?: QueryBookingFormsOptions
): Promise<
  NonNullablePaths<
    QueryBookingFormsResponse,
    | `bookingForms`
    | `bookingForms.${number}.formDetails.formId`
    | `bookingForms.${number}.totalServiceCount`
    | `defaultBookingForm.formDetails.formId`
    | `defaultBookingForm.connectedServices`
    | `defaultBookingForm.totalServiceCount`,
    5
  > & {
    __applicationErrorsType?: QueryBookingFormsApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    query: query,
    conditionalFields: options?.conditionalFields,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.queryBookingForms(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          query: '$[0]',
          conditionalFields: '$[1].conditionalFields',
        },
        singleArgumentUnchanged: false,
      },
      ['query', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryBookingFormsOptions {
  /**
   * Conditional fields to return.
   * @maxSize 5
   */
  conditionalFields?: RequestedFieldsWithLiterals[];
}

/**
 * Counts how many services match the given filter.
 *
 *
 * See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters.
 *
 * To learn about working with filters in general, see
 * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language#filters)
 * @public
 * @permissionId BOOKINGS.SERVICES_COUNT
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.CountServices
 */
export async function countServices(
  options?: CountServicesOptions
): Promise<NonNullablePaths<CountServicesResponse, `count`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts = ambassadorWixBookingsServicesV2Service.countServices(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CountServicesOptions {
  /** Query filter to base the count on. See [Query Services](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/query-services) for a list of supported filters. */
  filter?: Record<string, any> | null;
}

/**
 * Retrieves 3 separate lists of business, custom, and customer [locations](https://dev.wix.com/docs/api-reference/business-management/locations/introduction),
 * given the provided filtering, and whether each location is connected to at
 * least one of the site's services.
 *
 *
 * ### Defaults
 *
 * Query Locations has the following default setting, which you can't override:
 * Sorted by `id` in ascending order.
 *
 * ### Filters
 *
 * For a complete list of supported filters, refer to the `location` object
 * ([REST](https://dev.wix.com/docs/rest/business-management/locations/location-object)).
 *
 * When using date filters, you must use
 * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * ### See also
 *
 * To learn about working with Query methods, see
 * [API Query Language](https://dev.wix.com/docs/rest/articles/getting-started/api-query-language)
 * and [Sorting and Paging](https://dev.wix.com/docs/rest/articles/getting-started/sorting-and-paging).
 * @public
 * @permissionId BOOKINGS.SERVICES_READ
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.QueryLocations
 */
export async function queryLocations(
  options?: QueryLocationsOptions
): Promise<
  NonNullablePaths<
    QueryLocationsResponse,
    | `businessLocations.exists`
    | `businessLocations.locations`
    | `businessLocations.locations.${number}.business._id`
    | `businessLocations.locations.${number}.business.name`
    | `businessLocations.locations.${number}.custom._id`
    | `businessLocations.locations.${number}._id`
    | `businessLocations.locations.${number}.type`
    | `customLocations.exists`
    | `customerLocations.exists`,
    6
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.queryLocations(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'businessLocations.locations.calculatedAddress' },
            { path: 'businessLocations.locations.business.address' },
            { path: 'businessLocations.locations.custom.address' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryLocationsOptions {
  /** Filter. */
  filter?: QueryLocationsFilter;
}

/**
 * Retrieves a list of [service categories](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/categories-v2/introduction), given the provided filtering.
 *
 *
 * ### Defaults
 *
 * Query Categories has the following default setting, which you can't override:
 * Sorted by `id` in ascending order.
 *
 * ### Filters
 *
 * When using date filters, you must use
 * [UTC time](https://en.wikipedia.org/wiki/Coordinated_Universal_Time).
 *
 * ### See also
 *
 * To learn about working with Query methods, see
 * [API Query Language](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-the-wix-api-query-language)
 * and [Sorting and Paging](https://dev.wix.com/docs/api-reference/articles/work-with-wix-apis/data-retrieval/about-sorting-and-paging).
 * @public
 * @permissionId BOOKINGS.SERVICES_READ
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.QueryCategories
 */
export async function queryCategories(
  options?: QueryCategoriesOptions
): Promise<
  NonNullablePaths<
    QueryCategoriesResponse,
    `categories` | `categories.${number}._id`,
    4
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    filter: options?.filter,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.queryCategories(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { filter: '$[0].filter' },
        singleArgumentUnchanged: false,
      },
      ['options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface QueryCategoriesOptions {
  /** Filter. */
  filter?: QueryCategoriesFilter;
}

/**
 * Replaces the list of the [locations](https://dev.wix.com/docs/api-reference/business-management/locations/introduction) where the business offers the service.
 *
 *
 * ### Consequences for customers
 *
 * Removing a service location may impact existing sessions and their
 * participants. If you remove at least one service location, you must
 * specify `removedLocationSessionsAction` to indicate what happens to all
 * future sessions scheduled at this location.
 *
 * - **Keep existing location**: If you want to retain future sessions at their
 * originally scheduled location, specify
 * `{"removedLocationSessionsAction.action": "KEEP_AT_CURRENT_LOCATION"}`.
 * This ensures nothing changes for the customer, but the business must be
 * able to provide access to the removed location in the future.
 * - **Update location**: If you want to update the location for future sessions
 * scheduled at the removed location, specify
 * `{"removedLocationSessionsAction.action": "MOVE_TO_LOCATION"}` and
 * `moveToLocationOptions.newLocation`.
 *
 * You can't mix and match to keep some sessions at the previous location while
 * moving other sessions to an updated location.
 *
 * Past session details aren't changed, no matter which option you choose for
 * future sessions.
 *
 * Future sessions scheduled for a location defined by the customer are also not
 * updated.
 *
 * ### Specify location details
 *
 * Depending on whether the new or updated location is a business or custom location,
 * you need to specify different fields.
 *
 * - **Business location**: Specify the relevant [location ID](https://dev.wix.com/docs/api-reference/business-management/locations/introduction) in `locations.business.id`.
 * - **Custom location**: Specify the complete address object as
 * `locations.custom.address`.
 *
 * ### Participant notifications
 *
 * You can specify a `participantNotification.message` that's immediately send
 * to all customers who had booked at a changed location. Ensure
 * `participantNotification.notifyParticipants` is set to `true` to send the
 * message.
 * @param serviceId - ID of the service.
 * @public
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_LOCATIONS_SET
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.SetServiceLocations
 */
export async function setServiceLocations(
  serviceId: string,
  options?: SetServiceLocationsOptions
): Promise<
  NonNullablePaths<
    SetServiceLocationsResponse,
    | `service.type`
    | `service.media.items`
    | `service.category._id`
    | `service.form._id`
    | `service.payment.fixed.price.value`
    | `service.payment.fixed.price.currency`
    | `service.payment.rateType`
    | `service.payment.pricingPlanIds`
    | `service.payment.addOnOption`
    | `service.locations`
    | `service.locations.${number}.business._id`
    | `service.locations.${number}.business.name`
    | `service.locations.${number}.custom._id`
    | `service.locations.${number}._id`
    | `service.locations.${number}.type`
    | `service.bookingPolicy._id`
    | `service.bookingPolicy.customPolicyDescription.enabled`
    | `service.bookingPolicy.customPolicyDescription.description`
    | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `service.bookingPolicy.limitLateBookingPolicy.enabled`
    | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `service.bookingPolicy.bookAfterStartPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `service.bookingPolicy.reschedulePolicy.enabled`
    | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `service.bookingPolicy.waitlistPolicy.enabled`
    | `service.bookingPolicy.waitlistPolicy.capacity`
    | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `service.bookingPolicy.resourcesPolicy.enabled`
    | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `service.bookingPolicy.cancellationFeePolicy.enabled`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `service.bookingPolicy.saveCreditCardPolicy.enabled`
    | `service.schedule.availabilityConstraints.durations`
    | `service.schedule.availabilityConstraints.durations.${number}.minutes`
    | `service.schedule.availabilityConstraints.sessionDurations`
    | `service.schedule.availabilityConstraints.timeBetweenSessions`
    | `service.staffMemberIds`
    | `service.serviceResources`
    | `service.supportedSlugs`
    | `service.supportedSlugs.${number}.name`
    | `service.seoData.tags`
    | `service.seoData.tags.${number}.type`
    | `service.seoData.tags.${number}.children`
    | `service.seoData.tags.${number}.custom`
    | `service.seoData.tags.${number}.disabled`
    | `service.seoData.settings.preventAutoRedirect`
    | `service.seoData.settings.keywords`
    | `service.seoData.settings.keywords.${number}.term`
    | `service.seoData.settings.keywords.${number}.isMain`
    | `service.addOnGroups`
    | `service.addOnDetails`
    | `service.taxableAddress.taxableAddressType`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = transformPaths(
    renameKeysFromSDKRequestToRESTRequest({
      serviceId: serviceId,
      locations: options?.locations,
      removedLocationSessionsAction: options?.removedLocationSessionsAction,
      participantNotification: options?.participantNotification,
    }),
    [
      {
        transformFn: transformSDKAddressToRESTAddress,
        paths: [
          { path: 'locations.calculatedAddress' },
          { path: 'locations.business.address' },
          { path: 'locations.custom.address' },
          {
            path: 'removedLocationSessionsAction.moveToLocationOptions.newLocation.calculatedAddress',
          },
          {
            path: 'removedLocationSessionsAction.moveToLocationOptions.newLocation.business.address',
          },
          {
            path: 'removedLocationSessionsAction.moveToLocationOptions.newLocation.custom.address',
          },
        ],
      },
    ]
  );

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.setServiceLocations(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          serviceId: '$[0]',
          locations: '$[1].locations',
          removedLocationSessionsAction: '$[1].removedLocationSessionsAction',
          participantNotification: '$[1].participantNotification',
        },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SetServiceLocationsOptions {
  /**
   * List of locations replacing existing service locations. Omitting an existing location removes it from the service. Specify `removedLocationSessionsAction` to determine the handling of future sessions scheduled at that location.
   * @maxSize 100
   */
  locations?: V2Location[];
  /**
   * The action to perform on sessions currently set to a removed location. For
   * example, move existing sessions to a new specified location.
   * If not specified, sessions will not be moved to a new location.
   */
  removedLocationSessionsAction?: RemovedLocationSessionsAction;
  /**
   * Whether to notify participants about the change of location, and an
   * Optional custom message. The notification is sent only to participants of sessions that are affected by the change.
   */
  participantNotification?: V2ParticipantNotification;
}

/**
 * Adds a list of [pricing plan IDs](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/plans/plans-v3/introduction) to a service's `payment.pricingPlanIds` array.
 *
 *
 * The call doesn't validate whether the service's `payment.options.pricingPlan`
 * is set to `true`. If it's set to `false`, customers aren't able to pay
 * for their bookings using pricing plans. You can call [Update Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-service) to change a service's supported payment methods.
 * @param serviceId - ID of the service to update.
 * @param pricingPlanIds - IDs of the [pricing plans](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/plans/introduction)
 * to add to the service's `payment.pricingPlanIds` array.
 * Maintained for backward compatibility. Use `pricing_plan_selections` for explicit benefit selection.
 * @public
 * @requiredField pricingPlanIds
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_PRICING_PLANS_ADD
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.EnablePricingPlansForService
 */
export async function enablePricingPlansForService(
  serviceId: string,
  pricingPlanIds: string[]
): Promise<
  NonNullablePaths<
    EnablePricingPlansForServiceResponse,
    | `service.type`
    | `service.media.items`
    | `service.category._id`
    | `service.form._id`
    | `service.payment.fixed.price.value`
    | `service.payment.fixed.price.currency`
    | `service.payment.rateType`
    | `service.payment.pricingPlanIds`
    | `service.payment.addOnOption`
    | `service.locations`
    | `service.locations.${number}.business._id`
    | `service.locations.${number}.business.name`
    | `service.locations.${number}.custom._id`
    | `service.locations.${number}._id`
    | `service.locations.${number}.type`
    | `service.bookingPolicy._id`
    | `service.bookingPolicy.customPolicyDescription.enabled`
    | `service.bookingPolicy.customPolicyDescription.description`
    | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `service.bookingPolicy.limitLateBookingPolicy.enabled`
    | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `service.bookingPolicy.bookAfterStartPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `service.bookingPolicy.reschedulePolicy.enabled`
    | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `service.bookingPolicy.waitlistPolicy.enabled`
    | `service.bookingPolicy.waitlistPolicy.capacity`
    | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `service.bookingPolicy.resourcesPolicy.enabled`
    | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `service.bookingPolicy.cancellationFeePolicy.enabled`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `service.bookingPolicy.saveCreditCardPolicy.enabled`
    | `service.schedule.availabilityConstraints.durations`
    | `service.schedule.availabilityConstraints.durations.${number}.minutes`
    | `service.schedule.availabilityConstraints.sessionDurations`
    | `service.schedule.availabilityConstraints.timeBetweenSessions`
    | `service.staffMemberIds`
    | `service.serviceResources`
    | `service.supportedSlugs`
    | `service.supportedSlugs.${number}.name`
    | `service.seoData.tags`
    | `service.seoData.tags.${number}.type`
    | `service.seoData.tags.${number}.children`
    | `service.seoData.tags.${number}.custom`
    | `service.seoData.tags.${number}.disabled`
    | `service.seoData.settings.preventAutoRedirect`
    | `service.seoData.settings.keywords`
    | `service.seoData.settings.keywords.${number}.term`
    | `service.seoData.settings.keywords.${number}.isMain`
    | `service.addOnGroups`
    | `service.addOnDetails`
    | `service.taxableAddress.taxableAddressType`,
    7
  > & {
    __applicationErrorsType?: EnablePricingPlansForServiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
    pricingPlanIds: pricingPlanIds,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.enablePricingPlansForService(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { serviceId: '$[0]', pricingPlanIds: '$[1]' },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'pricingPlanIds']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Removes a list of [pricing plan IDs](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/plans/introduction) from a service's `payment.pricingPlanIds` array.
 *
 *
 * If you remove all pricing plan IDs from `payment.pricingPlanIds` and the
 * service supports only payments by pricing plan, customers will no longer be
 * able to book the service, as they will have no payment options available.
 * @param serviceId - ID of the service to update.
 * @public
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_PRICING_PLANS_REMOVE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.DisablePricingPlansForService
 */
export async function disablePricingPlansForService(
  serviceId: string,
  options?: DisablePricingPlansForServiceOptions
): Promise<
  NonNullablePaths<
    DisablePricingPlansForServiceResponse,
    | `service.type`
    | `service.media.items`
    | `service.category._id`
    | `service.form._id`
    | `service.payment.fixed.price.value`
    | `service.payment.fixed.price.currency`
    | `service.payment.rateType`
    | `service.payment.pricingPlanIds`
    | `service.payment.addOnOption`
    | `service.locations`
    | `service.locations.${number}.business._id`
    | `service.locations.${number}.business.name`
    | `service.locations.${number}.custom._id`
    | `service.locations.${number}._id`
    | `service.locations.${number}.type`
    | `service.bookingPolicy._id`
    | `service.bookingPolicy.customPolicyDescription.enabled`
    | `service.bookingPolicy.customPolicyDescription.description`
    | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `service.bookingPolicy.limitLateBookingPolicy.enabled`
    | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `service.bookingPolicy.bookAfterStartPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `service.bookingPolicy.reschedulePolicy.enabled`
    | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `service.bookingPolicy.waitlistPolicy.enabled`
    | `service.bookingPolicy.waitlistPolicy.capacity`
    | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `service.bookingPolicy.resourcesPolicy.enabled`
    | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `service.bookingPolicy.cancellationFeePolicy.enabled`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `service.bookingPolicy.saveCreditCardPolicy.enabled`
    | `service.schedule.availabilityConstraints.durations`
    | `service.schedule.availabilityConstraints.durations.${number}.minutes`
    | `service.schedule.availabilityConstraints.sessionDurations`
    | `service.schedule.availabilityConstraints.timeBetweenSessions`
    | `service.staffMemberIds`
    | `service.serviceResources`
    | `service.supportedSlugs`
    | `service.supportedSlugs.${number}.name`
    | `service.seoData.tags`
    | `service.seoData.tags.${number}.type`
    | `service.seoData.tags.${number}.children`
    | `service.seoData.tags.${number}.custom`
    | `service.seoData.tags.${number}.disabled`
    | `service.seoData.settings.preventAutoRedirect`
    | `service.seoData.settings.keywords`
    | `service.seoData.settings.keywords.${number}.term`
    | `service.seoData.settings.keywords.${number}.isMain`
    | `service.addOnGroups`
    | `service.addOnDetails`
    | `service.taxableAddress.taxableAddressType`,
    7
  > & {
    __applicationErrorsType?: DisablePricingPlansForServiceApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
    pricingPlanIds: options?.pricingPlanIds,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.disablePricingPlansForService(
      payload
    );

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          serviceId: '$[0]',
          pricingPlanIds: '$[1].pricingPlanIds',
        },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DisablePricingPlansForServiceOptions {
  /**
   * IDs of the [pricing plans](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/pricing-plans/plans/introduction)
   * to remove from the service's `payment.pricingPlanIds` array.
   * Maintained for backward compatibility. Use `pricing_plan_selections` for explicit benefit selection.
   * @format GUID
   * @maxSize 75
   */
  pricingPlanIds?: string[];
}

/**
 * Sets a new active slug for the service.
 *
 *
 * The call fails if at least one of these conditions is met:
 * - The slug doesn't adheres to the supported format.
 * - Another service is currently using the slug.
 * - Another service has used the slug in the past.
 * @param serviceId - ID of the service for which to update the active slug.
 * @public
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_CUSTOM_SLUGS_SET
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.SetCustomSlug
 */
export async function setCustomSlug(
  serviceId: string,
  options?: SetCustomSlugOptions
): Promise<
  NonNullablePaths<
    SetCustomSlugResponse,
    | `slug.name`
    | `service.type`
    | `service.media.items`
    | `service.category._id`
    | `service.form._id`
    | `service.payment.fixed.price.value`
    | `service.payment.fixed.price.currency`
    | `service.payment.rateType`
    | `service.payment.pricingPlanIds`
    | `service.payment.addOnOption`
    | `service.locations`
    | `service.locations.${number}.business._id`
    | `service.locations.${number}.business.name`
    | `service.locations.${number}.custom._id`
    | `service.locations.${number}._id`
    | `service.locations.${number}.type`
    | `service.bookingPolicy._id`
    | `service.bookingPolicy.customPolicyDescription.enabled`
    | `service.bookingPolicy.customPolicyDescription.description`
    | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `service.bookingPolicy.limitLateBookingPolicy.enabled`
    | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `service.bookingPolicy.bookAfterStartPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `service.bookingPolicy.reschedulePolicy.enabled`
    | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `service.bookingPolicy.waitlistPolicy.enabled`
    | `service.bookingPolicy.waitlistPolicy.capacity`
    | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `service.bookingPolicy.resourcesPolicy.enabled`
    | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `service.bookingPolicy.cancellationFeePolicy.enabled`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `service.bookingPolicy.saveCreditCardPolicy.enabled`
    | `service.schedule.availabilityConstraints.durations`
    | `service.schedule.availabilityConstraints.durations.${number}.minutes`
    | `service.schedule.availabilityConstraints.sessionDurations`
    | `service.schedule.availabilityConstraints.timeBetweenSessions`
    | `service.staffMemberIds`
    | `service.serviceResources`
    | `service.supportedSlugs`
    | `service.supportedSlugs.${number}.name`
    | `service.seoData.tags`
    | `service.seoData.tags.${number}.type`
    | `service.seoData.tags.${number}.children`
    | `service.seoData.tags.${number}.custom`
    | `service.seoData.tags.${number}.disabled`
    | `service.seoData.settings.preventAutoRedirect`
    | `service.seoData.settings.keywords`
    | `service.seoData.settings.keywords.${number}.term`
    | `service.seoData.settings.keywords.${number}.isMain`
    | `service.addOnGroups`
    | `service.addOnDetails`
    | `service.taxableAddress.taxableAddressType`,
    7
  > & {
    __applicationErrorsType?: SetCustomSlugApplicationErrors;
    __validationErrorsType?: SetCustomSlugValidationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
    slug: options?.slug,
  });

  const reqOpts = ambassadorWixBookingsServicesV2Service.setCustomSlug(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { serviceId: '$[0]', slug: '$[1].slug' },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SetCustomSlugOptions {
  /**
   * Slug to set as the active service slug.
   * @maxLength 500
   */
  slug?: string;
}

/**
 * Checks whether a custom slug is validate for the service.
 *
 *
 * The checks include:
 * - The slug adheres to the supported format.
 * - No other service is currently using the slug.
 * - No other service has used the slug in the past.
 *
 * The call fails if at least one of the checks fails.
 * @param serviceId - IO of the service to check custom slug validity for.
 * @public
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_CUSTOM_SLUGS_SET
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.ValidateSlug
 */
export async function validateSlug(
  serviceId: string,
  options?: ValidateSlugOptions
): Promise<NonNullablePaths<ValidateSlugResponse, `valid` | `errors`, 2>> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
    slug: options?.slug,
  });

  const reqOpts = ambassadorWixBookingsServicesV2Service.validateSlug(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { serviceId: '$[0]', slug: '$[1].slug' },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ValidateSlugOptions {
  /**
   * Custom slug to validate.
   * @maxLength 500
   */
  slug?: string;
}

/**
 * Clones a service.
 *
 *
 * ### Connected entities
 *
 * By default, not all entities connected to the service are cloned.
 *
 * #### Schedule
 *
 * Wix Bookings automatically creates a new active [schedule](https://dev.wix.com/docs/api-reference/business-management/calendar/schedules-v3/introduction)
 * for the cloned service. If Wix Bookings can't create this schedule, the
 * Clone Service call fails.
 *
 * - __For appointment-based services__: Future appointments aren't added to the
 * cloned service's schedule. Use the [Events API](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction) to add them as needed.
 * - __For classes and courses__: Future one-time events aren't added to the
 * cloned service's schedule, while future recurring events are added
 * asynchronously. The Clone Service call always succeeds, whether or not
 * recurring events are added.
 * If the response includes `RECURRING_EVENTS` in the `errors` array, it means the
 * cloned service doesn't have future recurring events, even though the original
 * service does. You can either delete the cloned service and try again or use
 * the [Events API](https://dev.wix.com/docs/api-reference/business-management/calendar/events-v3/introduction) to add missing events to the schedule.
 *
 * Wix Bookings never adds past events to the cloned service's schedule.
 *
 * #### Service options and variants
 *
 * If the original service has [variants](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction),
 * they're cloned asynchronously. The Clone Service call always succeeds,
 * regardless of whether variants were cloned.
 *
 * If the response includes `OPTIONS_AND_VARIANTS` in the `errors` array, the cloned
 * service doesn't have variants, even though the original service does. You can
 * delete the cloned service and call Clone Service again, or use the
 * [Service Options And Variants API](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/service-options-and-variants/introduction) to add variants.
 *
 * #### Booking form
 *
 * The original service's booking form isn't cloned, whether it's
 * the default or a custom booking form.
 *
 * #### Pricing plans
 *
 * If the original service's accepts payments via [pricing plans](https://dev.wix.com/docs/api-reference/business-solutions/pricing-plans/introduction/),
 * the cloned service's `payment.options.pricingPlan` is also set to `true`. To
 * accept specific pricing plans, call [Enable Pricing Plans For Service](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/enable-pricing-plans-for-service),
 * after cloning the service. If the original service accepts only
 * pricing plan payments and you don't call Enable Pricing Plans For Service
 * after cloning the service, customers will be unable to book the service.
 * @param sourceServiceId - ID of the service to clone.
 * @public
 * @requiredField sourceServiceId
 * @permissionId BOOKINGS.SERVICES_CREATE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.ServicesService.CloneService
 */
export async function cloneService(
  sourceServiceId: string
): Promise<
  NonNullablePaths<
    CloneServiceResponse,
    | `service.type`
    | `service.media.items`
    | `service.category._id`
    | `service.form._id`
    | `service.payment.fixed.price.value`
    | `service.payment.fixed.price.currency`
    | `service.payment.rateType`
    | `service.payment.pricingPlanIds`
    | `service.payment.addOnOption`
    | `service.locations`
    | `service.locations.${number}.business._id`
    | `service.locations.${number}.business.name`
    | `service.locations.${number}.custom._id`
    | `service.locations.${number}._id`
    | `service.locations.${number}.type`
    | `service.bookingPolicy._id`
    | `service.bookingPolicy.customPolicyDescription.enabled`
    | `service.bookingPolicy.customPolicyDescription.description`
    | `service.bookingPolicy.limitEarlyBookingPolicy.enabled`
    | `service.bookingPolicy.limitEarlyBookingPolicy.earliestBookingInMinutes`
    | `service.bookingPolicy.limitLateBookingPolicy.enabled`
    | `service.bookingPolicy.limitLateBookingPolicy.latestBookingInMinutes`
    | `service.bookingPolicy.bookAfterStartPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.enabled`
    | `service.bookingPolicy.cancellationPolicy.limitLatestCancellation`
    | `service.bookingPolicy.cancellationPolicy.latestCancellationInMinutes`
    | `service.bookingPolicy.reschedulePolicy.enabled`
    | `service.bookingPolicy.reschedulePolicy.limitLatestReschedule`
    | `service.bookingPolicy.reschedulePolicy.latestRescheduleInMinutes`
    | `service.bookingPolicy.waitlistPolicy.enabled`
    | `service.bookingPolicy.waitlistPolicy.capacity`
    | `service.bookingPolicy.waitlistPolicy.reservationTimeInMinutes`
    | `service.bookingPolicy.participantsPolicy.maxParticipantsPerBooking`
    | `service.bookingPolicy.resourcesPolicy.enabled`
    | `service.bookingPolicy.resourcesPolicy.autoAssignAllowed`
    | `service.bookingPolicy.cancellationFeePolicy.enabled`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows`
    | `service.bookingPolicy.cancellationFeePolicy.cancellationWindows.${number}.percentage`
    | `service.bookingPolicy.saveCreditCardPolicy.enabled`
    | `service.schedule.availabilityConstraints.durations`
    | `service.schedule.availabilityConstraints.durations.${number}.minutes`
    | `service.schedule.availabilityConstraints.sessionDurations`
    | `service.schedule.availabilityConstraints.timeBetweenSessions`
    | `service.staffMemberIds`
    | `service.serviceResources`
    | `service.supportedSlugs`
    | `service.supportedSlugs.${number}.name`
    | `service.seoData.tags`
    | `service.seoData.tags.${number}.type`
    | `service.seoData.tags.${number}.children`
    | `service.seoData.tags.${number}.custom`
    | `service.seoData.tags.${number}.disabled`
    | `service.seoData.settings.preventAutoRedirect`
    | `service.seoData.settings.keywords`
    | `service.seoData.settings.keywords.${number}.term`
    | `service.seoData.settings.keywords.${number}.isMain`
    | `service.addOnGroups`
    | `service.addOnDetails`
    | `service.taxableAddress.taxableAddressType`
    | `errors`,
    7
  >
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[1] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    sourceServiceId: sourceServiceId,
  });

  const reqOpts = ambassadorWixBookingsServicesV2Service.cloneService(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(
      transformPaths(result.data, [
        {
          transformFn: transformRESTImageToSDKImage,
          paths: [
            { path: 'service.media.items.image' },
            { path: 'service.media.mainMedia.image' },
            { path: 'service.media.coverMedia.image' },
            { path: 'service.staffMembers.mainMedia.image' },
            { path: 'service.staffMemberDetails.staffMembers.mainMedia.image' },
          ],
        },
        {
          transformFn: transformRESTAddressToSDKAddress,
          paths: [
            { path: 'service.locations.calculatedAddress' },
            { path: 'service.locations.business.address' },
            { path: 'service.locations.custom.address' },
          ],
        },
        {
          transformFn: transformRESTPageURLV2ToSDKPageURLV2,
          paths: [
            { path: 'service.urls.servicePage' },
            { path: 'service.urls.bookingPage' },
            { path: 'service.urls.calendarPage' },
          ],
        },
      ])
    )!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: { sourceServiceId: '$[0]' },
        singleArgumentUnchanged: false,
      },
      ['sourceServiceId']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

/**
 * Creates a new add-on group for a service and updates the service's `addOnGroups` array.
 * @param addOnGroup - Add-on group to create.
 * @public
 * @documentationMaturity preview
 * @requiredField addOnGroup
 * @requiredField addOnGroup.name
 * @requiredField options
 * @requiredField options.serviceId
 * @permissionId BOOKINGS.SERVICES_ADD_ON_GROUP_CREATE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.AddOnGroupsService.CreateAddOnGroup
 */
export async function createAddOnGroup(
  addOnGroup: NonNullablePaths<AddOnGroup, `name`, 2>,
  options: NonNullablePaths<CreateAddOnGroupOptions, `serviceId`, 2>
): Promise<
  NonNullablePaths<CreateAddOnGroupResponse, `addOnGroup.addOnIds`, 3>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    addOnGroup: addOnGroup,
    serviceId: options?.serviceId,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.createAddOnGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          addOnGroup: '$[0]',
          serviceId: '$[1].serviceId',
        },
        singleArgumentUnchanged: false,
      },
      ['addOnGroup', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface CreateAddOnGroupOptions {
  /**
   * ID of the service to create the add-on group for.
   * @format GUID
   */
  serviceId: string | null;
}

/**
 * Deletes an add-on group.
 *
 *
 * Deleting the group doesn't delete individual add-ons.
 * Any add-on not associated with another group isn't available for customers to book.
 * @param addOnGroupId - ID of the add-on group to delete.
 * @public
 * @documentationMaturity preview
 * @requiredField addOnGroupId
 * @requiredField options
 * @requiredField options.serviceId
 * @permissionId BOOKINGS.SERVICES_ADD_ON_GROUP_DELETE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.AddOnGroupsService.DeleteAddOnGroup
 */
export async function deleteAddOnGroup(
  addOnGroupId: string,
  options: NonNullablePaths<DeleteAddOnGroupOptions, `serviceId`, 2>
): Promise<
  void & {
    __applicationErrorsType?: DeleteAddOnGroupApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    addOnGroupId: addOnGroupId,
    serviceId: options?.serviceId,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.deleteAddOnGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          addOnGroupId: '$[0]',
          serviceId: '$[1].serviceId',
        },
        singleArgumentUnchanged: false,
      },
      ['addOnGroupId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface DeleteAddOnGroupOptions {
  /**
   * ID of the service from which to delete the add-on group.
   * @format GUID
   */
  serviceId: string | null;
}

/**
 * Updates an add-on group.
 *
 *
 * To update only the display order for add-ons, call [Set Add Ons For Group](https://dev.wix.com/docs/rest/business-solutions/bookings/services/services-v2/set-add-ons-for-group) instead.
 *
 * You can't change which service an add-on group belongs to.
 * Each add-on group is permanently associated with the service it was created for.
 * To use the same add-ons with a different service, create a new add-on group for that service.
 * @param addOnGroup - Add-on group to update.
 * @public
 * @documentationMaturity preview
 * @requiredField addOnGroup
 * @requiredField addOnGroup._id
 * @requiredField options
 * @requiredField options.serviceId
 * @permissionId BOOKINGS.SERVICES_ADD_ON_GROUP_UPDATE
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.AddOnGroupsService.UpdateAddOnGroup
 */
export async function updateAddOnGroup(
  addOnGroup: NonNullablePaths<AddOnGroup, `_id`, 2>,
  options: NonNullablePaths<UpdateAddOnGroupOptions, `serviceId`, 2>
): Promise<
  NonNullablePaths<UpdateAddOnGroupResponse, `addOnGroup.addOnIds`, 3> & {
    __applicationErrorsType?: UpdateAddOnGroupApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    addOnGroup: addOnGroup,
    serviceId: options?.serviceId,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.updateAddOnGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          addOnGroup: '$[0]',
          serviceId: '$[1].serviceId',
        },
        singleArgumentUnchanged: false,
      },
      ['addOnGroup', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface UpdateAddOnGroupOptions {
  /**
   * ID of the service that contains the add-on group.
   * @format GUID
   */
  serviceId: string | null;
}

/**
 * Retrieves add-on groups for a service with enriched add-on details.
 *
 *
 * Add-on details are returned based on their display order.
 *
 * If you specify `groupIds`, only those groups are returned.
 * By default, all groups associated with the service are returned.
 * @param serviceId - ID of the service to retrieve add-on groups for.
 * @public
 * @documentationMaturity preview
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_ADD_ON_GROUPS_LIST
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.AddOnGroupsService.ListAddOnGroupsByServiceId
 */
export async function listAddOnGroupsByServiceId(
  serviceId: string,
  options?: ListAddOnGroupsByServiceIdOptions
): Promise<
  NonNullablePaths<ListAddOnGroupsByServiceIdResponse, `addOnGroupsDetails`, 2>
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
    groupIds: options?.groupIds,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.listAddOnGroupsByServiceId(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          serviceId: '$[0]',
          groupIds: '$[1].groupIds',
        },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface ListAddOnGroupsByServiceIdOptions {
  /**
   * List of group IDs to return. By default, all groups are returned.
   * @format GUID
   * @maxSize 3
   */
  groupIds?: string[] | null;
}

/**
 * Sets the add-ons for a specific group, replacing any existing add-ons in the group.
 *
 *
 * Call this method when you want to:
 * - Add new add-ons to a group.
 * - Remove add-ons from a group.
 * - Reorder existing add-ons for optimal customer presentation.
 *
 * The order of add-ons in the request determines their display sequence to customers.
 * All specified add-ons must already exist before adding them to a group.
 *
 * For updating other add-on group properties, for example `name`, `prompt`, or `constraints`, call [Update Add On Group](https://dev.wix.com/docs/api-reference/business-solutions/bookings/services/services-v2/update-add-on-group) instead.
 * @param serviceId - Service ID containing the group.
 * @public
 * @documentationMaturity preview
 * @requiredField options
 * @requiredField options.addOnIds
 * @requiredField options.groupId
 * @requiredField serviceId
 * @permissionId BOOKINGS.SERVICES_ADD_ON_GROUPS_SET_ADD_ONS
 * @applicableIdentity APP
 * @fqn wix.bookings.services.v2.AddOnGroupsService.SetAddOnsForGroup
 */
export async function setAddOnsForGroup(
  serviceId: string,
  options: NonNullablePaths<SetAddOnsForGroupOptions, `addOnIds` | `groupId`, 2>
): Promise<
  NonNullablePaths<SetAddOnsForGroupResponse, `addOnGroup.addOnIds`, 3> & {
    __applicationErrorsType?: SetAddOnsForGroupApplicationErrors;
  }
> {
  // @ts-ignore
  const { httpClient, sideEffects } = arguments[2] as {
    httpClient: HttpClient;
    sideEffects?: any;
  };

  const payload = renameKeysFromSDKRequestToRESTRequest({
    serviceId: serviceId,
    groupId: options?.groupId,
    addOnIds: options?.addOnIds,
  });

  const reqOpts =
    ambassadorWixBookingsServicesV2Service.setAddOnsForGroup(payload);

  sideEffects?.onSiteCall?.();
  try {
    const result = await httpClient.request(reqOpts);
    sideEffects?.onSuccess?.(result);

    return renameKeysFromRESTResponseToSDKResponse(result.data)!;
  } catch (err: any) {
    const transformedError = sdkTransformError(
      err,
      {
        spreadPathsToArguments: {},
        explicitPathsToArguments: {
          serviceId: '$[0]',
          groupId: '$[1].groupId',
          addOnIds: '$[1].addOnIds',
        },
        singleArgumentUnchanged: false,
      },
      ['serviceId', 'options']
    );
    sideEffects?.onError?.(err);

    throw transformedError;
  }
}

export interface SetAddOnsForGroupOptions {
  /**
   * Group ID to set add-ons for.
   * @format GUID
   */
  groupId: string | null;
  /**
   * IDs of add-ons to set for the group in display order.
   * @format GUID
   * @minSize 1
   * @maxSize 7
   */
  addOnIds: string[] | null;
}
